import type { Language } from '../types';

/** Native-experience strings — not word-for-word English copies. */
export const nativeExperience: Record<Language, Record<string, string>> = {
  en: {
    'nav.about': 'About',
    'nav.openPortal': 'Open portal',
    'nav.customerPortal': 'Customer portal',
    'footer.platform': 'Platform',
    'footer.contact': 'Contact',
    'footer.privateDistribution': 'Secure software distribution',
    'common.home': 'Home',
    'common.comingSoon': 'Coming Soon',
    'common.backHome': 'Back home',
    'a11y.skipToContent': 'Skip to content',
    'common.error': 'Error',
    'common.failed': 'Failed',
    'common.loading': 'Loading',
    'common.tryAgain': 'Try again',
    'common.today': 'Today',
    'common.earlier': 'Earlier',
    'common.yesterday': 'Yesterday',
    'common.daysAgo': '{count} days ago',
    'common.dateUnavailable': 'Date unavailable',
    'common.version': 'Version',
    'common.status': 'Status',
    'common.published': 'Published',
    'common.draft': 'Draft',
    'common.beta': 'Beta',
    'common.archived': 'Archived',
    'common.open': 'Open',
    'common.manage': 'Manage',
    'common.viewAll': 'View all',
    'common.fullLog': 'Full log',
    'common.confirm': 'Confirm',
    'status.checking': 'Checking',
    'status.operational': 'Working',
    'status.unavailable': 'Unavailable',
    'status.down': 'Down',
    'status.disabled': 'Disabled',
    'status.stable': 'Stable',
    'status.inProgress': 'In progress',
    'status.awaitingRelease': 'Awaiting release',

    'landing.softwareEyebrow': 'Software',
    'landing.softwareTitle': 'Software from BeCoder',
    'landing.softwareLede':
      'These are the products BeCoder offers. Open any card to review features, screenshots, and pricing.',
    'landing.catalogEmpty': 'Catalog unavailable — start the API, or sign in to see licensed software.',
    'landing.bannerLabel': 'BeCoder messages',
    'landing.banner.1.title': 'We build custom software for your business.',
    'landing.banner.1.body': 'From operations to ownership — software shaped around how you work.',
    'landing.banner.2.title': 'Connect all your branches in one system.',
    'landing.banner.2.body': 'One platform identity across locations, teams, and products.',
    'landing.banner.3.title': 'Cloud solutions for growing companies.',
    'landing.banner.3.body': 'Licensed BeCoder applications that scale with your organization.',
    'landing.banner.4.title': 'One platform for all your business operations.',
    'landing.banner.4.body': 'Explore catalog products, start a trial, and launch from your portal.',
    'landing.banner.5.title': 'Enterprise-grade solutions for organizations.',
    'landing.banner.5.body': 'Secure software delivery, deliberate licensing, and dependable operational control.',
    'landing.banner.6.title': 'Need a custom system? We build it.',
    'landing.banner.6.body': 'Tell Sales what you need — BeCoder designs and delivers the software.',
    'landing.banner.cta.sales': 'Contact Sales',
    'landing.banner.cta.custom': 'Request Custom Software',
    'landing.banner.cta.explore': 'Explore Solutions',
    'landing.banner.cta.trial': 'Start Free Trial',
    'landing.manageEyebrow': 'Solutions',
    'landing.manageTitle': 'What do you want to manage?',
    'landing.manageLede':
      'Choose your business type. If software is ready for it, open the product page. Otherwise you will see Coming Soon.',
    'landing.need.talkSales': 'Contact Sales',
    'landing.need.customPath': 'Custom engagement',
    'landing.need.retail.title': 'Mini Market / Supermarket',
    'landing.need.retail.body':
      'Point-of-sale for mini markets and supermarkets — sales, products, and daily checkout with GlassPOS.',
    'landing.need.restaurant.title': 'Restaurant',
    'landing.need.restaurant.body':
      'Restaurant software from BeCoder is on the way.',
    'landing.need.pharmacy.title': 'Pharmacy',
    'landing.need.pharmacy.body':
      'Pharmacy software from BeCoder is on the way.',
    'landing.need.exchange.title': 'Exchange Office',
    'landing.need.exchange.body':
      'Currency buy/sell, rates, and vault cash movement for exchange-office operations.',
    'landing.need.company.title': 'Company',
    'landing.need.company.body':
      'Company operations software from BeCoder is on the way.',
    'landing.need.warehouse.title': 'Warehouse',
    'landing.need.warehouse.body': 'Warehouse software from BeCoder is on the way.',
    'landing.need.factory.title': 'Factory',
    'landing.need.factory.body': 'Factory software from BeCoder is on the way.',
    'landing.need.clinic.title': 'Clinic',
    'landing.need.clinic.body': 'Clinic software from BeCoder is on the way.',
    'landing.need.institute.title': 'Institute',
    'landing.need.institute.body': 'Institute software from BeCoder is on the way.',
    'landing.need.custom.title': 'Custom Software',
    'landing.need.custom.body': 'Tell us what you need — we design and build the system.',
    'landing.journeyEyebrow': 'How it works',
    'landing.journeyTitle': 'From discovery to running software.',
    'landing.journeyLede':
      'BeCoder is the company platform. Products live in the catalog; licenses, downloads, and launch live in your portal.',
    'landing.journey1Title': 'Explore',
    'landing.journey1Body': 'Browse the software catalog. See what each product is for before you commit.',
    'landing.journey2Title': 'Choose a path',
    'landing.journey2Body':
      'Review pricing on the product page, then subscribe, start a trial, or talk with Sales when you are ready.',
    'landing.journey3Title': 'Own in portal',
    'landing.journey3Body': 'Sign in to manage products, keys, devices, invoices, and support in one place.',
    'landing.journey4Title': 'Launch & update',
    'landing.journey4Body': 'Open entitled applications and pull signed releases when new builds ship.',
    'landing.trustEyebrow': 'Why BeCoder',
    'landing.trustTitle': 'Secure software distribution you can trust.',
    'landing.showcaseTitle': 'See every product in one place.',
    'landing.showcaseLede':
      'The software catalog presents BeCoder products with clear features, screenshots, and pricing.',
    'landing.exploreShowcase': 'Explore Software',
    'landing.exploreSoftware': 'Explore Software',
    'landing.heroCatalogCaption': 'Software in the BeCoder catalog',
    'landing.portalEyebrow': 'Customer portal',
    'landing.portalTitle': 'Everything after the license.',
    'landing.portalLede':
      'Once you are entitled, BeCoder keeps ownership, downloads, and help together — not scattered across tools.',
    'landing.portalLicenses': 'Licenses & devices',
    'landing.portalLicensesBody': 'Active entitlements, seat limits, and bound devices stay under your control.',
    'landing.portalDownloads': 'Downloads & updates',
    'landing.portalDownloadsBody': 'Signed, expiring download links for licensed releases appear when builds are ready.',
    'landing.portalSupport': 'Support & records',
    'landing.portalSupportBody': 'Tickets, documentation, and invoices sit beside the software you run.',
    'landing.commercialEyebrow': 'Commercial',
    'landing.commercialTitle': 'Ready when you are.',
    'landing.commercialBody':
      'Explore products and pricing first. When you decide, continue through BeCoder — subscribe, start a trial, or Contact Sales for a guided path.',
    'landing.resourcesEyebrow': 'Resources',
    'landing.resourcesTitle': 'Guides and platform health.',
    'landing.resourcesLede': 'Read how products work, check system status, or learn how BeCoder operates.',
    'landing.resourceDocs': 'Product guides for the people who run the software.',
    'landing.resourceKb': 'Practical answers from the BeCoder knowledge base.',
    'landing.resourceStatus': 'Live platform and service health.',
    'landing.resourceAbout': 'Who we are and how secure software distribution works.',
    'landing.closeTitle': 'Ready for the next step.',
    'landing.closeLede':
      'Explore the catalog, talk to Sales, or sign in to your portal — the same journey continues after the footer.',

    'catalog.eyebrow': 'Software',
    'catalog.title': 'Software catalog',
    'catalog.lede':
      'Discover BeCoder software — review features, screenshots, and pricing before you create an account.',
    'catalog.viewDetails': 'View Details',
    'catalog.viewAll': 'View All Software',
    'catalog.emptyTitle': 'Software catalog',
    'catalog.empty':
      'Published BeCoder software will appear here when it is ready for discovery.',
    'catalog.error': 'The software catalog could not load. Check your connection and try again.',
    'catalog.fromPrice': 'From {price}',
    'catalog.fromMonth': 'From {price}/mo',
    'catalog.fromYear': 'From {price}/yr',
    'catalog.fromLifetime': 'From {price}',
    'catalog.lifetimeLabel': 'Lifetime',
    'catalog.trialAvailable': 'Trial available',
    'catalog.statusAvailable': 'Available Now',
    'guide.categories.title': 'How do I start?',
    'guide.categories.body':
      'Choose the software that fits your business, review screenshots and features, try it, then subscribe if it works for you.',
    'guide.catalog.title': 'How do I start?',
    'guide.catalog.body':
      'Choose the software that fits you, review its screenshots and features, try it, then subscribe if it works for you.',
    'guide.product.title': 'Not sure it fits?',
    'guide.product.body':
      'Review the screenshots and features first. You can try the software before you subscribe.',
    'guide.trial.title': 'Try it first',
    'guide.trial.body':
      'You do not need to pay yet. Sign in and start a trial when you are ready.',
    'guide.docs.title': 'Looking for help?',
    'guide.docs.body':
      'Pick a product below. Guides appear here as they are published — open the product page for screenshots and pricing.',
    'docs.productHelp': 'Product help',
    'docs.gettingStarted': 'Getting started',
    'docs.gettingStartedBody':
      'BeCoder is where you discover and own software for your business. Follow these simple steps.',
    'docs.helpTopics': 'Help topics',
    'docs.helpComingSoon': 'Help articles will appear here as they are published for each product.',
    'docs.stepExplore': 'Open the software catalog and pick a program that matches your business.',
    'docs.stepReview': 'Read what it does, browse real screenshots, and check the price.',
    'docs.stepTry': 'Try it when available, then subscribe or buy if it fits.',
    'catalog.statusComingSoon': 'Coming soon',
    'catalog.noScreenshots': 'Screenshots will appear here when product visuals are published.',

    'market.comingTitle': 'Software catalog',
    'market.comingBody':
      'Explore software built and supported by BeCoder. Open a product to review features, screenshots, and pricing.',
    'market.catalogError': 'The product library could not load. Check your connection and try again.',
    'showcase.eyebrow': 'Software',
    'showcase.title': 'BeCoder Software',
    'showcase.lede': 'Every published product in the BeCoder catalog — clear, commercial, and ready to explore.',
    'showcase.carouselLabel': 'Software catalog',
    'showcase.appsRail': 'Choose a product',
    'showcase.explore': 'View Details',
    'showcase.subscribe': 'Subscribe',
    'showcase.subscribeSoon': 'Subscription options appear when configured for this product',
    'showcase.startTrial': 'Start {days}-day trial',
    'showcase.trialSoon': 'Trial availability is shown when enabled for this product',
    'showcase.launch': 'Launch',
    'showcase.openApp': 'Open',
    'showcase.launchPlaceholder': 'Launch wiring comes with Product Hub',
    'showcase.comingSoon': 'Coming soon',
    'showcase.prev': 'Previous product',
    'showcase.next': 'Next product',
    'showcase.navHint': 'Use arrows, swipe, or a controller. Enter opens the product.',
    'showcase.highlights': 'Highlights',
    'showcase.plans': 'Plans',
    'showcase.plansPlaceholder': 'Public pricing appears when plans are published for this product.',
    'showcase.screenshots': 'Screenshots',
    'showcase.openScreenshot': 'Open screenshot',
    'lightbox.title': 'Image preview',
    'lightbox.prev': 'Previous screenshot',
    'lightbox.next': 'Next screenshot',
    'lightbox.position': '{current} / {total}',
    'showcase.systemRequirements': 'System requirements',
    'showcase.systemRequirementsSoon': 'Detailed requirements will publish with each release.',
    'showcase.releaseNotes': 'Release notes',
    'showcase.releaseNotesSoon': 'Release notes will appear here when published.',
    'showcase.backToShowcase': 'Back to Software',
    'showcase.catalogError': 'Software could not load. Check your connection and try again.',
    'showcase.empty': 'No software is published in the catalog yet.',
    'showcase.notFound': 'This product is not available.',
    'showcase.about': 'About',
    'hub.startTrial': 'Start free trial',
    'hub.startTrialLede':
      'Start a 3-day free trial for {slug}. No payment method required. The trial starts only when you activate it.',
    'hub.confirmTrial': 'Start free trial',
    'hub.noPaymentRequired': 'No payment method required',
    'hub.trialDaysBadge': '3-day free trial',
    'hub.trialReady': '{name} trial is ready',
    'hub.daysRemaining': '{count} days remaining',
    'hub.dayRemaining': '1 day remaining',
    'hub.trialEndsToday': 'Trial ends today',
    'hub.trialExpiredTitle': 'Your free trial has ended',
    'hub.trialExpiredBody':
      'Your free trial has ended. Your data is safe. Subscribe to continue where you left off.',
    'hub.dataChoiceTitle': 'Your subscription is active. How would you like to continue?',
    'hub.dataChoiceKeep': 'Keep my trial data',
    'hub.dataChoiceFresh': 'Start fresh',
    'hub.dataChoiceFreshConfirmTitle': 'Start fresh?',
    'hub.dataChoiceFreshConfirmBody':
      'This will permanently remove the GlassPOS operational data created during your trial. Your BeCoder account, subscription and license will remain active. This action cannot be undone.',
    'hub.dataChoiceKeepDone': 'Trial data will be kept. Continue where you left off.',
    'hub.dataChoiceFreshDone': 'Your new workspace is ready.',
    'hub.dataChoiceDeferred':
      'Start Fresh is not safely available for this product yet. Your data was not deleted.',
    'hub.dataChoiceFreshUnavailable':
      'Start Fresh will be offered when this product supports a safe operational reset. Until then, keep your trial data.',
    'hub.dataChoicePending': 'Choose how to continue with your trial data',
    'hub.trialBadge': 'Trial',
    'hub.trialExpiredBadge': 'Trial expired',
    'hub.paidBadge': 'Paid',
    'hub.signInRequired': 'Sign in to start a trial.',
    'hub.orgName': 'Organization name',
    'hub.trialEnds': 'Trial ends {date}.',
    'hub.trialFailed': 'Could not start trial.',
    'hub.launchFailed': 'Could not launch application.',
    'hub.upgrade': 'Upgrade plan',
    'hub.upgradeLede': 'Choose a plan for this software. Online payment will appear here when it is enabled.',
    'hub.upgradeNeedProduct': 'Choose a product from Applications or My Products first.',
    'hub.plansLoadFailed': 'Plans could not load.',
    'hub.checkoutFailed': 'Checkout could not start.',
    'pay.title': 'Payment',
    'pay.redirecting': 'You are being redirected securely to FastPay to complete payment.',
    'pay.checking': 'Confirming your payment…',
    'pay.successTitle': 'Payment successful',
    'pay.successBody': 'Payment completed successfully. Your software is now available in your account.',
    'pay.pendingTitle': 'Payment pending',
    'pay.pendingBody':
      'We are still confirming your payment. Your software will appear once verification completes — you do not need to pay again.',
    'pay.failedTitle': 'Payment failed',
    'pay.failedBody': 'Payment could not be completed. You can try again or contact sales for help.',
    'pay.cancelledTitle': 'Payment cancelled',
    'pay.cancelledBody': 'Payment was cancelled. No charge was completed. You can try again when ready.',
    'pay.openSoftware': 'Open My Software',
    'pay.goWorkspace': 'Go to Customer Workspace',
    'pay.refresh': 'Check again',
    'pay.tryAgain': 'Try again',
    'pay.missingPayment': 'Missing payment reference.',
    'pay.confirmFailed': 'Could not confirm payment status.',
    'admin.reconcile': 'Reconcile',
    'admin.paymentsIntro': 'Inspect FastPay payments and reconcile interrupted activations.',
    'hub.billingOff': 'Online payment is not enabled yet. Contact Sales, or start a trial when available.',
    'hub.subscriptionsIntro': 'Trials and subscriptions for your active organization.',
    'hub.subscriptionsLoadFailed': 'Subscriptions could not load.',
    'hub.trials': 'Trials',
    'hub.plans': 'Subscriptions',
    'hub.noTrials': 'No trials yet.',
    'hub.noSubscriptions': 'No subscriptions yet.',
    'hub.invoicesIntro': 'Invoices and payments for your organization.',
    'hub.invoicesLoadFailed': 'Invoices could not load.',
    'admin.trialsIntro': 'Organization product trials across the platform.',
    'admin.subscriptionsIntro': 'Organization subscriptions. Activate manually when billing is off.',
    'admin.activateManual': 'Activate manually',
    'admin.activateSubscriptionConfirm': 'Activate this subscription for 30 days without a payment?',
    'admin.confirmSuspendCustomer.title': 'Suspend customer?',
    'admin.confirmSuspendCustomer.message':
      'Suspend {name} ({email})? They will lose access until reactivated.',
    'admin.confirmActivateCustomer.title': 'Activate customer?',
    'admin.confirmActivateCustomer.message': 'Activate {name} ({email}) and restore access?',
    'admin.confirmSuspendLicense.title': 'Suspend license?',
    'admin.confirmSuspendLicense.message':
      'Suspend the license for {name} ({email})? Active devices will stop validating.',
    'admin.confirmRevokeLicense.title': 'Revoke license?',
    'admin.confirmRevokeLicense.message':
      'Permanently revoke the license for {name} ({email})? This cannot be undone.',
    'admin.confirmActivateLicense.title': 'Activate license?',
    'admin.confirmActivateLicense.message': 'Activate the license for {name} ({email})?',
    'admin.confirmDeactivateDevice.title': 'Force deactivate device?',
    'admin.confirmDeactivateDevice.message':
      'Force deactivate {name} for {email}? The seat will be freed immediately.',
    'admin.confirmPublishRelease.title': 'Publish release?',
    'admin.confirmPublishRelease.message':
      'Publish version {name}? Customers will be able to download it.',
    'admin.confirmDeleteMedia.title': 'Delete media?',
    'admin.confirmDeleteMedia.message': 'Remove this media asset? This cannot be undone.',
    'admin.confirmActivateSubscription.title': 'Activate subscription?',
    'admin.confirmActivateSubscription.message':
      'Activate this subscription for 30 days without a payment?',
    'admin.customersIntro': 'Search customers and open details for operations.',
    'admin.searchCustomers': 'Search name, email, or user id',
    'admin.filterAnyStatus': 'Any status',
    'admin.filterActive': 'Active',
    'admin.filterSuspended': 'Suspended',
    'admin.filterAnyProvider': 'Any provider',
    'admin.open360': 'Details',
    'admin.customer360': 'Customer details',
    'admin.customerDetailsSub': 'Customer details and management',
    'desk.metric.activeSubscriptions': 'Active subscriptions',
    'cc.access.showSecondary': 'More metrics',
    'cc.access.hideSecondary': 'Fewer metrics',
    'cc.settings.tab.identity': 'Identity',
    'cc.settings.tab.contact': 'Contact',
    'cc.settings.tab.about': 'About',
    'cc.settings.tab.social': 'Social',
    'cc.settings.tab.footer': 'Footer',
    'cc.settings.tab.advanced': 'Advanced',
    'admin.results': 'results',
    'admin.noCustomers': 'No customers match these filters.',
    'admin.backToCustomers': 'Back to customers',
    'admin.customerNotFound': 'Customer not found.',
    'admin.provider': 'Provider',
    'admin.status': 'Status',
    'admin.verified': 'Verified',
    'admin.lastSignIn': 'Last sign-in',
    'admin.created': 'Created',
    'admin.role': 'Role',
    'admin.orgMemberships': 'Org memberships',
    'admin.tabOverview': 'Overview',
    'admin.tabNotes': 'Notes',
    'admin.tabActivity': 'Activity',
    'admin.emptyProducts': 'No entitled products.',
    'admin.emptyLicenses': 'No licenses.',
    'admin.emptyTrials': 'No trials.',
    'admin.emptySubscriptions': 'No subscriptions.',
    'admin.emptyInvoices': 'No invoices.',
    'admin.emptyPayments': 'No payments.',
    'admin.emptyDevices': 'No active devices yet.',
    'admin.emptyDownloads': 'No download activity yet.',
    'admin.emptyNotes': 'No internal notes yet.',
    'admin.emptyActivity': 'No related activity yet.',
    'admin.licenseType': 'Type',
    'admin.starts': 'Starts',
    'admin.ends': 'Ends',
    'admin.openLicenses': 'Licenses',
    'admin.organization': 'Organization',
    'admin.plan': 'Plan',
    'admin.periodStart': 'Period start',
    'admin.periodEnd': 'Period end',
    'admin.amount': 'Amount',
    'admin.payments': 'Payments',
    'admin.version': 'Version',
    'admin.platform': 'Platform',
    'admin.downloadedAt': 'Downloaded',
    'admin.deviceName': 'Device',
    'admin.activated': 'Activated',
    'admin.lastSeen': 'Last seen',
    'admin.action': 'Action',
    'admin.resource': 'Resource',
    'admin.actor': 'Actor',
    'admin.notePlaceholder': 'Internal note (not visible to the customer)',
    'admin.addNote': 'Add note',
    'admin.noteSaved': 'Note saved',
    'admin.settings.nav': 'Platform identity',
    'admin.settings.title': 'Platform identity & contact',
    'admin.settings.intro':
      'Public About and Contact pages read these values. Leave optional fields empty to hide them.',
    'admin.settings.saved': 'Platform settings saved',
    'admin.settings.platformName': 'Platform display name',
    'admin.settings.contactPerson': 'Primary contact person',
    'admin.settings.mobilePhone': 'Mobile / phone',
    'admin.settings.whatsappNumber': 'WhatsApp number',
    'admin.settings.supportEmail': 'Public support email',
    'admin.settings.salesEmail': 'Sales email',
    'admin.settings.address': 'Company / address text',
    'admin.settings.hours': 'Business hours text',
    'admin.settings.aboutShortEn': 'About Us short (EN)',
    'admin.settings.aboutShortAr': 'About Us short (AR)',
    'admin.settings.aboutShortCkb': 'About Us short (CKB)',
    'admin.settings.aboutFullEn': 'About Us full (EN)',
    'admin.settings.aboutFullAr': 'About Us full (AR)',
    'admin.settings.aboutFullCkb': 'About Us full (CKB)',
    'admin.settings.facebook': 'Facebook URL',
    'admin.settings.instagram': 'Instagram URL',
    'admin.settings.linkedin': 'LinkedIn URL',
    'admin.settings.youtube': 'YouTube URL',
    'admin.settings.footerContact': 'Footer contact text',
    'admin.settings.footerCopyright': 'Copyright / footer company text',
    'contact.title': 'Contact',
    'contact.lede': 'Reach BeCoder for product questions and sales.',
    'contact.person': 'Contact person',
    'contact.phone': 'Phone',
    'contact.hours': 'Business hours',
    'contact.address': 'Address',
    'contact.call': 'Call',
    'contact.whatsapp': 'WhatsApp',
    'contact.email': 'Email',
    'contact.contactSales': 'Contact Sales',
    'contact.openPage': 'Full contact page',
    'contact.social': 'Social',
    'about.defaultBody':
      'BeCoder is a software platform that provides ready business applications and builds custom software for companies and multi-branch operations — focused on practical business tools.',
    'about.pillarApps': 'Ready applications',
    'about.pillarAppsDesc': 'Catalog products designed for real business workflows.',
    'about.pillarCustom': 'Custom software',
    'about.pillarCustomDesc': 'Purpose-built systems when catalog products are not enough.',
    'about.pillarPractical': 'Practical focus',
    'about.pillarPracticalDesc': 'Software that serves operations — not marketing theater.',
    'common.search': 'Search',
    'common.prev': 'Previous',
    'common.next': 'Next',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.actions': 'Actions',
    'desk.licensesTitle': 'Licenses desk',
    'desk.licensesIntro': 'Search, filter, and operate customer licenses.',
    'desk.trialsTitle': 'Trials desk',
    'desk.subscriptionsTitle': 'Subscriptions desk',
    'desk.devicesTitle': 'Devices desk',
    'desk.devicesIntro': 'Active and deactivated device bindings.',
    'desk.searchLicenses': 'Search customer, email, license id, product',
    'desk.searchTrials': 'Search customer, email, product, trial or org id',
    'desk.searchSubscriptions': 'Search customer, email, product, subscription id',
    'desk.searchDevices': 'Search customer, email, device, product, license id',
    'desk.filterAnyProduct': 'Any product',
    'desk.filterAnyType': 'Any type',
    'desk.filterAnyValidity': 'Any validity',
    'desk.filterAnyPeriod': 'Any period',
    'desk.type.trial': 'Trial',
    'desk.type.monthly': 'Monthly',
    'desk.type.yearly': 'Annual',
    'desk.type.lifetime': 'Lifetime',
    'desk.status.expired': 'Expired',
    'desk.status.revoked': 'Revoked',
    'desk.status.canceled': 'Canceled',
    'desk.status.converted': 'Converted',
    'desk.status.deactivated': 'Deactivated',
    'desk.validity.active': 'Currently valid',
    'desk.validity.expired': 'Past end date',
    'desk.licenseId': 'License',
    'desk.issuedByAdmin': 'Admin-issued',
    'desk.permanent': 'Permanent',
    'desk.lifetimeNoEnd': 'Lifetime has no end date',
    'desk.endsOptional': 'Optional end date',
    'desk.lifetimePolicy':
      'Lifetime = pay once / use forever. Included Premium Support (3 months) is separate and is not tracked as license expiration.',
    'desk.openDetail': 'Detail',
    'desk.openInDesk': 'Open in desk',
    'desk.backLicenses': 'Back to licenses',
    'desk.licenseDetail': 'License detail',
    'desk.licenseNotFound': 'License not found.',
    'desk.history': 'History',
    'desk.newEndDate': 'New end date',
    'desk.currentEnd': 'Current end',
    'desk.saveExpiration': 'Update expiration',
    'desk.expirationSaved': 'Expiration updated',
    'desk.confirmExpiration.title': 'Update license expiration?',
    'desk.confirmExpiration.message':
      'Change end date for {product} ({email}) from {old} to {next}?',
    'desk.confirmActivateLicense.message':
      'Activate license {id} for {name} ({email}) on {product}?',
    'desk.confirmSuspendLicense.message':
      'Suspend license {id} for {name} ({email}) on {product}? Active devices will stop validating.',
    'desk.confirmRevokeLicense.message':
      'Revoke license {id} for {name} ({email}) on {product}? This cannot be undone. The record remains visible.',
    'desk.confirmExtendTrial.title': 'Extend trial?',
    'desk.confirmExtendTrial.message':
      'Extend {product} for {email} by {days} days? Current end {old} → new end {next}.',
    'desk.confirmForceGrantTrial.title': 'Force-grant trial time?',
    'desk.confirmForceGrantTrial.message':
      'This {product} trial for {email} is already {status}. Type-confirm force grant of {days} days ({old} → {next}). This is an exceptional admin override, not a normal extend.',
    'desk.confirmCancelTrial.title': 'Cancel trial?',
    'desk.confirmCancelTrial.message':
      'End trial for {product} ({email}) early? The historical record remains.',
    'desk.confirmActivateSubscription.message':
      'Manually activate subscription for {product} ({email}) for 30 days without a payment?',
    'desk.confirmDeactivateDevice.message':
      'Force deactivate {name} for {email} on {product}? The device record remains; the license is not deleted.',
    'desk.extend': 'Extend',
    'desk.forceGrant': 'Force grant',
    'desk.extendDays': 'Days to extend',
    'desk.cancelTrial': 'Cancel trial',
    'desk.daysLeft': 'Days left',
    'desk.noFilterResults': 'No records match these filters.',
    'desk.loadError': 'Could not load data',
    'desk.metric.activeLicenses': 'Active licenses',
    'desk.metric.suspendedLicenses': 'Suspended',
    'desk.metric.revokedLicenses': 'Revoked',
    'desk.metric.lifetime': 'Lifetime',
    'desk.metric.activeTrials': 'Active trials',
    'desk.metric.expiringTrials': 'Expiring trials (7d)',
    'desk.metric.activeMonthly': 'Active monthly',
    'desk.metric.activeAnnual': 'Active annual',
    'desk.metric.activeDevices': 'Active devices',
    'dist.releasesTitle': 'Distribution — releases',
    'dist.releasesIntro': 'Search, draft, upload, publish, and audit product releases.',
    'dist.downloadAuditNav': 'Download audit',
    'dist.downloadAuditTitle': 'Download audit',
    'dist.downloadAuditIntro': 'Customer download events across products and releases.',
    'dist.searchReleases': 'Search version, file, checksum',
    'dist.searchDownloads': 'Search customer, email, version',
    'dist.filterAnyPlatform': 'Any platform',
    'dist.createDraft': 'Create draft',
    'dist.draftCreated': 'Draft created',
    'dist.uploadFile': 'Upload file',
    'dist.replaceFile': 'Replace file',
    'dist.uploadDone': 'Upload complete',
    'dist.unpublish': 'Unpublish',
    'dist.unpublished': 'Unpublished',
    'dist.deleted': 'Draft deleted',
    'dist.verifyIntegrity': 'Verify integrity',
    'dist.integrityOk': 'Integrity verified',
    'dist.integrityFail': 'Integrity check failed',
    'dist.viewDownloads': 'View downloads',
    'dist.openDownloadAudit': 'Open download audit',
    'dist.downloadAuditHint': '{count} download events — use Download audit for search and filters.',
    'dist.file': 'File',
    'dist.size': 'Size',
    'dist.checksum': 'پشکنینی فایل',
    'dist.flags': 'Flags',
    'dist.current': 'Current',
    'dist.superseded': 'Superseded',
    'dist.notesYes': 'Notes ✓',
    'dist.notesNo': 'No notes',
    'dist.minSupported': 'Min supported version',
    'dist.notes.en': 'Release notes (English)',
    'dist.notes.ar': 'Release notes (Arabic)',
    'dist.notes.ckb': 'Release notes (Kurdish)',
    'dist.confirmPublishDetail':
      'Publish {product} {version} ({platform})? File: {file}. Customers will be able to download it.',
    'dist.confirmUnpublish.title': 'Unpublish release?',
    'dist.confirmUnpublish.message': 'Unpublish {product} {version}? Customers will lose download access.',
    'dist.confirmDelete.title': 'Delete draft?',
    'dist.confirmDelete.message': 'Delete draft {version}? This cannot be undone.',
    'dist.distributionStatus': 'Distribution',
    'dist.manageReleases': 'Manage releases',
    'dist.publishedAvailable': 'Published release available',
    'dist.latestVersion': 'Latest version',
    'dist.platforms': 'Platforms',
    'dist.packageReady': 'Package ready',
    'dist.fromDate': 'From date',
    'dist.toDate': 'To date',
    'dist.empty.no_entitlement': 'You do not have download access for this product yet.',
    'dist.empty.coming_soon': 'Downloads for this product are not available yet — it is coming soon.',
    'dist.empty.release_unpublished': 'No published releases are available for download yet.',
    'dist.empty.package_missing': 'A release exists but the install package is not ready yet.',
    'dist.empty.product_not_distributable': 'This product is not distributed through BeCoder downloads.',
    'dist.empty.expired': 'Your access to downloads for this product has expired.',
    'dist.empty.suspended_or_revoked': 'Downloads are unavailable because access was suspended or revoked.',
    'dist.empty.unknown': 'Downloads are not available right now.',
    'admin.launchBaseUrl': 'Launch base URL',
    'admin.saveLaunchUrl': 'Save launch URL',
    'admin.launchUrlSaved': 'Launch URL saved',
    'admin.mediaTitle': 'Media Manager',
    'admin.mediaLede': 'Upload and manage showcase media for catalog applications.',
    'admin.mediaIcon': 'App icon',
    'admin.mediaHero': 'Hero image',
    'admin.mediaPoster': 'Video poster',
    'admin.mediaVideo': 'Promotional video',
    'admin.mediaScreenshots': 'Screenshots',
    'admin.mediaUploadReplace': 'Upload / replace',
    'admin.mediaAddScreenshot': 'Add screenshot',
    'admin.mediaAddScreenshots': 'Add screenshots',
    'admin.mediaSelectScreenshots': 'Select screenshots',
    'admin.mediaScreenshotsHint':
      'Select multiple images, review thumbnails, then Upload all. Existing screenshots are kept. Drag thumbnails to reorder.',
    'admin.mediaDragReorder': 'Drag to reorder',
    'admin.mediaPreview': 'Preview image',
    'admin.mediaUploadDone': 'Done',
    'admin.mediaQueued': 'Queued',
    'admin.mediaSkipped': 'Skipped',
    'admin.mediaPartialUpload': 'Some uploads failed',
    'admin.mediaUploadFailed': 'Screenshot upload failed',
    'admin.mediaPendingTitle': '{count} selected ({valid} ready to upload)',
    'admin.mediaPendingInvalid': '{count} file(s) will be skipped due to validation errors.',
    'admin.mediaClearSelection': 'Clear selection',
    'admin.mediaUploadAll': 'Upload all ({count})',
    'admin.mediaRemoveFromSelection': 'Remove from selection',
    'admin.mediaBatchComplete': 'Batch upload finished',
    'admin.mediaBatchResult': 'Uploaded {uploaded} · Failed {failed} · Skipped {skipped} · Total {total}',
    'admin.mediaReplaceScreenshot': 'Replace screenshot',
    'admin.mediaReplaced': 'Screenshot replaced',
    'admin.mediaInvalidType': '{name}: unsupported type ({type}). Use PNG, JPG, WEBP, or GIF.',
    'admin.mediaInvalidSize': '{name}: exceeds {max} size limit.',
    'admin.mediaInvalidEmpty': '{name}: file is empty.',
    'admin.mediaEmpty': 'No media yet.',
    'admin.mediaNoProducts': 'Create a product first.',
    'admin.mediaUploaded': 'Media uploaded',
    'admin.mediaDeleted': 'Media removed',

    'cms.saveDraft': 'Save draft',
    'cms.publish': 'Publish',
    'cms.preview': 'Preview product page',
    'cms.saved': 'Saved',
    'cms.published': 'Published',
    'cms.notFound': 'Product not found',
    'cms.editCommercial': 'Edit commercial',
    'cms.searchProducts': 'Search name or slug',
    'cms.filterAnyStatus': 'Any status',
    'cms.filterAnyPurchasable': 'Any purchasable',
    'cms.filterAnyTrial': 'Any trial',
    'cms.purchasable': 'Purchasable',
    'cms.notPurchasable': 'Not purchasable',
    'cms.trialEnabled': 'Trial enabled',
    'cms.trialDisabled': 'Trial disabled',
    'cms.operationalTrial': 'Operational trial',
    'cms.trialDays': 'Trial days',
    'cms.trialSaved': 'Trial settings saved',
    'cms.statusPublished': 'Published',
    'cms.statusDraft': 'Draft',
    'cms.pricingOk': 'Pricing OK',
    'cms.pricingMissing': 'Pricing incomplete',
    'cms.plansPricing': 'Plans & Pricing',
    'cms.lifetimeLicense': 'Lifetime License',
    'cms.priceUsd': 'Price (USD)',
    'cms.priceMajor': 'Amount',
    'cms.priceHintIqd': 'Enter IQD as a whole amount (example 600000 for 600,000 IQD).',
    'cms.priceHintUsd': 'Enter USD as a whole amount (example 550 for $550).',
    'cms.pricePreview': 'Stores as {minor} amountMinor',
    'cms.marketIraq': 'IRAQ (IQD)',
    'cms.marketInternational': 'INTERNATIONAL (USD)',
    'cms.planType': 'Plan type',
    'cms.planMarket': 'Market',
    'cms.planPeriod': 'Billing period',
    'cms.planActive': 'Active',
    'cms.planInactive': 'Inactive',
    'cms.cloudServices': 'Cloud Services Annual',
    'cms.planMonthly': 'Monthly',
    'cms.planAnnual': 'Annual',
    'cms.createPlan': 'Add plan',
    'cms.planCreated': 'Plan created',
    'cms.planUpdated': 'Plan updated',
    'cms.noPlansMarket': 'No plans in this market yet.',
    'cms.raqbSubscriptionOnly': 'Raqb is subscription-only. No Lifetime plan.',
    'cms.raqbTrialNote': 'Trial: {days} days. Starts only after the customer taps Start Trial.',
    'cms.lifetimeVsCloud': 'Lifetime is permanent software ownership. Cloud Services is a separate annual subscription and does not expire Lifetime.',
    'cms.trialPlatformOff': 'Platform trial starts are currently OFF (TRIALS_ENABLED). Product settings below do not start public trials.',
    'cms.trialPlatformOn': 'Platform trial starts are ON. Product must also support trial and be operational.',
    'cms.trialProductSupport': 'This product supports trial',
    'cms.trialPlatformGate': 'Platform currently allows trial starts',
    'cms.statusHelp': 'Catalog visibility: Active is listed, Hidden/Archived are not, Coming soon is listed but not sold as available.',
    'cms.purchasableHelp': 'Commercial purchasable flag. Independent of catalog status and CMS publish.',
    'cms.publishedHelp': 'Storefront commercial copy is published. Draft content stays in Admin until Publish.',
    'cms.heroAuthoritative': 'Hero/Banner is the large image on the public product page. Replacing Hero does not delete screenshots or the icon.',
    'cms.savePrice': 'Save price',
    'cms.priceSaved': 'Price saved',
    'cms.annualSavings': 'Annual savings vs monthly',
    'cms.invalidPrice': 'Enter a valid price',
    'cms.displayName': 'Display name',
    'cms.heroIntro': 'Hero intro',
    'cms.whoLede': 'Who lede',
    'cms.highlightsTitle': 'Highlights title',
    'cms.explainHeading': 'Explain heading',
    'cms.fitGuidance': 'Fit guidance',
    'cms.audiences': 'Audiences',
    'cms.features': 'Features',
    'cms.problems': 'Problems',
    'cms.explainSections': 'Explain sections',
    'cms.fitRecommendations': 'Fit recommendations',
    'cms.addItem': 'Add item',
    'cms.featureTitle': 'Title',
    'cms.featureDesc': 'Description',
    'cms.faqNote': 'Shared FAQs apply to all products; product FAQs are scoped here.',
    'cms.addFaq': 'Add FAQ',
    'cms.faqShared': 'Shared FAQ',
    'cms.faqProduct': 'Product FAQ',
    'cms.faqSaved': 'FAQ saved',
    'cms.faqCreated': 'FAQ created',
    'cms.faqDeleted': 'FAQ deleted',
    'cms.newFaqQuestion': 'New question',
    'cms.savePolicy': 'Save policy',
    'cms.policySaved': 'Policy saved',
    'cms.mediaCounts': 'Media',
    'cms.openMediaManager': 'Open media manager',
    'cms.warnMissingAr': 'Arabic content missing',
    'cms.warnMissingCkb': 'Kurdish content missing',
    'cms.warnMissingPricing': 'Pricing incomplete',
    'cms.warnMissingHero': 'Hero image missing',
    'cms.warnMissingScreenshots': 'No screenshots',
    'cms.warnMissingLaunch': 'Launch URL missing for active product',
    'cms.warnTrialMismatch': 'Trial enabled but not operational',
    'cms.section.overview': 'Overview',
    'cms.section.content': 'Content',
    'cms.section.plans': 'Plans & Pricing',
    'cms.section.trial': 'Trial',
    'cms.section.structure': 'Audience & Features',
    'cms.section.faq': 'FAQ',
    'cms.section.policy': 'Support Policy',
    'cms.section.media': 'Media',
    'cms.section.distribution': 'Distribution',
    'cms.confirmLaunchUrl.title': 'Change launch URL?',
    'cms.confirmLaunchUrl.message': 'Old: {old}\nNew: {next}',
    'cms.confirmPrice.title': 'Change plan price?',
    'cms.confirmPrice.message': '{plan}\nOld: {old}\nNew: {next}',
    'cms.confirmPublish.title': 'Publish commercial content?',
    'cms.confirmPublish.message': 'Publish storefront content for {name}?',
    'cms.confirmPolicy.title': 'Save support & lifetime policy?',
    'cms.confirmPolicy.message': 'Update policy for {locale}?',
    'cms.confirmFaqDelete.title': 'Delete FAQ?',
    'cms.policy.lifetimeTitle': 'Lifetime title',
    'cms.policy.lifetimeLede': 'Lifetime lede',
    'cms.policy.lifetimeBody': 'Lifetime body',
    'cms.policy.supportTitle': 'Support title',
    'cms.policy.supportBugsTitle': 'Bugs title',
    'cms.policy.supportBugsBody': 'Bugs body',
    'cms.policy.supportSecurityTitle': 'Security title',
    'cms.policy.supportSecurityBody': 'Security body',
    'cms.policy.supportTechTitle': 'Tech title',
    'cms.policy.supportTechBody': 'Tech body',
    'cms.policy.supportCustomTitle': 'Custom title',
    'cms.policy.supportCustomBody': 'Custom body',
    'cms.policy.thirdPartyTitle': 'Third-party title',
    'cms.policy.thirdPartyBody': 'Third-party body',
    'cms.policy.updatesTitle': 'Updates title',
    'cms.policy.updatesBody': 'Updates body',
    'admin.mediaDeleteConfirm': 'Remove this media asset?',
    'admin.mediaMoveUp': 'Move up',
    'admin.mediaMoveDown': 'Move down',
    'nav.media': 'Media',
    'portal.launch': 'Launch',
    'nav.trials': 'Trials',
    'showcase.getStarted': 'Trial, subscribe, launch',
    'showcase.getStartedLede':
      'Actions are catalog-ready placeholders. Checkout and Product Hub launch arrive later.',
    'showcase.videoControls': 'Video controls',
    'showcase.play': 'Play video',
    'showcase.pause': 'Pause video',
    'showcase.mute': 'Mute',
    'showcase.unmute': 'Unmute',
    'showcase.fullscreen': 'Fullscreen',
    'showcase.exitFullscreen': 'Exit fullscreen',
    'showcase.videoReducedMotion': 'Motion is reduced — showing a still frame.',

    'soon.defaultSubtitle':
      'Reserved for a future release. Not part of the current BeCoder private-distribution launch.',

    'company.eyebrow': 'BeCoder Software',
    'company.heroTitle': 'Software, built to last.',
    'company.heroSubtitle': 'Build, publish, and evolve a portfolio of serious software from one trusted platform.',
    'company.exploreSoftware': 'Explore software',
    'company.apiStatus': 'API {status}',
    'company.softwareCount': '{count} software title',
    'company.softwareCountPlural': '{count} software titles',
    'company.releaseCount': '{count} published release',
    'company.releaseCountPlural': '{count} published releases',

    'pulse.aria': 'Company pulse',
    'pulse.software': 'Software',
    'pulse.softwareDetail': 'Built by BeCoder',
    'pulse.titles': '{count} titles',
    'pulse.latestRelease': 'Latest release',
    'pulse.noneYet': 'None yet',
    'pulse.awaitingPublication': 'Awaiting publication',
    'pulse.customerUse': 'Customer use',
    'pulse.activeCount': '{count} active',
    'pulse.deployments': '{count} deployments',
    'pulse.needsAttention': 'Needs attention',
    'pulse.trackedExceptions': 'Tracked exceptions',
    'pulse.noTrackedExceptions': 'No tracked exceptions',
    'pulse.platformHealth': 'Platform health',
    'pulse.liveApiSignal': 'Live API signal',

    'featured.sectionEyebrow': 'What BeCoder builds',
    'featured.sectionTitle': 'Featured software',
    'featured.sectionDesc': 'The product currently leading the BeCoder portfolio',
    'featured.managePortfolio': 'Manage portfolio',
    'featured.empty': 'Publish your first product to establish the BeCoder software portfolio.',
    'featured.portfolioLabel': 'BeCoder portfolio',
    'featured.fallbackTagline': 'Purpose-built software, engineered and distributed by BeCoder.',
    'featured.currentVersion': 'Current version',
    'featured.released': 'Released',
    'featured.customers': 'Customers',
    'featured.deployments': 'Deployments',
    'featured.stability': 'Stability',
    'featured.updatePolicy': 'Update policy',
    'featured.recentImprovements': 'Recent improvements',
    'featured.openProduct': 'Open product',
    'featured.coverLabel': 'BeCoder Software',

    'portfolio.sectionEyebrow': 'Built by BeCoder',
    'portfolio.sectionTitle': 'Software portfolio',
    'portfolio.sectionDesc': 'Every product shares one standard of engineering and craft',
    'portfolio.fallbackTagline': 'Software engineered and distributed by BeCoder.',
    'portfolio.customers': 'Customers',
    'portfolio.deployments': 'Deployments',
    'portfolio.policy': 'Policy',
    'portfolio.updated': 'Updated {date}',
    'portfolio.updateUnavailable': 'Update history unavailable',
    'portfolio.open': 'Open',

    'releases.sectionEyebrow': 'Progress, shipped',
    'releases.sectionTitle': 'Recent releases',
    'releases.sectionDesc': 'Verified milestones from the software BeCoder publishes',
    'releases.pipeline': 'Release pipeline',
    'releases.empty': 'No published releases yet.',
    'releases.fallbackProduct': 'Software product',
    'releases.noNotes': 'No release notes supplied.',

    'activity.momentumEyebrow': 'Customer momentum',
    'activity.momentumTitle': 'Software in use',
    'activity.momentumDesc': 'Recent installations and activations from real customer activity',
    'activity.newestDeployments': 'Newest deployments',
    'activity.noDeployments': 'No deployments recorded.',
    'activity.latestActivation': 'Latest activation',
    'activity.customer': 'Customer',
    'activity.software': 'Software',
    'activity.newsEyebrow': 'Software newsroom',
    'activity.newsTitle': "What's new",
    'activity.newsDesc': 'Factual signals from releases, roadmap, and security activity',
    'activity.latestRelease': 'Latest release',
    'activity.noPublishedRelease': 'No published release',
    'activity.noDate': 'No date available',
    'activity.releaseWork': 'Release work',
    'activity.releasesInProgress': '{count} releases in progress',
    'activity.releaseInProgress': '{count} release in progress',
    'activity.draftPipeline': 'Draft pipeline',
    'activity.upcomingSoftware': 'Upcoming software',
    'activity.noAnnouncedTitles': 'No announced titles',
    'activity.onRoadmap': '{count} on the public roadmap',
    'activity.latestSecurity': 'Latest security activity',
    'activity.noSecurityEvent': 'No recent security event',
    'activity.auditChecked': 'Audit trail checked',

    'kpi.sectionEyebrow': 'Business health',
    'kpi.sectionTitle': 'Platform KPIs',
    'kpi.sectionDesc': 'Operational signals supporting the software portfolio',
    'kpi.customers': 'Customers',
    'kpi.customersSub': 'Active accounts on the platform',
    'kpi.products': 'Products',
    'kpi.productsSub': 'Catalog titles under distribution',
    'kpi.licenses': 'Licenses',
    'kpi.licensesSub': 'Issued entitlements',
    'kpi.suspended': 'Suspended',
    'kpi.suspendedSub': 'Suspended or revoked seats',

    'eng.sectionEyebrow': 'Engineering transparency',
    'eng.sectionTitle': 'Platform engineering',
    'eng.sectionDesc': 'Live, factual indicators from the systems behind BeCoder software',
    'eng.apiStatus': 'API status',
    'eng.publishedArtifacts': 'Published artifacts',
    'eng.checksummed': 'پشکنینی فایلmed artifacts',
    'eng.docsCoverage': 'Documentation coverage',
    'eng.activeDeployments': 'Active deployments',
    'eng.recentAudit': 'Recent audit events',

    'attention.sectionEyebrow': 'Attention',
    'attention.titleNone': 'No tracked exceptions',
    'attention.titleSome': '{count} tracked exceptions',
    'attention.titleOne': '{count} tracked exception',
    'attention.desc': 'Only conditions represented by current platform data are shown',
    'attention.suspendedLicenses': 'Suspended or revoked licenses',
    'attention.draftReleases': 'Draft releases',
    'attention.inactiveDeployments': 'Inactive deployments',
    'attention.hiddenProducts': 'Hidden or archived products',

    'pulse.sectionEyebrow': 'Platform activity',
    'pulse.sectionTitle': 'System pulse',
    'pulse.sectionDesc': 'The latest work completed across your platform',
    'pulse.empty': 'No audit entries yet.',
    'pulse.authLogin': 'Secure sign-in completed',

    'ops.sectionEyebrow': 'Operational detail',
    'ops.sectionTitle': 'Run the platform',
    'ops.sectionDesc': 'The controls behind distribution, access, and release integrity',
    'ops.customerAccess': 'Customer access',
    'ops.customerAccessDetail': '{count} accounts',
    'ops.licenseOps': 'License operations',
    'ops.licenseOpsDetail': '{count} entitlements',
    'ops.releasePipeline': 'Release pipeline',
    'ops.releasePipelineDetail': '{count} software titles',

    'customer.heroEyebrow': 'BeCoder Customer',
    'customer.heroTitle': 'Your software, ready.',
    'customer.heroSubtitle': 'Licensed products, secure downloads, and trusted devices — controlled from one place.',
    'customer.openDownloads': 'Open downloads',
    'customer.trustStanding': 'Account in good standing',
    'customer.trustLicensed': 'Licensed access',
    'customer.trustPrivate': 'Secure software distribution',
    'customer.kpiProducts': 'Products',
    'customer.kpiProductsSub': 'Licensed titles available to you',
    'customer.kpiLicenses': 'Licenses',
    'customer.kpiLicensesSub': 'Active and historical entitlements',
    'customer.kpiDevices': 'Active devices',
    'customer.kpiDevicesSub': 'Bound seats currently in use',
    'customer.readyTitle': 'Ready to use',
    'customer.readyDesc': 'Software currently licensed to your account',
    'customer.readyEmptyTitle': 'Your software journey starts here.',
    'customer.readyEmptyBody': 'When BeCoder licenses software to your account, it will appear in this home.',
    'customer.downloadsLink': 'Downloads',
    'customer.loadFailed': 'Failed to load',

    'home.eyebrow': 'Customer home',
    'home.title': 'Welcome back, {name}.',
    'home.subtitle': 'Your software, licenses, updates, and support — in one calm place.',
    'home.exploreProducts': 'Explore products',
    'home.announceTitle': 'You are inside BeCoder',
    'home.announceBody': 'This is your customer home. Owned software, downloads, licenses, and help live here — not scattered across tools.',
    'home.dismiss': 'Dismiss',
    'home.kpi.products': 'Software licensed to you',
    'home.kpi.licenses': 'Active entitlements',
    'home.kpi.updatesLabel': 'Updates',
    'home.kpi.updates': 'Ready when you are',
    'home.ownedTitle': 'Owned software',
    'home.ownedDesc': 'Titles licensed to this account',
    'home.empty.productsTitle': 'Your software journey starts here.',
    'home.empty.productsBody': 'Browse the BeCoder ecosystem, then ask Sales or your administrator to license what you need.',
    'home.updatesTitle': 'Available updates',
    'home.updatesDesc': 'Release notes for software you already own',
    'home.empty.updatesTitle': 'Updates will appear when releases ship.',
    'home.empty.updatesBody': 'Licensed products publish signed builds here first.',
    'home.releaseNotesFallback': 'Release notes available on download.',
    'home.activityTitle': 'Recent activity',
    'home.activityDesc': 'Licenses and devices tied to your account',
    'home.activity.licenses': '{count} licenses on this account',
    'home.activity.devices': '{count} active devices',
    'home.empty.licensesTitle': 'When you purchase software, your licenses will appear here.',
    'home.empty.devicesTitle': 'Bound devices will show after your first install.',
    'home.resourcesTitle': 'Support & resources',
    'home.resourcesDesc': 'Everything a customer expects nearby',
    'home.guide.docs': 'Documentation',
    'home.guide.docsBody': 'Product guides written for the people who run the software.',
    'home.guide.support': 'Support',
    'home.guide.supportBody': 'Reach BeCoder when something needs a human.',
    'home.guide.community': 'Knowledge base',
    'home.guide.communityBody': 'Practical answers from the BeCoder knowledge base.',
    'home.guide.invoices': 'Invoices',
    'home.guide.invoicesBody': 'Commercial records appear here after agreements are confirmed.',

    'welcome.skip': 'Skip',
    'welcome.skipStep': 'Skip for now',
    'welcome.back': 'Back',
    'welcome.continue': 'Continue',
    'welcome.enter': 'Enter BeCoder',
    'welcome.learnMore': 'Learn more',
    'welcome.selectProduct': 'Choose {name} as your preferred product',
    'welcome.catalogError': 'We could not load the product catalog. Your welcome progress is safe.',
    'welcome.catalogEmpty': 'New BeCoder products will appear here as soon as they are published.',
    'welcome.saveError': 'We could not save this step. Check your connection before finishing; your account has not been changed.',
    'welcome.progress': 'Step {current} of {total}',
    'welcome.step1.eyebrow': 'Welcome',
    'welcome.step1.title': 'Welcome to BeCoder.',
    'welcome.step1.body': 'One place to discover, manage and grow with professional business software.',
    'welcome.step2.title': 'Meet the ecosystem.',
    'welcome.step2.body': 'Each product is built for a clear job. Start anywhere — the rest stays close.',
    'welcome.product.glasspos': 'Fast daily point of sale.',
    'welcome.product.ledger': 'Business financial monitoring.',
    'welcome.product.befx': 'Specialized exchange-office software.',
    'welcome.product.iqstore': 'Products, catalog, and inventory.',
    'welcome.status.available': 'Available',
    'welcome.step3.title': 'What would you like to explore first?',
    'welcome.step3.body': 'Choose a starting point. You can always come back to this home.',
    'welcome.start.products': 'Explore Products',
    'welcome.start.portal': 'Open Customer Portal',
    'welcome.start.licenses': 'Manage Licenses',
    'welcome.start.docs': 'Visit Documentation',
    'welcome.start.dashboard': 'Continue to Dashboard',
    'welcome.step4.title': 'Make it yours.',
    'welcome.step4.body': 'Optional preferences. Skip anything — BeCoder still feels like home.',
    'welcome.field.name': 'Display name',
    'welcome.field.language': 'Language',
    'welcome.field.theme': 'Theme',
    'welcome.field.timezone': 'Time zone',
    'welcome.field.notifications': 'Notifications',
    'welcome.field.photoHint': 'Your profile photo comes from Google when connected. You can refine it later in Profile.',
    'welcome.theme.dark': 'Dark',
    'welcome.theme.light': 'Light',
    'welcome.theme.system': 'System',
    'welcome.notify.products': 'Product updates',
    'welcome.notify.security': 'Security alerts',
    'welcome.notify.announcements': 'Company announcements',
    'welcome.step5.title': 'You’re ready.',
    'welcome.step5.body': 'You understand BeCoder. You know what we build. Now step into the platform.',
    'welcome.ready.title': 'You’re in.',
    'welcome.ready.body': 'Taking you to your starting point…',
    'welcome.reset': 'Replay welcome experience',
    'welcome.resetDone': 'Welcome experience reset. Sign in again to replay it.',

    'portal.customers': 'Customers',
    'portal.products': 'Products',
    'portal.releases': 'Releases',
    'portal.licenses': 'Licenses',
    'portal.devices': 'Devices',
    'portal.auditLogs': 'Audit logs',
    'portal.featureFlags': 'Feature flags',
    'portal.myProducts': 'My products',
    'portal.downloadsTitle': 'Downloads & updates',
    'portal.downloadsDesc': 'Signed, expiring download links for licensed releases',
    'portal.profile': 'Profile',
    'portal.supportTitle': 'Support & documentation',
    'portal.helpTitle': 'Help',
    'portal.helpLede': 'Get product documentation or reach BeCoder Sales for human support.',
    'portal.helpSalesBody': 'Licensing, trials, and commercial questions go through Contact Sales.',
    'portal.downloadsEmpty': 'No owned products yet — explore the catalog to start a trial or contact Sales.',
    'portal.softLaunchTitle': 'App handoff is not online yet',
    'portal.softLaunchTitleNamed': '{name} is not online for direct launch yet',
    'portal.softLaunchBody':
      'Your entitlement is active. Use downloads when releases are published, or Contact Sales for handoff.',
    'portal.softLaunchNoReleases':
      'No installer packages are published for this product yet. Contact Sales if you need a build.',
    'portal.noLicenses': 'When you purchase software, your licenses will appear here.',
    'portal.noPublishedReleases': 'Published releases will appear here when builds are ready for you.',
    'portal.noOwnedProducts': 'Your software journey starts here. Explore the catalog, then license what fits.',
    'portal.createCustomer': 'Create customer',
    'portal.createProduct': 'Create product',
    'portal.createUpload': 'Create + upload',
    'portal.issueLicense': 'Issue license',
    'portal.registerDevice': 'Register device',
    'portal.suspend': 'Suspend',
    'portal.activate': 'Activate',
    'portal.publish': 'Publish',
    'portal.revoke': 'Revoke',
    'portal.forceDeactivate': 'Force deactivate',
    'portal.remove': 'Remove',
    'portal.signOut': 'Sign out',
    'portal.download': 'Download',
    'portal.name': 'Name',
    'portal.email': 'Email',
    'portal.password': 'Password (10+)',
    'portal.slug': 'slug',
    'portal.productName': 'Name',
    'portal.tagline': 'Tagline',
    'portal.description': 'Description (markdown)',
    'portal.version': 'Version',
    'portal.changelog': 'Changelog',
    'portal.architecture': 'Architecture',
    'portal.deviceName': 'Device name',
    'portal.appVersion': 'App version',
    'portal.product': 'Product',
    'portal.key': 'Key',
    'portal.type': 'Type',
    'portal.seats': 'Seats',
    'portal.ends': 'Ends',
    'portal.lifetime': 'Lifetime',
    'portal.noFile': 'No file',
    'portal.on': 'ON',
    'portal.off': 'OFF',
    'portal.actor': 'Actor',
    'portal.action': 'Action',
    'portal.resource': 'Resource',
    'portal.time': 'Time',
    'portal.allEvents': 'All',
    'portal.authEvents': 'Auth',
    'portal.licenseEvents': 'Licenses',
    'portal.downloadEvents': 'Downloads',
    'portal.subscriptionsDesc': 'Licenses issued by BeCoder administrators after off-platform purchase confirmation.',
    'portal.subscriptionsEmpty': 'No subscriptions yet. Contact Sales to get licensed.',
    'portal.invoicesEmpty': 'Invoices appear here after commercial agreements are recorded. Contact Sales for billing details.',
    'portal.supportEmpty': 'Open documentation for product guides, or Contact Sales for commercial help.',
    'portal.profileSignedIn': 'Signed in as',
    'portal.profileRole': 'Role',
    'security.title': 'Identity & Security',
    'security.subtitle': 'Manage how you sign in to BeCoder.',
    'security.pageDesc': 'Account details and authentication security.',
    'security.methods': 'Sign-in methods',
    'security.status': 'Account status',
    'security.connected': 'Connected',
    'security.notConnected': 'Not connected',
    'security.connectGoogle': 'Connect Google',
    'security.disconnectGoogle': 'Disconnect Google',
    'security.replaceGoogle': 'Replace Google account',
    'security.emailPassword': 'Email & password',
    'security.createdVia': 'Account created via',
    'security.emailVerified': 'Verified email',
    'security.lastSignIn': 'Last successful sign-in',
    'security.lastProvider': 'Last authentication provider',
    'security.yes': 'Yes',
    'security.no': 'No',
    'security.unlockTitle': 'Confirm it’s you',
    'security.unlockBody': 'Re-authenticate before changing identity settings.',
    'security.confirmPassword': 'Confirm with password',
    'security.confirmGoogle': 'Confirm with Google',
    'security.unlocked': 'Security settings unlocked for a short time.',
    'security.reauthRequired': 'Re-authenticate before continuing.',
    'security.reauthOk': 'Identity confirmed',
    'security.reauthOkBody': 'You can update security settings for a few minutes.',
    'security.reauthFailed': 'Re-authentication failed',
    'security.addPassword': 'Add password',
    'security.changePassword': 'Change password',
    'security.newPassword': 'New password',
    'security.confirmNewPassword': 'Confirm new password',
    'security.passwordMismatch': 'Passwords must match and be at least 10 characters.',
    'security.passwordAdded': 'Password added',
    'security.passwordChanged': 'Password updated',
    'security.googleLinked': 'Google connected',
    'security.googleReplaced': 'Google account replaced',
    'security.googleDisconnected': 'Google disconnected',
    'security.needOtherMethod': 'Add a password before disconnecting Google.',
    'security.sessions': 'Active sessions',
    'security.signOutAll': 'Sign out all other devices',
    'security.thisDevice': 'This device',
    'security.unknownDevice': 'Unknown device',
    'security.lastSeen': 'Last seen',
    'security.revokeSession': 'Revoke',
    'security.sessionRevoked': 'Session revoked',
    'security.sessionsRevoked': 'Other sessions signed out',
    'security.noSessions': 'No active sessions.',
    'portal.kbSearch': 'Search help articles...',
    'portal.kbViews': '{count} views',
    'portal.blogDesc': 'Engineering updates, architectural deep dives, and product changelogs.',
    'portal.kbDesc': 'Search help desk guides, license troubleshooting, and enterprise setup tutorials.',

    'status.pageTitle': 'Service status',
    'status.pageSubtitle': 'Live signals from BeCoder platform services.',
    'status.apiLiveness': 'API liveness',
    'status.apiReadiness': 'API readiness',
    'status.database': 'Database',
    'status.marketplace': 'Marketplace',
    'status.billing': 'Billing providers',
    'status.website': 'BeCoder Website',
    'status.catalogService': 'Software Catalog',
    'status.accounts': 'Customer Account',
    'status.downloadsLicensing': 'Downloads & Licensing',
    'status.payments': 'Payments',
    'status.notAvailableYet': 'Not Available Yet',
    'status.comingSoonNeutral': 'Coming Soon',

    'about.title': 'About BeCoder',
    'about.heroTitle': 'Practical business software.',
    'about.private': 'Private',
    'about.privateDesc': 'Not a marketplace. Our products only.',
    'about.licensed': 'Licensed',
    'about.licensedDesc': 'Entitlements, devices, and releases — controlled.',
    'about.deliberate': 'Deliberate',
    'about.deliberateDesc': 'Clear pricing on product pages. Guided purchase when you are ready.',
    'about.contactTitle': 'Contact',
    'about.inquiryTitle': 'General inquiry',
    'about.messageLabel': 'Message',
    'about.messageNoted': 'Message noted',
    'about.messageBody':
      'General inquiries are handled by the sales/support mailboxes configured via environment variables.',
    'about.placeholder': 'How can we help?',
    'about.send': 'Send message',

    'pricing.starter': 'Starter',
    'pricing.starterBlurb': 'One product. A focused team.',
    'pricing.starter1': '1 software product',
    'pricing.starter2': 'Up to 3 devices',
    'pricing.starter3': 'Email support',
    'pricing.team': 'Team',
    'pricing.teamBlurb': 'Several people and devices on one account.',
    'pricing.team1': 'Multiple products',
    'pricing.team2': 'Control who can use which device',
    'pricing.team3': 'Priority support path',
    'pricing.enterprise': 'Enterprise',
    'pricing.enterpriseBlurb': 'Custom agreements. Full control.',
    'pricing.enterprise1': 'Custom user and device limits',
    'pricing.enterprise2': 'Dedicated onboarding',
    'pricing.enterprise3': 'Manual invoicing',
    'pricing.featured': 'Recommended',
    'pricing.contact': 'Contact Sales',
    'pricing.noCheckoutNote':
      'Review product pricing in the catalog, or Contact Sales for a guided commercial path.',

    'plan.name.starter': 'Starter',
    'plan.name.growth': 'Growth',
    'plan.name.enterprise': 'Enterprise',
    'plan.period.month': 'month',
    'plan.period.year': 'year',
    'plan.feature.coreAccess': 'Core {name} access',
    'plan.feature.coreAccessShort': 'Core access',
    'plan.feature.emailSupport': 'Email support',
    'plan.feature.oneOrg': '1 organization',
    'plan.feature.everythingStarter': 'Everything in Starter',
    'plan.feature.prioritySupport': 'Priority support',
    'plan.feature.moreSeats': 'More seats',
    'plan.feature.advancedReports': 'Advanced reports',
    'plan.feature.customTerms': 'Custom terms',
    'plan.feature.dedicatedSuccess': 'Dedicated success',
    'plan.feature.ssoReady': 'SSO-ready path',
    'plan.feature.slaOptions': 'SLA options',
    'plan.feature.fastCheckout': 'Fast checkout with offline resilience',
    'plan.feature.inventorySync': 'Inventory and multi-register sync',
    'plan.feature.receiptsShifts': 'Receipts, shifts, and staff roles',
    'plan.feature.tabletDesktop': 'Works on tablet and desktop',
    'plan.feature.booksParties': 'Books, parties, and period close',
    'plan.feature.doubleEntry': 'Calm double-entry workspace',
    'plan.feature.statements': 'Statements without noise',
    'plan.feature.buySell': 'Buy/sell with vault discipline',
    'plan.feature.remittances': 'Remittances and daily close',
    'plan.feature.auditExchange': 'Audit-friendly exchange ops',
    'plan.feature.imageCatalog': 'Image-ready product catalog',
    'plan.feature.ordersOps': 'Orders and store operations',
    'plan.feature.regionalRetail': 'Built for regional retail',
    'plan.feature.doubleEntryClarity': 'Double-entry clarity without clutter',
    'plan.feature.exportStatements': 'Export-ready statements',
    'plan.feature.growingTeams': 'Built for growing teams',
    'plan.feature.buySellLive': 'Buy / sell with live rates',
    'plan.feature.vaultRemittance': 'Vault and remittance workflows',
    'plan.feature.auditDailyClose': 'Audit-friendly daily close',
    'plan.feature.multiLangCounter': 'Multi-language counter UI',
    'plan.feature.catalogOrders': 'Catalog and order operations',
    'plan.feature.imagePages': 'Image-ready product pages',
    'plan.feature.roleStore': 'Role-aware store management',
    'plan.feature.designedRetail': 'Designed for regional retail',
    'plan.feature.liveScores': 'Live scores and match pulse',
    'plan.feature.fanNotifications': 'Fan notifications that matter',
    'plan.feature.sportsUi': 'Calm sports atmosphere UI',
    'plan.feature.mobileFirst': 'Mobile-first experience',
    'plan.feature.privateDist': 'Secure distribution for your software',
    'plan.feature.licensesDevices': 'Licenses, devices, and releases',
    'plan.feature.orgIdentity': 'Organization-ready identity',
    'plan.feature.ecosystem': 'Built for the BeCoder ecosystem',
    'plan.category.application': 'Application',
    'plan.category.pos': 'Point of Sale',
    'plan.category.finance': 'Finance',
    'plan.category.exchange': 'Exchange',
    'plan.category.pharmacy': 'Pharmacy',
    'plan.category.commerce': 'Commerce',
    'plan.category.sports': 'Sports',
    'plan.category.platform': 'Platform',
    'status.active': 'Active',
    'status.trialing': 'Trialing',
    'status.trial': 'Trial',
    'status.canceled': 'Canceled',
    'status.cancelled': 'Canceled',
    'status.past_due': 'Past due',
    'status.incomplete': 'Incomplete',
    'status.expired': 'Expired',
    'status.pending': 'Pending',
    'status.paid': 'Paid',
    'status.open': 'Open',
    'status.revoked': 'Revoked',
    'status.suspended': 'Suspended',
    'hub.orgNameDefault': "{name}'s Organization",
    'hub.launchFailedShort': 'Launch failed',
    'portal.provider.fastpay': 'FastPay',
    'portal.provider.manual': 'Manual',
    'portal.provider.none': 'None',
    'portal.provider.google': 'Google',
    'content.seed.glasspos.tagline': 'Fast, reliable point of sale for modern businesses.',
    'content.seed.glasspos.description':
      'GlassPOS is BeCoder’s point-of-sale application — fast checkout, inventory clarity, and offline-ready registers.',
    'content.seed.glasspos.descriptionShort': 'GlassPOS is BeCoder’s point-of-sale application.',
    'content.seed.ledger.tagline': 'Books that stay quiet and correct.',
    'content.seed.ledger.description':
      'Ledger brings double-entry finance into a calm workspace — parties, periods, and statements without noise.',
    'content.seed.ledger.descriptionShort': 'Ledger brings double-entry finance into a calm workspace.',
    'content.seed.befx.tagline': 'Exchange operations with vault discipline.',
    'content.seed.befx.description':
      'BeFX handles buy/sell, vaults, and remittances with audit-friendly daily close for exchange desks.',
    'content.seed.befx.descriptionShort': 'BeFX handles buy/sell, vaults, and remittances.',
    'content.seed.iqStore.tagline': 'Commerce catalog built for regional retail.',
    'content.seed.iqStore.description':
      'IQ Store manages products, orders, and store operations with an image-ready catalog experience.',
    'content.seed.iqStore.descriptionShort': 'IQ Store manages products, orders, and store operations.',
    'content.seed.aura.tagline': 'Live sports pulse for fans.',
    'content.seed.aura.description':
      'Aura Sport delivers match energy with calm UI — scores, alerts, and mobile-first sports atmosphere.',

    'docs.quickstart': 'Quickstart CLI',
    'docs.auth': 'Authentication',
    'docs.sdkC': 'C / C++ Embed',
    'docs.offline': 'Air-gapped verify',
    'docs.title': 'Documentation',
    'docs.products': 'Product documentation',
    'docs.productsBody': 'Guides and product details from the current BeCoder software catalog.',
    'docs.openProductDocs': 'Open documentation',
    'docs.viewProduct': 'View product',
    'docs.developers': 'Product help',
    'docs.subtitle':
      'Help and documentation for BeCoder products — organized by software, in plain customer language.',
    'docs.rust': 'Rust SDK',
    'docs.rustTitle': 'Rust SDK notes',
    'docs.cTitle': 'C / C++ embed notes',
    'docs.offlineTitle': 'Offline activation',

    'status.operations': 'Status',
    'status.notReady': 'Not ready ({reason})',
    'status.databaseNamed': 'Database ({name})',
    'status.sampleAt': 'Checked · {time}',
    'portal.kbEmpty':
      'Help articles are not published yet. Use product documentation or Contact Sales for assistance.',
    'portal.kbEmptyDocs': 'Browse product documentation from the BeCoder catalog.',
    'portal.kbEmptySales': 'Reach BeCoder through Contact Sales while support mailboxes are prepared.',
    'docs.quickstartPending':
      'Install and activation guides will appear here when product release packages are published.',
    'docs.sdkPending':
      'SDK integration notes will be published with each product’s licensing documentation.',
    'docs.offlinePending':
      'Air-gapped activation steps will be documented when offline licensing packages are available.',
    'status.liveChecks':
      'A quick look at BeCoder services customers use — website, software catalog, account, downloads, and payments.',

    'soon.organizations': 'Organizations',
    'soon.organizationsBody': 'Use Customers for account management in this cycle.',
    'soon.moderation': 'Moderation',
    'soon.moderationBody': 'Marketplace moderation is not part of this cycle.',
    'soon.organizationBody': 'Organization management is deferred.',
    'soon.checkout': 'Checkout',
    'soon.checkoutBody': 'Payment providers are not integrated in this cycle.',
    'soon.cartBody': 'Checkout is disabled until billing is enabled.',
    'soon.developerBody': 'Third-party publishing is disabled.',
    'soon.wishlist': 'Wishlist',
    'account.title': 'My Account',
    'account.subtitle': 'Manage your administrator profile and password.',
    'account.profile': 'Profile information',
    'account.details': 'Account details',
    'account.displayName': 'Display name',
    'account.role': 'Role',
    'account.save': 'Save changes',
    'account.saved': 'Profile saved',
    'account.savedBody': 'Your display name was updated.',
    'account.nameTooShort': 'Display name must be at least 2 characters.',
    'account.passwordHelp':
      'Confirm your current password, then set a new one. Other sessions will be signed out after a password change.',
    'account.passwordChangedBody': 'Use your new password the next time you sign in.',
    'theme.light': 'Light',
    'theme.dark': 'Dark',
    'theme.system': 'System',
    'nav.language': 'Language',
    'nav.theme': 'Theme',
    'nav.menu': 'Menu',

    'portal.details': 'Details',
    'portal.licensePrefix': 'License:',
    'portal.lastSeen': 'Last seen {date}',
    'portal.register': 'Register',
    'portal.flagsNote':
      'Marketplace and billing stay rejected by API policy in this cycle even if toggled. Env gates also apply.',
    'portal.noInAppPayments': 'No in-app payments',
    'portal.supportDeferred':
      'Ticket workflows are deferred. Use product documentation and support links below.',
    'portal.invoicesDeferred':
      'Invoice history will appear here when a billing provider is connected.',
    'portal.invoicesNoGateway':
      'No payment gateway in this release. Purchases go through Contact Sales.',
    'portal.auditPlatform': 'Platform',
    'portal.seatsCount': 'seats {count}',
    'portal.endsPrefix': 'ends {date}',
    'portal.signedOut': 'Signed out',
    'portal.sessionCleared': 'Session cleared',
    'command.paletteAria': 'Command palette',

    'license.modalTitle': 'License entitlement',
    'license.modalBody':
      'Self-service marketplace activation is disabled. Licenses are issued by platform administrators and appear under My Licenses after sign-in.',
    'license.placeholder': 'License key (reference only)',
    'license.unavailableTitle': 'Not available',
    'license.unavailableBody': 'Contact your administrator to issue or manage licenses.',

    'ai.modalBody':
      'External AI providers are not integrated in this cycle. Use documentation and your customer portal for product and license help.',

    'command.home': 'Home',
    'command.signIn': 'Sign in',
    'command.adminPortal': 'Admin portal',
    'command.customerPortal': 'Customer portal',
    'command.marketplaceSoon': '{label} (Coming Soon)',

    'concept.pos.category': 'Modern retail point of sale',
    'concept.pos.checkout': 'Point of sale',
    'concept.pos.order': 'Current order',
    'concept.pos.total': 'Total',
    'concept.ledger.category': 'Financial monitoring and business oversight',
    'concept.ledger.overview': 'Financial overview',
    'concept.ledger.balance': 'Net balance',
    'concept.ledger.income': 'Income',
    'concept.ledger.expenses': 'Expenses',
    'concept.befx.category': 'Currency exchange operations',
    'concept.befx.rates': 'Exchange desk',
    'concept.befx.pair': 'Currency pair',
    'concept.befx.buy': 'Buy',
    'concept.befx.sell': 'Sell',
    'concept.store.category': 'Product catalog and inventory management',
    'concept.store.inventory': 'Product inventory',
    'concept.store.inStock': 'In stock',
    'concept.store.lowStock': 'Low stock',

    'product.status.active': 'Published',
    'product.status.hidden': 'Beta',
    'product.status.coming_soon': 'Coming Soon',
    'product.status.archived': 'Archived',
    'common.dayAgo': '1 day ago',
    'release.policy.optional': 'Optional',
    'release.policy.mandatory': 'Mandatory',
    'content.seed.studio.tagline': 'Private client for BeCoder software distribution',
    'content.seed.studio.description':
      'BeCoder Studio is our flagship application for licensed access across Web/PWA, Windows, Android APK, and iOS/PWA.',
    'content.seed.studio.changelog': 'Initial private release of BeCoder Studio for Windows x64.',
    'product.detail.platforms': 'Platforms',
    'product.detail.requirements': 'System requirements',
    'product.detail.releases': 'Releases',
    'common.notFound': 'Not found',
    'portal.releaseFile': 'Release file',
    'common.retry': 'Retry',

    'cc.controlCenter': 'Control Center',
    'cc.dashboardTitle': 'Operations overview',
    'cc.dashboardIntro': 'Live platform status from real entitlements, catalog, and audit data.',
    'cc.rolePlatformAdmin': 'Platform Admin',
    'cc.collapseSidebar': 'Collapse',
    'cc.expandSidebar': 'Expand sidebar',
    'cc.apiOperational': 'API operational',
    'cc.apiUnavailable': 'API unavailable',
    'cc.loadFailed': 'Could not load Control Center overview. Try again.',
    'cc.kpis': 'Operational KPIs',
    'cc.kpi.customers': 'Customers',
    'cc.kpi.activeLicenses': 'Active licenses',
    'cc.kpi.activeTrials': 'Active trials',
    'cc.kpi.activeSubs': 'Active subscriptions',
    'cc.kpi.activeDevices': 'Active devices',
    'cc.kpi.publishedProducts': 'Published products',
    'cc.attentionTitle': 'Attention required',
    'cc.attentionNone': 'No critical items.',
    'cc.attention.expiringTrials': '{count} trials expiring within 7 days',
    'cc.attention.suspendedLicenses': '{count} suspended licenses',
    'cc.attention.revokedLicenses': '{count} revoked licenses',
    'cc.attention.expiredSubs': '{count} expired subscriptions',
    'cc.attention.missingPricing': '{name}: missing pricing',
    'cc.attention.incompleteLocales': '{name}: incomplete locale content',
    'cc.attention.noRelease': '{name}: no published release package',
    'cc.attention.problemMissingPricing': 'Missing pricing',
    'cc.attention.problemIncompleteLocales': 'Incomplete locale content',
    'cc.attention.problemNoRelease': 'No published release',
    'cc.attention.openTrials': 'Review trials',
    'cc.attention.openLicenses': 'Manage licenses',
    'cc.attention.openSubs': 'Review subscriptions',
    'cc.attention.openPricing': 'Open pricing',
    'cc.attention.openContent': 'Open content',
    'cc.attention.openReleases': 'Manage releases',
    'cc.kpi.hint.directory': 'Customer directory',
    'cc.kpi.hint.entitlements': 'Valid licenses',
    'cc.kpi.hint.trials': 'Currently running',
    'cc.kpi.hint.billing': 'Active billing',
    'cc.kpi.hint.devices': 'Bound devices',
    'cc.kpi.hint.catalog': 'Live catalog',
    'cc.noActivity': 'No recent Admin activity.',
    'cc.activity.system': 'system',
    'cc.activity.aProduct': 'a product',
    'cc.activity.aLicense': 'a license',
    'cc.activity.aCustomer': 'a customer',
    'cc.activity.byActor': 'by {actor}',
    'cc.activity.mediaUploaded': 'Media uploaded to {name}',
    'cc.activity.mediaScreenshot': 'Screenshot uploaded to {name}',
    'cc.activity.mediaIcon': 'Icon uploaded to {name}',
    'cc.activity.mediaHero': 'Hero image uploaded to {name}',
    'cc.activity.mediaRemoved': 'Media removed from {name}',
    'cc.activity.mediaScreenshotRemoved': 'Screenshot removed from {name}',
    'cc.activity.licenseIssued': 'License issued for {name}',
    'cc.activity.licenseIssuedTo': 'License issued to {name}',
    'cc.activity.licenseSuspended': 'License suspended ({name})',
    'cc.activity.licenseRevoked': 'License revoked ({name})',
    'cc.activity.licenseActivated': 'License activated ({name})',
    'cc.activity.trialExtended': '{name} trial extended',
    'cc.activity.trialForceGrant': '{name} trial force-granted',
    'cc.activity.trialCanceled': '{name} trial canceled',
    'cc.activity.priceUpdated': '{name} {code} price updated',
    'cc.activity.planCreated': '{name} {code} plan created',
    'cc.activity.planToggled': '{name} {code} plan {state}',
    'cc.activity.productCore': '{name} product status updated',
    'cc.activity.commercialFlags': '{name} commercial flags updated',
    'cc.activity.productPublished': '{name} commercial content published',
    'cc.activity.draftSaved': '{name} draft saved',
    'cc.activity.customerSuspended': 'Customer suspended: {name}',
    'cc.activity.customerActivated': 'Customer activated: {name}',
    'cc.activity.releasePublished': 'Release published for {name}',
    'cc.activity.releaseUnpublished': 'Release unpublished for {name}',
    'cc.activity.settingsUpdated': 'Platform settings updated',
    'cc.time.justNow': 'Just now',
    'cc.time.minutesAgo': '{count} min ago',
    'cc.time.hoursAgo': '{count} hours ago',
    'cc.time.yesterday': 'Yesterday',
    'cc.time.daysAgo': '{count} days ago',
    'cc.ready.content': 'Content',
    'cc.ready.pricing': 'Pricing',
    'cc.ready.media': 'Media',
    'cc.ready.trial': 'Trial',
    'cc.ready.distribution': 'Distribution',
    'cc.ready.ok': 'Ready',
    'cc.ready.warn': 'Needs attention',
    'cc.ready.na': 'N/A',
    'cc.empty.devices': 'No active devices yet.',
    'cc.empty.downloads': 'No download activity yet.',
    'cc.empty.activity': 'No recent Admin activity.',
    'cc.settings.savedState': 'Saved',
    'cc.settings.saving': 'Saving…',
    'cc.settings.saveFailed': 'Save failed',
    'cc.action.findCustomer': 'Find customer',
    'cc.action.issueLicense': 'Issue license',
    'cc.action.createRelease': 'Create release',
    'cc.action.manageProducts': 'Manage products',
    'cc.action.uploadMedia': 'Upload media',
    'cc.action.downloadAudit': 'Download audit',
    'cc.recentActivity': 'Recent activity',
    'cc.viewAuditLog': 'View audit log',
    'cc.quickActions': 'Quick actions',
    'cc.nav.overview': 'Overview',
    'cc.nav.customers': 'Customers',
    'cc.nav.catalog': 'Catalog',
    'cc.nav.entitlements': 'Entitlements',
    'cc.nav.access': 'Access',
    'cc.nav.distribution': 'Distribution',
    'cc.nav.trust': 'Trust & operations',
    'cc.nav.settings': 'Settings',
    'cc.products.intro': 'Manage catalog products and commercial readiness.',
    'cc.products.create': 'Create Product',
    'cc.products.manage': 'Manage',
    'cc.access.issueLicense': '+ Issue License',
    'cc.settings.advanced': 'Advanced',
    'cc.settings.advancedIntro': 'Technical and account destinations.',
    'cc.settings.accountHint': 'Your admin account',
    'cc.settings.flagsHint': 'Feature flags',
    'cc.settings.auditHint': 'Security audit log',
    'cms.hub.overview': 'Overview',
    'cms.hub.content': 'Content',
    'cms.hub.plans': 'Pricing',
    'cms.hub.trial': 'Trial',
    'cms.hub.media': 'Media',
    'cms.hub.distribution': 'Distribution',
    'cc.settings.identity': 'Platform identity',
    'cc.settings.contact': 'Contact',
    'cc.settings.about': 'About',
    'cc.settings.social': 'Social',
    'cc.settings.footer': 'Footer',
    'cc.customerSince': 'Customer since',
    'cc.showMetadata': 'Show details',
    'cc.hideMetadata': 'Hide details',
    'cc.unsaved': 'Unsaved changes',
  },

  ar: {
    'nav.about': 'من نحن',
    'nav.openPortal': 'فتح البوابة',
    'nav.customerPortal': 'بوابة العميل',
    'footer.platform': 'المنصة',
    'footer.contact': 'التواصل',
    'footer.privateDistribution': 'توزيع آمن للبرامج',
    'common.home': 'الرئيسية',
    'common.comingSoon': 'قريبًا',
    'common.backHome': 'العودة للرئيسية',
    'a11y.skipToContent': 'تخطي إلى المحتوى',
    'common.error': 'خطأ',
    'common.failed': 'فشل',
    'common.loading': 'جارٍ التحميل',
    'common.tryAgain': 'حاول مجدداً',
    'common.today': 'اليوم',
    'common.earlier': 'سابقًا',
    'common.yesterday': 'أمس',
    'common.daysAgo': 'قبل {count} يومًا',
    'common.dateUnavailable': 'التاريخ غير متاح',
    'common.version': 'الإصدار',
    'common.status': 'الحالة',
    'common.published': 'منشور',
    'common.draft': 'مسودة',
    'common.beta': 'تجريبي',
    'common.archived': 'مؤرشف',
    'common.open': 'فتح',
    'common.manage': 'إدارة',
    'common.viewAll': 'عرض الكل',
    'common.fullLog': 'السجل الكامل',
    'common.confirm': 'تأكيد',
    'status.checking': 'جارٍ التحقق',
    'status.operational': 'يعمل',
    'status.unavailable': 'غير متاح',
    'status.down': 'متوقف',
    'status.disabled': 'معطّل',
    'status.stable': 'مستقر',
    'status.inProgress': 'قيد الإعداد',
    'status.awaitingRelease': 'بانتظار الإصدار',

    'landing.softwareEyebrow': 'البرمجيات',
    'landing.softwareTitle': 'برمجيات من BeCoder',
    'landing.softwareLede':
      'هذه المنتجات التي تقدّمها BeCoder. افتح أي بطاقة لمراجعة المزايا واللقطات والأسعار.',
    'landing.catalogEmpty': 'الكتالوج غير متاح — شغّل واجهة البرمجة أو سجّل الدخول لعرض البرمجيات المرخّصة.',
    'landing.bannerLabel': 'رسائل BeCoder',
    'landing.banner.1.title': 'نبني برمجيات مخصصة لأعمالك.',
    'landing.banner.1.body': 'من التشغيل إلى الملكية — برمجيات مصمّمة حول طريقة عملك.',
    'landing.banner.2.title': 'اربط جميع فروعك في نظام واحد.',
    'landing.banner.2.body': 'هوية منصة واحدة عبر المواقع والفرق والمنتجات.',
    'landing.banner.3.title': 'حلول سحابية للشركات النامية.',
    'landing.banner.3.body': 'تطبيقات BeCoder مرخّصة تنمو مع مؤسستك.',
    'landing.banner.4.title': 'منصة واحدة لكل عمليات أعمالك.',
    'landing.banner.4.body': 'استكشف منتجات الكتالوج، ابدأ تجربة، وأطلق من بوابتك.',
    'landing.banner.5.title': 'حلول بمستوى المؤسسات للمنظمات.',
    'landing.banner.5.body': 'توزيع آمن للبرامج، وتراخيص مدروسة، وإدارة تشغيل موثوقة.',
    'landing.banner.6.title': 'تحتاج نظاماً مخصصاً؟ نحن نبنيه.',
    'landing.banner.6.body': 'أخبر المبيعات بما تحتاجه — BeCoder يصمّم ويسلّم البرمجيات.',
    'landing.banner.cta.sales': 'تواصل للمبيعات',
    'landing.banner.cta.custom': 'اطلب برمجيات مخصصة',
    'landing.banner.cta.explore': 'استكشف الحلول',
    'landing.banner.cta.trial': 'ابدأ تجربة مجانية',
    'landing.manageEyebrow': 'الحلول',
    'landing.manageTitle': 'ماذا تريد أن تدير؟',
    'landing.manageLede':
      'اختر نوع عملك. إذا كان البرنامج جاهزًا نفتح صفحة المنتج. وإلا ستظهر قريبًا.',
    'landing.need.talkSales': 'تواصل للمبيعات',
    'landing.need.customPath': 'مسار مخصص',
    'landing.need.retail.title': 'ميني ماركت / سوبر ماركت',
    'landing.need.retail.body':
      'نقاط البيع لميني ماركت والسوبر ماركت — مبيعات ومنتجات ودفع يومي مع GlassPOS.',
    'landing.need.restaurant.title': 'مطعم',
    'landing.need.restaurant.body':
      'برامج المطاعم من BeCoder قريبًا.',
    'landing.need.pharmacy.title': 'صيدلية',
    'landing.need.pharmacy.body':
      'برامج الصيدليات من BeCoder قريبًا.',
    'landing.need.exchange.title': 'مكتب صرافة',
    'landing.need.exchange.body':
      'شراء وبيع العملات والأسعار وحركة نقد الخزنة لعمليات مكاتب الصرافة.',
    'landing.need.company.title': 'شركة',
    'landing.need.company.body':
      'برامج تشغيل الشركات من BeCoder قريبًا.',
    'landing.need.warehouse.title': 'مستودع',
    'landing.need.warehouse.body': 'برامج المستودعات من BeCoder قريبًا.',
    'landing.need.factory.title': 'مصنع',
    'landing.need.factory.body': 'برامج المصانع من BeCoder قريبًا.',
    'landing.need.clinic.title': 'عيادة',
    'landing.need.clinic.body': 'برامج العيادات من BeCoder قريبًا.',
    'landing.need.institute.title': 'معهد',
    'landing.need.institute.body': 'برامج المعاهد من BeCoder قريبًا.',
    'landing.need.custom.title': 'برمجيات مخصصة',
    'landing.need.custom.body': 'أخبرنا بما تحتاجه — نصمّم النظام ونبنيه.',
    'landing.journeyEyebrow': 'كيف تعمل',
    'landing.journeyTitle': 'من الاكتشاف إلى تشغيل البرمجيات.',
    'landing.journeyLede':
      'BeCoder منصة الشركة. المنتجات في الكتالوج؛ التراخيص والتنزيلات والتشغيل في بوابتك.',
    'landing.journey1Title': 'استكشف',
    'landing.journey1Body': 'تصفّح كتالوج البرمجيات. افهم دور كل منتج قبل الالتزام.',
    'landing.journey2Title': 'اختر مسارك',
    'landing.journey2Body': 'راجع الأسعار في صفحة المنتج، ثم اشترك أو ابدأ تجربة أو تواصل مع المبيعات عندما تكون جاهزاً.',
    'landing.journey3Title': 'امتلك في البوابة',
    'landing.journey3Body': 'سجّل الدخول لإدارة المنتجات والمفاتيح والأجهزة والفواتير والدعم في مكان واحد.',
    'landing.journey4Title': 'شغّل وحدّث',
    'landing.journey4Body': 'افتح التطبيقات المستحقة واسحب الإصدارات الموقّعة عند صدور بنى جديدة.',
    'landing.trustEyebrow': 'لماذا BeCoder',
    'landing.trustTitle': 'توزيع آمن للبرامج يمكنك الوثوق به.',
    'landing.showcaseTitle': 'شاهد كل منتج في مكان واحد.',
    'landing.showcaseLede':
      'كتالوج البرمجيات يعرض منتجات BeCoder بوضوح — المزايا واللقطات والأسعار قبل إنشاء الحساب.',
    'landing.exploreShowcase': 'استكشف البرامج',
    'landing.exploreSoftware': 'استكشف البرامج',
    'landing.heroCatalogCaption': 'برمجيات في كتالوج BeCoder',
    'landing.portalEyebrow': 'بوابة العميل',
    'landing.portalTitle': 'كل ما بعد الترخيص.',
    'landing.portalLede':
      'بعد الاستحقاق، تجمع BeCoder الملكية والتنزيلات والمساعدة — لا تتوزّع على أدوات متفرقة.',
    'landing.portalLicenses': 'التراخيص والأجهزة',
    'landing.portalLicensesBody': 'الاستحقاقات النشطة وحدود المقاعد والأجهزة المرتبطة تبقى تحت سيطرتك.',
    'landing.portalDownloads': 'التنزيلات والتحديثات',
    'landing.portalDownloadsBody': 'روابط تنزيل موقّعة ومنتهية للإصدارات المرخّصة تظهر عند جاهزية البناء.',
    'landing.portalSupport': 'الدعم والسجلات',
    'landing.portalSupportBody': 'التذاكر والتوثيق والفواتير بجانب البرمجيات التي تشغّلها.',
    'landing.commercialEyebrow': 'المسار التجاري',
    'landing.commercialTitle': 'جاهز عندما تكون أنت جاهزاً.',
    'landing.commercialBody':
      'استكشف المنتجات والأسعار أولاً. عندما تقرّر، تابع عبر BeCoder — اشترك أو ابدأ تجربة أو تواصل مع المبيعات لمسار موجّه.',
    'landing.resourcesEyebrow': 'الموارد',
    'landing.resourcesTitle': 'أدلة وصحة المنصة.',
    'landing.resourcesLede': 'اقرأ كيف تعمل المنتجات، راقب حالة النظام، أو تعرّف على أسلوب BeCoder.',
    'landing.resourceDocs': 'أدلة المنتج لمن يشغّل البرمجيات.',
    'landing.resourceKb': 'إجابات عملية من قاعدة معرفة BeCoder.',
    'landing.resourceStatus': 'صحة المنصة والخدمات لحظياً.',
    'landing.resourceAbout': 'من نحن وكيف يعمل التوزيع الآمن للبرامج.',
    'landing.closeTitle': 'جاهز للخطوة التالية.',
    'landing.closeLede':
      'استكشف الكتالوج، تواصل مع المبيعات، أو سجّل الدخول إلى بوابتك — الرحلة نفسها تستمر بعد التذييل.',

    'catalog.eyebrow': 'البرامج',
    'catalog.title': 'كتالوج البرمجيات',
    'catalog.lede':
      'اكتشف برمجيات BeCoder — راجع المزايا واللقطات والأسعار قبل إنشاء حساب.',
    'catalog.viewDetails': 'عرض التفاصيل',
    'catalog.viewAll': 'عرض جميع البرامج',
    'catalog.emptyTitle': 'كتالوج البرمجيات',
    'catalog.empty': 'ستظهر برمجيات BeCoder المنشورة هنا عندما تكون جاهزة للاكتشاف.',
    'catalog.error': 'تعذّر تحميل كتالوج البرمجيات. تحقق من اتصالك وحاول مجدداً.',
    'catalog.fromPrice': 'من {price}',
    'catalog.fromMonth': 'من {price}/شهر',
    'catalog.fromYear': 'من {price}/سنة',
    'catalog.fromLifetime': 'من {price}',
    'catalog.lifetimeLabel': 'مدى الحياة',
    'catalog.trialAvailable': 'تجربة متاحة',
    'catalog.statusAvailable': 'متاح الآن',
    'guide.categories.title': 'كيف تبدأ؟',
    'guide.categories.body':
      'اختر البرنامج المناسب لعملك، وراجع الصور والمزايا، ثم جرّبه واشترك إذا ناسبك.',
    'guide.catalog.title': 'كيف تبدأ؟',
    'guide.catalog.body':
      'اختر البرنامج المناسب لعملك، وراجع الصور والمزايا، ثم جرّبه واشترك إذا ناسبك.',
    'guide.product.title': 'هل يناسب احتياجك؟',
    'guide.product.body':
      'راجع الصور والمزايا أولاً، ويمكنك تجربة البرنامج قبل الاشتراك.',
    'guide.trial.title': 'جرّبه أولاً',
    'guide.trial.body':
      'لن تحتاج إلى الدفع الآن. سجّل الدخول وابدأ التجربة.',
    'guide.docs.title': 'تحتاج مساعدة؟',
    'guide.docs.body':
      'اختر منتجاً من القائمة. ستظهر الأدلة هنا عند نشرها، كما تعرض صفحة المنتج الصور والأسعار.',
    'docs.productHelp': 'مساعدة المنتجات',
    'docs.gettingStarted': 'البداية',
    'docs.gettingStartedBody':
      'BeCoder مكان تتعرّف فيه على برامج عملك وتمتلكها. اتبع هذه الخطوات البسيطة.',
    'docs.helpTopics': 'مواضيع المساعدة',
    'docs.helpComingSoon': 'ستظهر مقالات المساعدة هنا عند نشرها لكل منتج.',
    'docs.stepExplore': 'افتح كتالوج البرامج واختر برنامجًا يناسب عملك.',
    'docs.stepReview': 'اقرأ ماذا يفعل، تصفّح الصور الحقيقية، وتحقق من السعر.',
    'docs.stepTry': 'جرّبه عند التوفر، ثم اشترك أو اشترِ إذا ناسبك.',
    'catalog.statusComingSoon': 'قريباً',
    'catalog.noScreenshots': 'ستظهر لقطات الشاشة هنا عند نشر مرئيات المنتج.',

    'market.comingTitle': 'كتالوج البرمجيات',
    'market.comingBody':
      'استكشف البرمجيات التي تبنيها وتدعمها BeCoder. افتح المنتج لمراجعة المزايا واللقطات والأسعار.',
    'market.catalogError': 'تعذّر تحميل مكتبة المنتجات. تحقق من اتصالك وحاول مجدداً.',
    'showcase.eyebrow': 'البرامج',
    'showcase.title': 'برمجيات BeCoder',
    'showcase.lede': 'كل منتج منشور في كتالوج BeCoder — واضح وتجاري وجاهز للاستكشاف.',
    'showcase.carouselLabel': 'عرض التطبيقات',
    'showcase.appsRail': 'اختر تطبيقاً',
    'showcase.explore': 'عرض التفاصيل',
    'showcase.subscribe': 'اشترك',
    'showcase.subscribeSoon': 'خيارات الاشتراك تظهر عند تهيئتها لهذا المنتج',
    'showcase.startTrial': 'ابدأ تجربة {days} يوماً',
    'showcase.trialSoon': 'تظهر التجربة عندما تكون مفعّلة لهذا المنتج',
    'showcase.launch': 'تشغيل',
    'showcase.openApp': 'فتح',
    'showcase.launchPlaceholder': 'تشغيل التطبيق يأتي مع مركز المنتجات',
    'showcase.comingSoon': 'قريباً',
    'showcase.prev': 'التطبيق السابق',
    'showcase.next': 'التطبيق التالي',
    'showcase.navHint': 'استخدم الأسهم أو السحب أو جهاز التحكم.',
    'showcase.highlights': 'أبرز المزايا',
    'showcase.plans': 'الخطط',
    'showcase.plansPlaceholder': 'تظهر الأسعار العامة عند نشر خطط هذا المنتج.',
    'showcase.screenshots': 'لقطات الشاشة',
    'showcase.openScreenshot': 'فتح اللقطة',
    'lightbox.title': 'معاينة الصورة',
    'lightbox.prev': 'اللقطة السابقة',
    'lightbox.next': 'اللقطة التالية',
    'lightbox.position': '{current} / {total}',
    'showcase.systemRequirements': 'متطلبات النظام',
    'showcase.systemRequirementsSoon': 'المتطلبات التفصيلية تُنشر مع كل إصدار.',
    'showcase.releaseNotes': 'ملاحظات الإصدار',
    'showcase.releaseNotesSoon': 'ستظهر ملاحظات الإصدار هنا عند النشر.',
    'showcase.backToShowcase': 'العودة إلى البرمجيات',
    'showcase.catalogError': 'تعذّر تحميل التطبيقات. تحقق من اتصالك وحاول مجدداً.',
    'showcase.empty': 'لا توجد برمجيات منشورة في الكتالوج بعد.',
    'showcase.notFound': 'هذا المنتج غير متاح.',
    'showcase.about': 'نبذة',
    'hub.startTrial': 'ابدأ التجربة المجانية',
    'hub.startTrialLede':
      'ابدأ تجربة مجانية لمدة 3 أيام لـ {slug}. لا تحتاج إلى وسيلة دفع، وتبدأ المدة عند التفعيل فقط.',
    'hub.confirmTrial': 'ابدأ التجربة المجانية',
    'hub.noPaymentRequired': 'لا تحتاج إلى وسيلة دفع',
    'hub.trialDaysBadge': 'تجربة مجانية لمدة 3 أيام',
    'hub.trialReady': 'تجربة {name} جاهزة',
    'hub.daysRemaining': 'متبقي {count} أيام',
    'hub.dayRemaining': 'متبقي يوم واحد',
    'hub.trialEndsToday': 'تنتهي التجربة اليوم',
    'hub.trialExpiredTitle': 'انتهت تجربتك المجانية',
    'hub.trialExpiredBody':
      'انتهت تجربتك المجانية. بياناتك محفوظة. اشترك للمتابعة من حيث توقفت.',
    'hub.dataChoiceTitle': 'تم تفعيل اشتراكك. كيف تريد أن تبدأ؟',
    'hub.dataChoiceKeep': 'الاحتفاظ ببيانات الفترة التجريبية',
    'hub.dataChoiceFresh': 'بدء استخدام جديد',
    'hub.dataChoiceFreshConfirmTitle': 'بدء استخدام جديد؟',
    'hub.dataChoiceFreshConfirmBody':
      'سيؤدي ذلك إلى إزالة بيانات التشغيل في GlassPOS التي أُنشئت أثناء التجربة نهائياً. يبقى حساب BeCoder والاشتراك والترخيص فعّالين. لا يمكن التراجع عن هذا الإجراء.',
    'hub.dataChoiceKeepDone': 'سيتم الاحتفاظ ببيانات التجربة. تابع من حيث توقفت.',
    'hub.dataChoiceFreshDone': 'مساحة عملك الجديدة جاهزة.',
    'hub.dataChoiceDeferred':
      'بدء استخدام جديد غير متاح بأمان لهذا المنتج بعد. لم تُحذف بياناتك.',
    'hub.dataChoiceFreshUnavailable':
      'سيُتاح بدء استخدام جديد عندما يدعم هذا المنتج إعادة ضبط تشغيلية آمنة. حتى ذلك الحين، احتفظ ببيانات التجربة.',
    'hub.dataChoicePending': 'اختر كيف تريد المتابعة مع بيانات التجربة',
    'hub.trialBadge': 'تجربة',
    'hub.trialExpiredBadge': 'انتهت التجربة',
    'hub.paidBadge': 'مدفوع',
    'hub.signInRequired': 'سجّل الدخول لبدء التجربة.',
    'hub.orgName': 'اسم المنظمة',
    'hub.trialEnds': 'تنتهي التجربة في {date}.',
    'hub.trialFailed': 'تعذّر بدء التجربة.',
    'hub.launchFailed': 'تعذّر تشغيل التطبيق.',
    'hub.upgrade': 'ترقية الخطة',
    'hub.upgradeLede': 'اختر خطة لهذا البرنامج. سيظهر الدفع الإلكتروني هنا عند تفعيله.',
    'hub.upgradeNeedProduct': 'اختر منتجاً من التطبيقات أو منتجاتي أولاً.',
    'hub.plansLoadFailed': 'تعذّر تحميل الخطط.',
    'hub.checkoutFailed': 'تعذّر بدء الدفع.',
    'pay.title': 'الدفع',
    'pay.redirecting': 'جارٍ تحويلك بأمان إلى FastPay لإتمام الدفع.',
    'pay.checking': 'جارٍ تأكيد الدفع…',
    'pay.successTitle': 'تم الدفع بنجاح',
    'pay.successBody': 'اكتمل الدفع بنجاح. أصبح برنامجك متاحاً الآن في حسابك.',
    'pay.pendingTitle': 'الدفع قيد التأكيد',
    'pay.pendingBody':
      'ما زلنا نؤكد الدفع. ستظهر برمجياتك بعد اكتمال التحقق — لا حاجة للدفع مرة أخرى.',
    'pay.failedTitle': 'فشل الدفع',
    'pay.failedBody': 'تعذّر إتمام الدفع. يمكنك المحاولة مجدداً أو التواصل مع المبيعات.',
    'pay.cancelledTitle': 'تم إلغاء الدفع',
    'pay.cancelledBody': 'أُلغي الدفع ولم يُخصم أي مبلغ. يمكنك المحاولة لاحقاً.',
    'pay.openSoftware': 'فتح برمجياتي',
    'pay.goWorkspace': 'الذهاب إلى مساحة العميل',
    'pay.refresh': 'تحقق مجدداً',
    'pay.tryAgain': 'إعادة المحاولة',
    'pay.missingPayment': 'مرجع الدفع غير موجود.',
    'pay.confirmFailed': 'تعذّر تأكيد حالة الدفع.',
    'admin.reconcile': 'مطابقة',
    'admin.paymentsIntro': 'مراجعة مدفوعات FastPay ومطابقة التفعيل عند الانقطاع.',
    'hub.billingOff': 'الدفع الإلكتروني غير مفعّل بعد. تواصل مع المبيعات، أو ابدأ تجربة عند التوفر.',
    'hub.subscriptionsIntro': 'التجارب والاشتراكات لمنظمتك النشطة.',
    'hub.subscriptionsLoadFailed': 'تعذّر تحميل الاشتراكات.',
    'hub.trials': 'التجارب',
    'hub.plans': 'الاشتراكات',
    'hub.noTrials': 'لا توجد تجارب بعد.',
    'hub.noSubscriptions': 'لا توجد اشتراكات بعد.',
    'hub.invoicesIntro': 'الفواتير والمدفوعات لمنظمتك.',
    'hub.invoicesLoadFailed': 'تعذّر تحميل الفواتير.',
    'admin.trialsIntro': 'تجارب المنتجات للمنظمات على المنصة.',
    'admin.subscriptionsIntro': 'اشتراكات المنظمات. فعّل يدوياً عند إيقاف الفوترة.',
    'admin.activateManual': 'تفعيل يدوي',
    'admin.activateSubscriptionConfirm': 'تفعيل هذا الاشتراك لمدة 30 يوماً دون دفع؟',
    'admin.confirmSuspendCustomer.title': 'إيقاف العميل؟',
    'admin.confirmSuspendCustomer.message':
      'إيقاف {name} ({email})؟ سيفقدون الوصول حتى إعادة التفعيل.',
    'admin.confirmActivateCustomer.title': 'تفعيل العميل؟',
    'admin.confirmActivateCustomer.message': 'تفعيل {name} ({email}) واستعادة الوصول؟',
    'admin.confirmSuspendLicense.title': 'إيقاف الترخيص؟',
    'admin.confirmSuspendLicense.message':
      'إيقاف ترخيص {name} ({email})؟ ستتوقف الأجهزة النشطة عن التحقق.',
    'admin.confirmRevokeLicense.title': 'إلغاء الترخيص؟',
    'admin.confirmRevokeLicense.message':
      'إلغاء ترخيص {name} ({email}) نهائياً؟ لا يمكن التراجع عن هذا.',
    'admin.confirmActivateLicense.title': 'تفعيل الترخيص؟',
    'admin.confirmActivateLicense.message': 'تفعيل ترخيص {name} ({email})؟',
    'admin.confirmDeactivateDevice.title': 'إلغاء تنشيط الجهاز؟',
    'admin.confirmDeactivateDevice.message':
      'إلغاء تنشيط {name} لـ {email}؟ سيتم تحرير المقعد فوراً.',
    'admin.confirmPublishRelease.title': 'نشر الإصدار؟',
    'admin.confirmPublishRelease.message':
      'نشر الإصدار {name}؟ سيتمكن العملاء من تنزيله.',
    'admin.confirmDeleteMedia.title': 'حذف الوسائط؟',
    'admin.confirmDeleteMedia.message': 'إزالة أصل الوسائط هذا؟ لا يمكن التراجع عن هذا.',
    'admin.confirmActivateSubscription.title': 'تفعيل الاشتراك؟',
    'admin.confirmActivateSubscription.message':
      'تفعيل هذا الاشتراك لمدة 30 يوماً دون دفع؟',
    'admin.customersIntro': 'ابحث عن العملاء وافتح التفاصيل للعمليات.',
    'admin.searchCustomers': 'ابحث بالاسم أو البريد أو معرّف المستخدم',
    'admin.filterAnyStatus': 'أي حالة',
    'admin.filterActive': 'نشط',
    'admin.filterSuspended': 'موقوف',
    'admin.filterAnyProvider': 'أي مزوّد',
    'admin.open360': 'التفاصيل',
    'admin.customer360': 'تفاصيل العميل',
    'admin.customerDetailsSub': 'تفاصيل العميل وإدارته',
    'desk.metric.activeSubscriptions': 'اشتراكات نشطة',
    'cc.access.showSecondary': 'المزيد من المقاييس',
    'cc.access.hideSecondary': 'إخفاء المقاييس',
    'cc.settings.tab.identity': 'الهوية',
    'cc.settings.tab.contact': 'التواصل',
    'cc.settings.tab.about': 'من نحن',
    'cc.settings.tab.social': 'التواصل الاجتماعي',
    'cc.settings.tab.footer': 'التذييل',
    'cc.settings.tab.advanced': 'متقدم',
    'admin.results': 'نتيجة',
    'admin.noCustomers': 'لا يوجد عملاء مطابقون لهذه المرشحات.',
    'admin.backToCustomers': 'العودة إلى العملاء',
    'admin.customerNotFound': 'العميل غير موجود.',
    'admin.provider': 'المزوّد',
    'admin.status': 'الحالة',
    'admin.verified': 'موثّق',
    'admin.lastSignIn': 'آخر تسجيل دخول',
    'admin.created': 'تاريخ الإنشاء',
    'admin.role': 'الدور',
    'admin.orgMemberships': 'عضويات المؤسسة',
    'admin.tabOverview': 'نظرة عامة',
    'admin.tabNotes': 'ملاحظات',
    'admin.tabActivity': 'النشاط',
    'admin.emptyProducts': 'لا توجد منتجات مرتبطة.',
    'admin.emptyLicenses': 'لا توجد تراخيص.',
    'admin.emptyTrials': 'لا توجد تجارب.',
    'admin.emptySubscriptions': 'لا توجد اشتراكات.',
    'admin.emptyInvoices': 'لا توجد فواتير.',
    'admin.emptyPayments': 'لا توجد مدفوعات.',
    'admin.emptyDevices': 'لا توجد أجهزة نشطة بعد.',
    'admin.emptyDownloads': 'لا يوجد نشاط تنزيل بعد.',
    'admin.emptyNotes': 'لا توجد ملاحظات داخلية بعد.',
    'admin.emptyActivity': 'لا يوجد نشاط مرتبط بعد.',
    'admin.licenseType': 'النوع',
    'admin.starts': 'البداية',
    'admin.ends': 'النهاية',
    'admin.openLicenses': 'التراخيص',
    'admin.organization': 'المؤسسة',
    'admin.plan': 'الخطة',
    'admin.periodStart': 'بداية الفترة',
    'admin.periodEnd': 'نهاية الفترة',
    'admin.amount': 'المبلغ',
    'admin.payments': 'المدفوعات',
    'admin.version': 'الإصدار',
    'admin.platform': 'المنصة',
    'admin.downloadedAt': 'وقت التنزيل',
    'admin.deviceName': 'الجهاز',
    'admin.activated': 'التفعيل',
    'admin.lastSeen': 'آخر ظهور',
    'admin.action': 'الإجراء',
    'admin.resource': 'المورد',
    'admin.actor': 'الفاعل',
    'admin.notePlaceholder': 'ملاحظة داخلية (غير ظاهرة للعميل)',
    'admin.addNote': 'إضافة ملاحظة',
    'admin.noteSaved': 'تم حفظ الملاحظة',
    'admin.settings.nav': 'هوية المنصة',
    'admin.settings.title': 'هوية المنصة وبيانات التواصل',
    'admin.settings.intro':
      'صفحات من نحن والتواصل العامة تقرأ هذه القيم. اترك الحقول الاختيارية فارغة لإخفائها.',
    'admin.settings.saved': 'تم حفظ إعدادات المنصة',
    'admin.settings.platformName': 'اسم المنصة للعرض',
    'admin.settings.contactPerson': 'مسؤول التواصل الرئيسي',
    'admin.settings.mobilePhone': 'الجوال / الهاتف',
    'admin.settings.whatsappNumber': 'رقم واتساب',
    'admin.settings.supportEmail': 'بريد الدعم العام',
    'admin.settings.salesEmail': 'بريد المبيعات',
    'admin.settings.address': 'نص العنوان / الشركة',
    'admin.settings.hours': 'نص ساعات العمل',
    'admin.settings.aboutShortEn': 'من نحن مختصر (EN)',
    'admin.settings.aboutShortAr': 'من نحن مختصر (AR)',
    'admin.settings.aboutShortCkb': 'من نحن مختصر (CKB)',
    'admin.settings.aboutFullEn': 'من نحن كامل (EN)',
    'admin.settings.aboutFullAr': 'من نحن كامل (AR)',
    'admin.settings.aboutFullCkb': 'من نحن كامل (CKB)',
    'admin.settings.facebook': 'رابط فيسبوك',
    'admin.settings.instagram': 'رابط إنستغرام',
    'admin.settings.linkedin': 'رابط لينكدإن',
    'admin.settings.youtube': 'رابط يوتيوب',
    'admin.settings.footerContact': 'نص تواصل التذييل',
    'admin.settings.footerCopyright': 'نص حقوق النشر / الشركة',
    'contact.title': 'تواصل معنا',
    'contact.lede': 'تواصل مع BeCoder لاستفسارات المنتجات والمبيعات.',
    'contact.person': 'مسؤول التواصل',
    'contact.phone': 'الهاتف',
    'contact.hours': 'ساعات العمل',
    'contact.address': 'العنوان',
    'contact.call': 'اتصال',
    'contact.whatsapp': 'واتساب',
    'contact.email': 'البريد',
    'contact.contactSales': 'تواصل للمبيعات',
    'contact.openPage': 'صفحة التواصل الكاملة',
    'contact.social': 'وسائل التواصل',
    'about.defaultBody':
      'BeCoder منصة برمجيات تقدّم تطبيقات أعمال جاهزة وتبني برمجيات مخصصة للشركات والعمليات متعددة الفروع — مع تركيز على الأدوات العملية للأعمال.',
    'about.pillarApps': 'تطبيقات جاهزة',
    'about.pillarAppsDesc': 'منتجات كتالوج مصممة لسير عمل الأعمال الحقيقي.',
    'about.pillarCustom': 'برمجيات مخصصة',
    'about.pillarCustomDesc': 'أنظمة مبنية لغرض محدد عندما لا تكفي منتجات الكتالوج.',
    'about.pillarPractical': 'تركيز عملي',
    'about.pillarPracticalDesc': 'برمجيات تخدم العمليات — لا استعراض تسويقي.',
    'common.search': 'بحث',
    'common.prev': 'السابق',
    'common.next': 'التالي',
    'common.yes': 'نعم',
    'common.no': 'لا',
    'common.actions': 'إجراءات',
    'desk.licensesTitle': 'مكتب التراخيص',
    'desk.licensesIntro': 'ابحث وصفِّ وادر تراخيص العملاء.',
    'desk.trialsTitle': 'مكتب التجارب',
    'desk.subscriptionsTitle': 'مكتب الاشتراكات',
    'desk.devicesTitle': 'مكتب الأجهزة',
    'desk.devicesIntro': 'ارتباطات الأجهزة النشطة والمعطّلة.',
    'desk.searchLicenses': 'ابحث بالعميل أو البريد أو معرّف الترخيص أو المنتج',
    'desk.searchTrials': 'ابحث بالعميل أو البريد أو المنتج أو التجربة أو المؤسسة',
    'desk.searchSubscriptions': 'ابحث بالعميل أو البريد أو المنتج أو الاشتراك',
    'desk.searchDevices': 'ابحث بالعميل أو البريد أو الجهاز أو المنتج أو الترخيص',
    'desk.filterAnyProduct': 'أي منتج',
    'desk.filterAnyType': 'أي نوع',
    'desk.filterAnyValidity': 'أي صلاحية',
    'desk.filterAnyPeriod': 'أي فترة',
    'desk.type.trial': 'تجربة',
    'desk.type.monthly': 'شهري',
    'desk.type.yearly': 'سنوي',
    'desk.type.lifetime': 'مدى الحياة',
    'desk.status.expired': 'منتهٍ',
    'desk.status.revoked': 'ملغى',
    'desk.status.canceled': 'ملغى',
    'desk.status.converted': 'محوّل',
    'desk.status.deactivated': 'معطّل',
    'desk.validity.active': 'ساري الآن',
    'desk.validity.expired': 'تجاوز تاريخ الانتهاء',
    'desk.licenseId': 'الترخيص',
    'desk.issuedByAdmin': 'صادر من الإدارة',
    'desk.permanent': 'دائم',
    'desk.lifetimeNoEnd': 'الترخيص مدى الحياة بلا تاريخ انتهاء',
    'desk.endsOptional': 'تاريخ انتهاء اختياري',
    'desk.lifetimePolicy':
      'مدى الحياة = ادفع مرة واستخدم للأبد. دعم Premium المضمّن (3 أشهر) منفصل ولا يُستخدم كانتهاء للترخيص.',
    'desk.openDetail': 'التفاصيل',
    'desk.openInDesk': 'فتح في المكتب',
    'desk.backLicenses': 'العودة إلى التراخيص',
    'desk.licenseDetail': 'تفاصيل الترخيص',
    'desk.licenseNotFound': 'الترخيص غير موجود.',
    'desk.history': 'السجل',
    'desk.newEndDate': 'تاريخ الانتهاء الجديد',
    'desk.currentEnd': 'الانتهاء الحالي',
    'desk.saveExpiration': 'تحديث الانتهاء',
    'desk.expirationSaved': 'تم تحديث الانتهاء',
    'desk.confirmExpiration.title': 'تحديث انتهاء الترخيص؟',
    'desk.confirmExpiration.message':
      'تغيير تاريخ انتهاء {product} ({email}) من {old} إلى {next}؟',
    'desk.confirmActivateLicense.message':
      'تفعيل الترخيص {id} لـ {name} ({email}) على {product}؟',
    'desk.confirmSuspendLicense.message':
      'إيقاف الترخيص {id} لـ {name} ({email}) على {product}؟ ستتوقف الأجهزة عن التحقق.',
    'desk.confirmRevokeLicense.message':
      'إلغاء الترخيص {id} لـ {name} ({email}) على {product}؟ لا يمكن التراجع. يبقى السجل ظاهرًا.',
    'desk.confirmExtendTrial.title': 'تمديد التجربة؟',
    'desk.confirmExtendTrial.message':
      'تمديد {product} لـ {email} بمقدار {days} يومًا؟ الحالي {old} → الجديد {next}.',
    'desk.confirmForceGrantTrial.title': 'منح وقت تجربة إجباري؟',
    'desk.confirmForceGrantTrial.message':
      'تجربة {product} لـ {email} منتهية بالفعل ({status}). تأكيد منح {days} يومًا ({old} → {next}). هذا تجاوز إداري استثنائي وليس تمديدًا عاديًا.',
    'desk.confirmCancelTrial.title': 'إلغاء التجربة؟',
    'desk.confirmCancelTrial.message':
      'إنهاء تجربة {product} ({email}) مبكرًا؟ يبقى السجل التاريخي.',
    'desk.confirmActivateSubscription.message':
      'تفعيل اشتراك {product} ({email}) يدويًا لمدة 30 يومًا دون دفعة؟',
    'desk.confirmDeactivateDevice.message':
      'إلغاء تنشيط {name} لـ {email} على {product}؟ يبقى سجل الجهاز؛ لا يُحذف الترخيص.',
    'desk.extend': 'تمديد',
    'desk.forceGrant': 'منح إجباري',
    'desk.extendDays': 'أيام التمديد',
    'desk.cancelTrial': 'إلغاء التجربة',
    'desk.daysLeft': 'الأيام المتبقية',
    'desk.noFilterResults': 'لا توجد سجلات مطابقة لهذه المرشحات.',
    'desk.loadError': 'تعذّر تحميل البيانات',
    'desk.metric.activeLicenses': 'تراخيص نشطة',
    'desk.metric.suspendedLicenses': 'موقوفة',
    'desk.metric.revokedLicenses': 'ملغاة',
    'desk.metric.lifetime': 'مدى الحياة',
    'desk.metric.activeTrials': 'تجارب نشطة',
    'desk.metric.expiringTrials': 'تجارب تنتهي (7 أيام)',
    'desk.metric.activeMonthly': 'شهري نشط',
    'desk.metric.activeAnnual': 'سنوي نشط',
    'desk.metric.activeDevices': 'أجهزة نشطة',
    'dist.releasesTitle': 'التوزيع — الإصدارات',
    'dist.releasesIntro': 'ابحث، أنشئ مسودات، ارفع، انشر، ودقّق إصدارات المنتجات.',
    'dist.downloadAuditNav': 'تدقيق التنزيلات',
    'dist.downloadAuditTitle': 'تدقيق التنزيلات',
    'dist.downloadAuditIntro': 'أحداث تنزيل العملاء عبر المنتجات والإصدارات.',
    'dist.searchReleases': 'ابحث بالإصدار أو الملف أو checksum',
    'dist.searchDownloads': 'ابحث بالعميل أو البريد أو الإصدار',
    'dist.filterAnyPlatform': 'أي منصة',
    'dist.createDraft': 'إنشاء مسودة',
    'dist.draftCreated': 'تم إنشاء المسودة',
    'dist.uploadFile': 'رفع ملف',
    'dist.replaceFile': 'استبدال الملف',
    'dist.uploadDone': 'اكتمل الرفع',
    'dist.unpublish': 'إلغاء النشر',
    'dist.unpublished': 'تم إلغاء النشر',
    'dist.deleted': 'تم حذف المسودة',
    'dist.verifyIntegrity': 'تحقق من السلامة',
    'dist.integrityOk': 'تم التحقق من السلامة',
    'dist.integrityFail': 'فشل التحقق من السلامة',
    'dist.viewDownloads': 'عرض التنزيلات',
    'dist.openDownloadAudit': 'فتح تدقيق التنزيلات',
    'dist.downloadAuditHint': '{count} حدث تنزيل — استخدم تدقيق التنزيلات للبحث والتصفية.',
    'dist.file': 'الملف',
    'dist.size': 'الحجم',
    'dist.checksum': 'پشکنینی فایل',
    'dist.flags': 'العلامات',
    'dist.current': 'حالي',
    'dist.superseded': 'مُستبدَل',
    'dist.notesYes': 'ملاحظات ✓',
    'dist.notesNo': 'بلا ملاحظات',
    'dist.minSupported': 'أدنى إصدار مدعوم',
    'dist.notes.en': 'ملاحظات الإصدار (إنجليزي)',
    'dist.notes.ar': 'ملاحظات الإصدار (عربي)',
    'dist.notes.ckb': 'ملاحظات الإصدار (كردي)',
    'dist.confirmPublishDetail':
      'نشر {product} {version} ({platform})؟ الملف: {file}. سيتمكن العملاء من التنزيل.',
    'dist.confirmUnpublish.title': 'إلغاء نشر الإصدار؟',
    'dist.confirmUnpublish.message': 'إلغاء نشر {product} {version}؟ سيفقد العملاء إمكانية التنزيل.',
    'dist.confirmDelete.title': 'حذف المسودة؟',
    'dist.confirmDelete.message': 'حذف المسودة {version}؟ لا يمكن التراجع.',
    'dist.distributionStatus': 'التوزيع',
    'dist.manageReleases': 'إدارة الإصدارات',
    'dist.publishedAvailable': 'إصدار منشور متاح',
    'dist.latestVersion': 'أحدث إصدار',
    'dist.platforms': 'المنصات',
    'dist.packageReady': 'الحزمة جاهزة',
    'dist.fromDate': 'من تاريخ',
    'dist.toDate': 'إلى تاريخ',
    'dist.empty.no_entitlement': 'ليس لديك بعد صلاحية تنزيل لهذا المنتج.',
    'dist.empty.coming_soon': 'التنزيلات غير متاحة بعد — المنتج قادم قريبًا.',
    'dist.empty.release_unpublished': 'لا توجد إصدارات منشورة للتنزيل بعد.',
    'dist.empty.package_missing': 'يوجد إصدار لكن حزمة التثبيت غير جاهزة بعد.',
    'dist.empty.product_not_distributable': 'هذا المنتج لا يُوزَّع عبر تنزيلات BeCoder.',
    'dist.empty.expired': 'انتهت صلاحية وصولك لتنزيلات هذا المنتج.',
    'dist.empty.suspended_or_revoked': 'التنزيلات غير متاحة لأن الوصول موقوف أو ملغى.',
    'dist.empty.unknown': 'التنزيلات غير متاحة حاليًا.',
    'admin.launchBaseUrl': 'رابط التشغيل الأساسي',
    'admin.saveLaunchUrl': 'حفظ رابط التشغيل',
    'admin.launchUrlSaved': 'تم حفظ رابط التشغيل',
    'admin.mediaTitle': 'مدير الوسائط',
    'admin.mediaLede': 'ارفع وأدر وسائط العرض لتطبيقات الكتالوج.',
    'admin.mediaIcon': 'أيقونة التطبيق',
    'admin.mediaHero': 'صورة البطل',
    'admin.mediaPoster': 'ملصق الفيديو',
    'admin.mediaVideo': 'فيديو ترويجي',
    'admin.mediaScreenshots': 'لقطات الشاشة',
    'admin.mediaUploadReplace': 'رفع / استبدال',
    'admin.mediaAddScreenshot': 'إضافة لقطة',
    'admin.mediaAddScreenshots': 'إضافة لقطات',
    'admin.mediaSelectScreenshots': 'اختيار لقطات',
    'admin.mediaScreenshotsHint':
      'اختر عدة صور، راجع المصغّرات، ثم ارفع الكل. اللقطات الحالية تُحفظ. اسحب المصغّرات لإعادة الترتيب.',
    'admin.mediaDragReorder': 'اسحب لإعادة الترتيب',
    'admin.mediaPreview': 'معاينة الصورة',
    'admin.mediaUploadDone': 'تم',
    'admin.mediaQueued': 'في الانتظار',
    'admin.mediaSkipped': 'تم التخطي',
    'admin.mediaPartialUpload': 'فشل بعض الرفع',
    'admin.mediaUploadFailed': 'فشل رفع اللقطات',
    'admin.mediaPendingTitle': '{count} محددة ({valid} جاهزة للرفع)',
    'admin.mediaPendingInvalid': '{count} ملف/ملفات ستُتخطى بسبب أخطاء التحقق.',
    'admin.mediaClearSelection': 'مسح التحديد',
    'admin.mediaUploadAll': 'رفع الكل ({count})',
    'admin.mediaRemoveFromSelection': 'إزالة من التحديد',
    'admin.mediaBatchComplete': 'اكتمل رفع الدفعة',
    'admin.mediaBatchResult': 'تم الرفع {uploaded} · فشل {failed} · تخطي {skipped} · الإجمالي {total}',
    'admin.mediaReplaceScreenshot': 'استبدال اللقطة',
    'admin.mediaReplaced': 'تم استبدال اللقطة',
    'admin.mediaInvalidType': '{name}: نوع غير مدعوم ({type}). استخدم PNG أو JPG أو WEBP أو GIF.',
    'admin.mediaInvalidSize': '{name}: يتجاوز حد الحجم {max}.',
    'admin.mediaInvalidEmpty': '{name}: الملف فارغ.',
    'admin.mediaEmpty': 'لا توجد وسائط بعد.',
    'admin.mediaNoProducts': 'أنشئ منتجًا أولًا.',
    'admin.mediaUploaded': 'تم رفع الوسائط',
    'admin.mediaDeleted': 'تمت إزالة الوسائط',

    'cms.saveDraft': 'حفظ المسودة',
    'cms.publish': 'نشر',
    'cms.preview': 'معاينة صفحة المنتج',
    'cms.saved': 'تم الحفظ',
    'cms.published': 'تم النشر',
    'cms.notFound': 'المنتج غير موجود',
    'cms.editCommercial': 'تحرير المحتوى التجاري',
    'cms.searchProducts': 'بحث بالاسم أو المعرف',
    'cms.filterAnyStatus': 'أي حالة',
    'cms.filterAnyPurchasable': 'أي قابلية شراء',
    'cms.filterAnyTrial': 'أي تجربة',
    'cms.purchasable': 'قابل للشراء',
    'cms.notPurchasable': 'غير قابل للشراء',
    'cms.trialEnabled': 'التجربة مفعّلة',
    'cms.trialDisabled': 'التجربة معطّلة',
    'cms.operationalTrial': 'تجربة تشغيلية',
    'cms.trialDays': 'أيام التجربة',
    'cms.trialSaved': 'تم حفظ إعدادات التجربة',
    'cms.statusPublished': 'منشور',
    'cms.statusDraft': 'مسودة',
    'cms.pricingOk': 'التسعير جاهز',
    'cms.pricingMissing': 'التسعير ناقص',
    'cms.plansPricing': 'الخطط والأسعار',
    'cms.lifetimeLicense': 'ترخيص مدى الحياة',
    'cms.priceUsd': 'السعر (USD)',
    'cms.priceMajor': 'المبلغ',
    'cms.priceHintIqd': 'أدخل الدينار كمبلغ كامل (مثال 600000 يعني 600,000 د.ع).',
    'cms.priceHintUsd': 'أدخل الدولار كمبلغ كامل (مثال 550 يعني 550$).',
    'cms.pricePreview': 'يُخزَّن {minor} amountMinor',
    'cms.marketIraq': 'العراق (IQD)',
    'cms.marketInternational': 'الدولي (USD)',
    'cms.planType': 'نوع الخطة',
    'cms.planMarket': 'السوق',
    'cms.planPeriod': 'فترة الفوترة',
    'cms.planActive': 'نشطة',
    'cms.planInactive': 'غير نشطة',
    'cms.cloudServices': 'الخدمات السحابية السنوية',
    'cms.planMonthly': 'شهري',
    'cms.planAnnual': 'سنوي',
    'cms.createPlan': 'إضافة خطة',
    'cms.planCreated': 'تم إنشاء الخطة',
    'cms.planUpdated': 'تم تحديث الخطة',
    'cms.noPlansMarket': 'لا توجد خطط في هذا السوق بعد.',
    'cms.raqbSubscriptionOnly': 'راقب اشتراك فقط. لا توجد خطة مدى الحياة.',
    'cms.raqbTrialNote': 'تجربة: {days} أيام. تبدأ فقط بعد أن يضغط العميل على بدء التجربة.',
    'cms.lifetimeVsCloud': 'الترخيص مدى الحياة يمنح حقًا دائمًا في استخدام البرنامج. الخدمات السحابية اشتراك سنوي منفصل، وانتهاؤها لا يلغي الترخيص الدائم.',
    'cms.trialPlatformOff': 'بدء التجارب على المنصة متوقف حالياً (TRIALS_ENABLED). الإعدادات أدناه لا تشغّل تجارب عامة.',
    'cms.trialPlatformOn': 'بدء التجارب على المنصة مفعّل. يجب أن يدعم المنتج التجربة وأن تكون تشغيلية.',
    'cms.trialProductSupport': 'هذا المنتج يدعم التجربة',
    'cms.trialPlatformGate': 'المنصة تسمح حالياً ببدء التجربة',
    'cms.statusHelp': 'ظهور الكتالوج: النشط يُعرض، المخفي/الأرشيفي لا يُعرض، قريباً يُعرض دون اعتباره متاحاً للبيع.',
    'cms.purchasableHelp': 'علامة القابلية للشراء. مستقلة عن حالة الكتالوج ونشر المحتوى.',
    'cms.publishedHelp': 'نصوص المتجر منشورة. المسودة تبقى في الإدارة حتى النشر.',
    'cms.heroAuthoritative': 'الصورة الرئيسية هي الصورة الكبيرة في صفحة المنتج. استبدالها لا يحذف اللقطات أو الأيقونة.',
    'cms.savePrice': 'حفظ السعر',
    'cms.priceSaved': 'تم حفظ السعر',
    'cms.annualSavings': 'توفير سنوي مقابل شهري',
    'cms.invalidPrice': 'أدخل سعرًا صالحًا',
    'cms.displayName': 'اسم العرض',
    'cms.heroIntro': 'مقدمة البطل',
    'cms.whoLede': 'مقدمة الجمهور',
    'cms.highlightsTitle': 'عنوان أبرز النقاط',
    'cms.explainHeading': 'عنوان الشرح',
    'cms.fitGuidance': 'إرشاد الملاءمة',
    'cms.audiences': 'الجماهير',
    'cms.features': 'الميزات',
    'cms.problems': 'المشكلات',
    'cms.explainSections': 'أقسام الشرح',
    'cms.fitRecommendations': 'توصيات الملاءمة',
    'cms.addItem': 'إضافة عنصر',
    'cms.featureTitle': 'العنوان',
    'cms.featureDesc': 'الوصف',
    'cms.faqNote': 'الأسئلة المشتركة لجميع المنتجات؛ أسئلة المنتج محصورة هنا.',
    'cms.addFaq': 'إضافة سؤال',
    'cms.faqShared': 'سؤال مشترك',
    'cms.faqProduct': 'سؤال المنتج',
    'cms.faqSaved': 'تم حفظ السؤال',
    'cms.faqCreated': 'تم إنشاء السؤال',
    'cms.faqDeleted': 'تم حذف السؤال',
    'cms.newFaqQuestion': 'سؤال جديد',
    'cms.savePolicy': 'حفظ السياسة',
    'cms.policySaved': 'تم حفظ السياسة',
    'cms.mediaCounts': 'الوسائط',
    'cms.openMediaManager': 'فتح مدير الوسائط',
    'cms.warnMissingAr': 'محتوى عربي ناقص',
    'cms.warnMissingCkb': 'محتوى كردي ناقص',
    'cms.warnMissingPricing': 'التسعير ناقص',
    'cms.warnMissingHero': 'صورة البطل ناقصة',
    'cms.warnMissingScreenshots': 'لا لقطات شاشة',
    'cms.warnMissingLaunch': 'رابط التشغيل ناقص لمنتج نشط',
    'cms.warnTrialMismatch': 'التجربة مفعّلة لكن غير تشغيلية',
    'cms.section.overview': 'نظرة عامة',
    'cms.section.content': 'المحتوى',
    'cms.section.plans': 'الخطط والتسعير',
    'cms.section.trial': 'التجربة',
    'cms.section.structure': 'الجمهور والميزات',
    'cms.section.faq': 'الأسئلة',
    'cms.section.policy': 'سياسة الدعم',
    'cms.section.media': 'الوسائط',
    'cms.section.distribution': 'التوزيع',
    'cms.confirmLaunchUrl.title': 'تغيير رابط التشغيل؟',
    'cms.confirmLaunchUrl.message': 'قديم: {old}\nجديد: {next}',
    'cms.confirmPrice.title': 'تغيير سعر الخطة؟',
    'cms.confirmPrice.message': '{plan}\nقديم: {old}\nجديد: {next}',
    'cms.confirmPublish.title': 'نشر المحتوى التجاري؟',
    'cms.confirmPublish.message': 'نشر محتوى المتجر لـ {name}؟',
    'cms.confirmPolicy.title': 'حفظ سياسة الدعم ومدى الحياة؟',
    'cms.confirmPolicy.message': 'تحديث السياسة لـ {locale}؟',
    'cms.confirmFaqDelete.title': 'حذف السؤال؟',
    'cms.policy.lifetimeTitle': 'عنوان مدى الحياة',
    'cms.policy.lifetimeLede': 'مقدمة مدى الحياة',
    'cms.policy.lifetimeBody': 'نص مدى الحياة',
    'cms.policy.supportTitle': 'عنوان الدعم',
    'cms.policy.supportBugsTitle': 'عنوان الأخطاء',
    'cms.policy.supportBugsBody': 'نص الأخطاء',
    'cms.policy.supportSecurityTitle': 'عنوان الأمان',
    'cms.policy.supportSecurityBody': 'نص الأمان',
    'cms.policy.supportTechTitle': 'عنوان التقنية',
    'cms.policy.supportTechBody': 'نص التقنية',
    'cms.policy.supportCustomTitle': 'عنوان مخصص',
    'cms.policy.supportCustomBody': 'نص مخصص',
    'cms.policy.thirdPartyTitle': 'عنوان طرف ثالث',
    'cms.policy.thirdPartyBody': 'نص طرف ثالث',
    'cms.policy.updatesTitle': 'عنوان التحديثات',
    'cms.policy.updatesBody': 'نص التحديثات',
    'admin.mediaDeleteConfirm': 'إزالة أصل الوسائط هذا؟',
    'admin.mediaMoveUp': 'تحريك لأعلى',
    'admin.mediaMoveDown': 'تحريك لأسفل',
    'nav.media': 'الوسائط',
    'portal.launch': 'تشغيل',
    'nav.trials': 'التجارب',
    'showcase.getStarted': 'تجربة، اشتراك، تشغيل',
    'showcase.getStartedLede':
      'الأزرار جاهزة من الكتالوج. الدفع وتشغيل مركز المنتجات يأتيان لاحقاً.',
    'showcase.videoControls': 'عناصر التحكم بالفيديو',
    'showcase.play': 'تشغيل الفيديو',
    'showcase.pause': 'إيقاف الفيديو',
    'showcase.mute': 'كتم الصوت',
    'showcase.unmute': 'تشغيل الصوت',
    'showcase.fullscreen': 'ملء الشاشة',
    'showcase.exitFullscreen': 'إنهاء ملء الشاشة',
    'showcase.videoReducedMotion': 'الحركة مخفّضة — تُعرض لقطة ثابتة.',

    'soon.defaultSubtitle': 'محجوز لإصدار لاحق، وليس ضمن الإصدار الحالي لمنصة BeCoder.',

    'company.eyebrow': 'برمجيات BeCoder',
    'company.heroTitle': 'برمجيات تُبنى لتدوم.',
    'company.heroSubtitle': 'ابنِ وانشر وطوّر محفظة برمجيات جادة من منصة واحدة موثوقة.',
    'company.exploreSoftware': 'استكشف البرمجيات',
    'company.apiStatus': 'واجهة البرمجة {status}',
    'company.softwareCount': 'منتج برمجي واحد',
    'company.softwareCountPlural': '{count} منتجات برمجية',
    'company.releaseCount': 'إصدار منشور واحد',
    'company.releaseCountPlural': '{count} إصدارات منشورة',

    'pulse.aria': 'نبض الشركة',
    'pulse.software': 'البرمجيات',
    'pulse.softwareDetail': 'من بناء BeCoder',
    'pulse.titles': '{count} عناوين',
    'pulse.latestRelease': 'أحدث إصدار',
    'pulse.noneYet': 'لا يوجد بعد',
    'pulse.awaitingPublication': 'بانتظار النشر',
    'pulse.customerUse': 'استخدام العملاء',
    'pulse.activeCount': '{count} نشط',
    'pulse.deployments': '{count} عمليات نشر',
    'pulse.needsAttention': 'يحتاج متابعة',
    'pulse.trackedExceptions': 'حالات مرصودة',
    'pulse.noTrackedExceptions': 'لا توجد حالات مرصودة',
    'pulse.platformHealth': 'صحة المنصة',
    'pulse.liveApiSignal': 'إشارة مباشرة من الواجهة',

    'featured.sectionEyebrow': 'ما تبنيه BeCoder',
    'featured.sectionTitle': 'البرمجية المميزة',
    'featured.sectionDesc': 'المنتج الذي يتصدر محفظة BeCoder حاليًا',
    'featured.managePortfolio': 'إدارة المحفظة',
    'featured.empty': 'انشر منتجك الأول لتأسيس محفظة برمجيات BeCoder.',
    'featured.portfolioLabel': 'محفظة BeCoder',
    'featured.fallbackTagline': 'برمجيات مبنية لغرض واضح، تُهندَس وتُوزَّع من BeCoder.',
    'featured.currentVersion': 'الإصدار الحالي',
    'featured.released': 'تاريخ الإصدار',
    'featured.customers': 'العملاء',
    'featured.deployments': 'عمليات النشر',
    'featured.stability': 'الاستقرار',
    'featured.updatePolicy': 'سياسة التحديث',
    'featured.recentImprovements': 'أحدث التحسينات',
    'featured.openProduct': 'فتح المنتج',
    'featured.coverLabel': 'برمجيات BeCoder',

    'portfolio.sectionEyebrow': 'من بناء BeCoder',
    'portfolio.sectionTitle': 'محفظة البرمجيات',
    'portfolio.sectionDesc': 'كل منتج يلتزم بمعيار واحد في الهندسة والإتقان',
    'portfolio.fallbackTagline': 'برمجيات تُهندَس وتُوزَّع من BeCoder.',
    'portfolio.customers': 'العملاء',
    'portfolio.deployments': 'النشر',
    'portfolio.policy': 'السياسة',
    'portfolio.updated': 'حدّث في {date}',
    'portfolio.updateUnavailable': 'سجل التحديث غير متاح',
    'portfolio.open': 'فتح',

    'releases.sectionEyebrow': 'إنجازات أُطلقت',
    'releases.sectionTitle': 'الإصدارات الأخيرة',
    'releases.sectionDesc': 'محطات موثّقة من البرمجيات التي تنشرها BeCoder',
    'releases.pipeline': 'مسار الإصدارات',
    'releases.empty': 'لا توجد إصدارات منشورة بعد.',
    'releases.fallbackProduct': 'منتج برمجي',
    'releases.noNotes': 'لا توجد ملاحظات إصدار.',

    'activity.momentumEyebrow': 'زخم العملاء',
    'activity.momentumTitle': 'البرمجيات قيد الاستخدام',
    'activity.momentumDesc': 'أحدث عمليات التثبيت والتفعيل من نشاط العملاء الحقيقي',
    'activity.newestDeployments': 'أحدث عمليات النشر',
    'activity.noDeployments': 'لا توجد عمليات نشر مسجّلة.',
    'activity.latestActivation': 'آخر تفعيل',
    'activity.customer': 'عميل',
    'activity.software': 'برمجية',
    'activity.newsEyebrow': 'غرفة أخبار البرمجيات',
    'activity.newsTitle': 'ما الجديد',
    'activity.newsDesc': 'إشارات واقعية من الإصدارات وخارطة الطريق والنشاط الأمني',
    'activity.latestRelease': 'أحدث إصدار',
    'activity.noPublishedRelease': 'لا يوجد إصدار منشور',
    'activity.noDate': 'لا يوجد تاريخ',
    'activity.releaseWork': 'عمل الإصدارات',
    'activity.releasesInProgress': '{count} إصدارات قيد الإعداد',
    'activity.releaseInProgress': 'إصدار واحد قيد الإعداد',
    'activity.draftPipeline': 'مسودة قيد العمل',
    'activity.upcomingSoftware': 'برمجيات قادمة',
    'activity.noAnnouncedTitles': 'لا عناوين معلنة',
    'activity.onRoadmap': '{count} في خارطة الطريق العامة',
    'activity.latestSecurity': 'آخر نشاط أمني',
    'activity.noSecurityEvent': 'لا يوجد نشاط أمني حديث',
    'activity.auditChecked': 'تم فحص سجل المراجعة',

    'kpi.sectionEyebrow': 'صحة الأعمال',
    'kpi.sectionTitle': 'مؤشرات المنصة',
    'kpi.sectionDesc': 'إشارات تشغيلية تدعم محفظة البرمجيات',
    'kpi.customers': 'العملاء',
    'kpi.customersSub': 'حسابات نشطة على المنصة',
    'kpi.products': 'المنتجات',
    'kpi.productsSub': 'عناوين ضمن التوزيع',
    'kpi.licenses': 'التراخيص',
    'kpi.licensesSub': 'صلاحيات صادرة',
    'kpi.suspended': 'معلّق',
    'kpi.suspendedSub': 'مقاعد معلّقة أو ملغاة',

    'eng.sectionEyebrow': 'شفافية هندسية',
    'eng.sectionTitle': 'هندسة المنصة',
    'eng.sectionDesc': 'مؤشرات واقعية حية من الأنظمة خلف برمجيات BeCoder',
    'eng.apiStatus': 'حالة الواجهة',
    'eng.publishedArtifacts': 'الملفات المنشورة',
    'eng.checksummed': 'الملفات ذات التحقق',
    'eng.docsCoverage': 'تغطية الوثائق',
    'eng.activeDeployments': 'عمليات النشر النشطة',
    'eng.recentAudit': 'أحداث المراجعة الأخيرة',

    'attention.sectionEyebrow': 'يتطلب انتباهًا',
    'attention.titleNone': 'لا توجد حالات مرصودة',
    'attention.titleSome': '{count} حالات مرصودة',
    'attention.titleOne': 'حالة مرصودة واحدة',
    'attention.desc': 'تُعرض فقط الحالات الموجودة في بيانات المنصة الحالية',
    'attention.suspendedLicenses': 'تراخيص معلّقة أو ملغاة',
    'attention.draftReleases': 'مسودات إصدارات',
    'attention.inactiveDeployments': 'عمليات نشر غير نشطة',
    'attention.hiddenProducts': 'منتجات مخفية أو مؤرشفة',

    'pulse.sectionEyebrow': 'نشاط المنصة',
    'pulse.sectionTitle': 'نبض النظام',
    'pulse.sectionDesc': 'أحدث الأعمال المنجزة عبر منصتكم',
    'pulse.empty': 'لا توجد قيود مراجعة بعد.',
    'pulse.authLogin': 'اكتمل تسجيل الدخول الآمن',

    'ops.sectionEyebrow': 'التفاصيل التشغيلية',
    'ops.sectionTitle': 'تشغيل المنصة',
    'ops.sectionDesc': 'الضوابط خلف التوزيع والوصول وسلامة الإصدارات',
    'ops.customerAccess': 'وصول العملاء',
    'ops.customerAccessDetail': '{count} حسابات',
    'ops.licenseOps': 'عمليات التراخيص',
    'ops.licenseOpsDetail': '{count} صلاحيات',
    'ops.releasePipeline': 'مسار الإصدارات',
    'ops.releasePipelineDetail': '{count} عناوين برمجية',

    'customer.heroEyebrow': 'عميل BeCoder',
    'customer.heroTitle': 'برمجياتك جاهزة.',
    'customer.heroSubtitle': 'منتجات مرخّصة وتنزيلات آمنة وأجهزة موثوقة — كلها من مكان واحد.',
    'customer.openDownloads': 'فتح التنزيلات',
    'customer.trustStanding': 'الحساب بحالة جيدة',
    'customer.trustLicensed': 'وصول مرخّص',
    'customer.trustPrivate': 'توزيع آمن للبرامج',
    'customer.kpiProducts': 'المنتجات',
    'customer.kpiProductsSub': 'عناوين مرخّصة لحسابك',
    'customer.kpiLicenses': 'التراخيص',
    'customer.kpiLicensesSub': 'صلاحيات نشطة وسابقة',
    'customer.kpiDevices': 'الأجهزة النشطة',
    'customer.kpiDevicesSub': 'مقاعد مربوطة قيد الاستخدام',
    'customer.readyTitle': 'جاهز للاستخدام',
    'customer.readyDesc': 'البرمجيات المرخّصة لحسابك حاليًا',
    'customer.readyEmptyTitle': 'رحلة برمجياتك تبدأ من هنا.',
    'customer.readyEmptyBody': 'عندما ترخّص BeCoder برمجيات لحسابك، ستظهر في هذه الصفحة الرئيسية.',
    'customer.downloadsLink': 'التنزيلات',
    'customer.loadFailed': 'تعذّر التحميل',

    'home.eyebrow': 'الصفحة الرئيسية للعميل',
    'home.title': 'مرحباً بعودتك، {name}.',
    'home.subtitle': 'برمجياتك وتراخيصك وتحديثاتك ودعمك — في مكان واحد هادئ.',
    'home.exploreProducts': 'استكشف المنتجات',
    'home.announceTitle': 'أنت داخل BeCoder',
    'home.announceBody': 'هذه صفحتك الرئيسية كعميل. البرمجيات المملوكة والتنزيلات والتراخيص والمساعدة تعيش هنا — لا مبعثرة بين أدوات.',
    'home.dismiss': 'إخفاء',
    'home.kpi.products': 'برمجيات مرخّصة لك',
    'home.kpi.licenses': 'استحقاقات نشطة',
    'home.kpi.updatesLabel': 'تحديثات',
    'home.kpi.updates': 'جاهزة عندما تحتاجها',
    'home.ownedTitle': 'البرمجيات المملوكة',
    'home.ownedDesc': 'العناوين المرخّصة لهذا الحساب',
    'home.empty.productsTitle': 'رحلة برمجياتك تبدأ من هنا.',
    'home.empty.productsBody': 'تصفّح منظومة BeCoder، ثم اطلب من المبيعات أو المسؤول ترخيص ما تحتاجه.',
    'home.updatesTitle': 'تحديثات متاحة',
    'home.updatesDesc': 'ملاحظات الإصدار للبرمجيات التي تملكها',
    'home.empty.updatesTitle': 'ستظهر التحديثات عند نشر الإصدارات.',
    'home.empty.updatesBody': 'المنتجات المرخّصة تنشر هنا أولاً نسخاً موقّعة.',
    'home.releaseNotesFallback': 'ملاحظات الإصدار متاحة عند التنزيل.',
    'home.activityTitle': 'النشاط الأخير',
    'home.activityDesc': 'التراخيص والأجهزة المرتبطة بحسابك',
    'home.activity.licenses': '{count} تراخيص على هذا الحساب',
    'home.activity.devices': '{count} أجهزة نشطة',
    'home.empty.licensesTitle': 'عند شراء البرمجيات، ستظهر تراخيصك هنا.',
    'home.empty.devicesTitle': 'ستظهر الأجهزة المرتبطة بعد أول تثبيت.',
    'home.resourcesTitle': 'الدعم والمصادر',
    'home.resourcesDesc': 'كل ما يتوقعه العميل قريباً',
    'home.guide.docs': 'الوثائق',
    'home.guide.docsBody': 'أدلة منتجات مكتوبة لمن يشغّل البرمجيات.',
    'home.guide.support': 'الدعم',
    'home.guide.supportBody': 'تواصل مع BeCoder عندما تحتاج شخصاً.',
    'home.guide.community': 'قاعدة المعرفة',
    'home.guide.communityBody': 'إجابات عملية من قاعدة معرفة BeCoder.',
    'home.guide.invoices': 'الفواتير',
    'home.guide.invoicesBody': 'تظهر السجلات التجارية هنا بعد تأكيد الاتفاقات.',

    'welcome.skip': 'تخطّي',
    'welcome.skipStep': 'تخطّي الآن',
    'welcome.back': 'رجوع',
    'welcome.continue': 'متابعة',
    'welcome.enter': 'ادخل BeCoder',
    'welcome.learnMore': 'اعرف المزيد',
    'welcome.selectProduct': 'اختر {name} كمنتجك المفضّل',
    'welcome.catalogError': 'تعذّر تحميل كتالوج المنتجات. تقدّمك في الترحيب محفوظ.',
    'welcome.catalogEmpty': 'ستظهر منتجات BeCoder الجديدة هنا فور نشرها.',
    'welcome.saveError': 'تعذّر حفظ هذه الخطوة. تحقق من اتصالك قبل الإنهاء؛ لم يتغيّر حسابك.',
    'welcome.progress': 'الخطوة {current} من {total}',
    'welcome.step1.eyebrow': 'مرحباً',
    'welcome.step1.title': 'مرحباً بك في BeCoder.',
    'welcome.step1.body': 'مكان واحد لاكتشاف وإدارة والنمو مع برمجيات أعمال احترافية.',
    'welcome.step2.title': 'تعرّف على المنظومة.',
    'welcome.step2.body': 'كل منتج مبني لمهمة واضحة. ابدأ من أي مكان — والباقي يبقى قريباً.',
    'welcome.product.glasspos': 'نقطة بيع يومية سريعة.',
    'welcome.product.ledger': 'مراقبة مالية للأعمال.',
    'welcome.product.befx': 'برنامج متخصص لمكاتب الصرافة.',
    'welcome.product.iqstore': 'منتجات وكتالوج ومخزون.',
    'welcome.status.available': 'متاح',
    'welcome.step3.title': 'ماذا تود استكشافه أولاً؟',
    'welcome.step3.body': 'اختر نقطة بداية. يمكنك دائماً العودة إلى هذه الصفحة الرئيسية.',
    'welcome.start.products': 'استكشف المنتجات',
    'welcome.start.portal': 'افتح بوابة العميل',
    'welcome.start.licenses': 'إدارة التراخيص',
    'welcome.start.docs': 'زيارة الوثائق',
    'welcome.start.dashboard': 'المتابعة إلى لوحة التحكم',
    'welcome.step4.title': 'اجعلها لك.',
    'welcome.step4.body': 'تفضيلات اختيارية. تخطَّ أي شيء — وBeCoder تبقى كأنها بيتك.',
    'welcome.field.name': 'الاسم المعروض',
    'welcome.field.language': 'اللغة',
    'welcome.field.theme': 'المظهر',
    'welcome.field.timezone': 'المنطقة الزمنية',
    'welcome.field.notifications': 'الإشعارات',
    'welcome.field.photoHint': 'صورتك الشخصية تأتي من Google عند الربط. يمكنك تعديلها لاحقاً في الملف الشخصي.',
    'welcome.theme.dark': 'داكن',
    'welcome.theme.light': 'فاتح',
    'welcome.theme.system': 'النظام',
    'welcome.notify.products': 'تحديثات المنتجات',
    'welcome.notify.security': 'تنبيهات الأمن',
    'welcome.notify.announcements': 'إعلانات الشركة',
    'welcome.step5.title': 'أنت جاهز.',
    'welcome.step5.body': 'فهمت BeCoder. تعرف ماذا نبني. الآن ادخل المنصة.',
    'welcome.ready.title': 'أنت في الداخل.',
    'welcome.ready.body': 'نأخذك إلى نقطة البداية…',
    'welcome.reset': 'إعادة تجربة الترحيب',
    'welcome.resetDone': 'أُعيدت تجربة الترحيب. سجّل الدخول مجدداً لإعادة عرضها.',

    'portal.customers': 'العملاء',
    'portal.products': 'المنتجات',
    'portal.releases': 'الإصدارات',
    'portal.licenses': 'التراخيص',
    'portal.devices': 'الأجهزة',
    'portal.auditLogs': 'سجلات المراجعة',
    'portal.featureFlags': 'خصائص المنصة',
    'portal.myProducts': 'منتجاتي',
    'portal.downloadsTitle': 'التنزيلات والتحديثات',
    'portal.downloadsDesc': 'روابط تنزيل موقعة ومنتهية الصلاحية للإصدارات المرخّصة',
    'portal.profile': 'الملف الشخصي',
    'portal.supportTitle': 'الدعم والوثائق',
    'portal.helpTitle': 'المساعدة',
    'portal.helpLede': 'اطّلع على وثائق المنتجات أو تواصل مع مبيعات BeCoder للدعم البشري.',
    'portal.helpSalesBody': 'أسئلة الترخيص والتجارب والجانب التجاري عبر تواصل المبيعات.',
    'portal.downloadsEmpty': 'لا توجد منتجات مملوكة بعد — استكشف الكتالوج لبدء تجربة أو تواصل مع المبيعات.',
    'portal.softLaunchTitle': 'تسليم التطبيق غير متاح بعد عبر الإنترنت',
    'portal.softLaunchTitleNamed': '{name} غير متاح للإطلاق المباشر بعد',
    'portal.softLaunchBody':
      'صلاحيتك نشطة. استخدم التنزيلات عند نشر الإصدارات، أو تواصل مع المبيعات للتسليم.',
    'portal.softLaunchNoReleases':
      'لا توجد حزم تثبيت منشورة لهذا المنتج بعد. تواصل مع المبيعات إن احتجت نسخة.',
    'portal.noLicenses': 'عند شراء البرمجيات، ستظهر تراخيصك هنا.',
    'portal.noPublishedReleases': 'ستظهر الإصدارات المنشورة هنا عندما تكون النسخ جاهزة لك.',
    'portal.noOwnedProducts': 'رحلة برمجياتك تبدأ من هنا. استكشف الكتالوج ثم رخّص ما يناسبك.',
    'portal.createCustomer': 'إنشاء عميل',
    'portal.createProduct': 'إنشاء منتج',
    'portal.createUpload': 'إنشاء ورفع',
    'portal.issueLicense': 'إصدار ترخيص',
    'portal.registerDevice': 'تسجيل جهاز',
    'portal.suspend': 'تعليق',
    'portal.activate': 'تفعيل',
    'portal.publish': 'نشر',
    'portal.revoke': 'إلغاء',
    'portal.forceDeactivate': 'إلغاء التفعيل فورًا',
    'portal.remove': 'إزالة',
    'portal.signOut': 'تسجيل الخروج',
    'portal.download': 'تنزيل',
    'portal.name': 'الاسم',
    'portal.email': 'البريد',
    'portal.password': 'كلمة المرور (١٠+)',
    'portal.slug': 'المعرّف',
    'portal.productName': 'الاسم',
    'portal.tagline': 'الوصف المختصر',
    'portal.description': 'الوصف (Markdown)',
    'portal.version': 'الإصدار',
    'portal.changelog': 'سجل التغييرات',
    'portal.architecture': 'المعمارية',
    'portal.deviceName': 'اسم الجهاز',
    'portal.appVersion': 'إصدار التطبيق',
    'portal.product': 'المنتج',
    'portal.key': 'المفتاح',
    'portal.type': 'النوع',
    'portal.seats': 'المقاعد',
    'portal.ends': 'ينتهي',
    'portal.lifetime': 'مدى الحياة',
    'portal.noFile': 'لا ملف',
    'portal.on': 'مفعّل',
    'portal.off': 'معطّل',
    'portal.actor': 'الفاعل',
    'portal.action': 'الإجراء',
    'portal.resource': 'المورد',
    'portal.time': 'الوقت',
    'portal.allEvents': 'الكل',
    'portal.authEvents': 'الدخول',
    'portal.licenseEvents': 'التراخيص',
    'portal.downloadEvents': 'التنزيلات',
    'portal.subscriptionsDesc': 'تراخيص يصدرها مسؤولو BeCoder بعد تأكيد الشراء خارج المنصة.',
    'portal.subscriptionsEmpty': 'لا اشتراكات بعد. تواصل مع المبيعات للحصول على ترخيص.',
    'portal.invoicesEmpty': 'تظهر الفواتير هنا بعد تسجيل الاتفاقات التجارية. تواصل مع المبيعات لتفاصيل الفوترة.',
    'portal.supportEmpty': 'افتح الوثائق لأدلة المنتجات، أو تواصل مع المبيعات للمساعدة التجارية.',
    'portal.profileSignedIn': 'مسجّل الدخول باسم',
    'portal.profileRole': 'الدور',
    'security.title': 'الهوية والأمان',
    'security.subtitle': 'أدِر طرق تسجيل الدخول إلى BeCoder.',
    'security.pageDesc': 'تفاصيل الحساب وأمان المصادقة.',
    'security.methods': 'طرق تسجيل الدخول',
    'security.status': 'حالة الحساب',
    'security.connected': 'متصل',
    'security.notConnected': 'غير متصل',
    'security.connectGoogle': 'ربط Google',
    'security.disconnectGoogle': 'فصل Google',
    'security.replaceGoogle': 'استبدال حساب Google',
    'security.emailPassword': 'البريد وكلمة المرور',
    'security.createdVia': 'أُنشئ الحساب عبر',
    'security.emailVerified': 'البريد موثّق',
    'security.lastSignIn': 'آخر تسجيل دخول ناجح',
    'security.lastProvider': 'آخر مزود مصادقة',
    'security.yes': 'نعم',
    'security.no': 'لا',
    'security.unlockTitle': 'أكّد هويتك',
    'security.unlockBody': 'أعد المصادقة قبل تغيير إعدادات الهوية.',
    'security.confirmPassword': 'التأكيد بكلمة المرور',
    'security.confirmGoogle': 'التأكيد عبر Google',
    'security.unlocked': 'تم فتح إعدادات الأمان لفترة قصيرة.',
    'security.reauthRequired': 'أعد المصادقة قبل المتابعة.',
    'security.reauthOk': 'تم تأكيد الهوية',
    'security.reauthOkBody': 'يمكنك تحديث إعدادات الأمان لعدة دقائق.',
    'security.reauthFailed': 'فشل إعادة المصادقة',
    'security.addPassword': 'إضافة كلمة مرور',
    'security.changePassword': 'تغيير كلمة المرور',
    'security.newPassword': 'كلمة مرور جديدة',
    'security.confirmNewPassword': 'تأكيد كلمة المرور الجديدة',
    'security.passwordMismatch': 'يجب أن تتطابق كلمات المرور وأن لا تقل عن 10 أحرف.',
    'security.passwordAdded': 'تمت إضافة كلمة المرور',
    'security.passwordChanged': 'تم تحديث كلمة المرور',
    'security.googleLinked': 'تم ربط Google',
    'security.googleReplaced': 'تم استبدال حساب Google',
    'security.googleDisconnected': 'تم فصل Google',
    'security.needOtherMethod': 'أضف كلمة مرور قبل فصل Google.',
    'security.sessions': 'الجلسات النشطة',
    'security.signOutAll': 'تسجيل الخروج من كل الأجهزة الأخرى',
    'security.thisDevice': 'هذا الجهاز',
    'security.unknownDevice': 'جهاز غير معروف',
    'security.lastSeen': 'آخر ظهور',
    'security.revokeSession': 'إنهاء',
    'security.sessionRevoked': 'تم إنهاء الجلسة',
    'security.sessionsRevoked': 'تم إنهاء الجلسات الأخرى',
    'security.noSessions': 'لا توجد جلسات نشطة.',
    'portal.kbSearch': 'ابحث في مقالات المساعدة...',
    'portal.kbViews': '{count} مشاهدة',
    'portal.blogDesc': 'تحديثات هندسية وعمق معماري وسجلات المنتجات.',
    'portal.kbDesc': 'ابحث في أدلة الدعم وتشخيص التراخيص وإعداد المؤسسات.',

    'status.pageTitle': 'حالة الخدمات',
    'status.pageSubtitle': 'إشارات حية من خدمات منصة BeCoder.',
    'status.apiLiveness': 'حيوية الواجهة',
    'status.apiReadiness': 'جاهزية الواجهة',
    'status.database': 'قاعدة البيانات',
    'status.marketplace': 'المتجر',
    'status.billing': 'مزودو الدفع',
    'status.website': 'موقع BeCoder',
    'status.catalogService': 'كتالوج البرامج',
    'status.accounts': 'حساب العميل',
    'status.downloadsLicensing': 'التنزيلات والترخيص',
    'status.payments': 'المدفوعات',
    'status.notAvailableYet': 'غير متاح حاليًا',
    'status.comingSoonNeutral': 'قريبًا',

    'about.title': 'عن BeCoder',
    'about.heroTitle': 'برمجيات أعمال عملية.',
    'about.private': 'خاص',
    'about.privateDesc': 'ليست سوقًا عامة. منتجاتنا فقط.',
    'about.licensed': 'مرخّص',
    'about.licensedDesc': 'صلاحيات وأجهزة وإصدارات — تحت السيطرة.',
    'about.deliberate': 'واعٍ',
    'about.deliberateDesc': 'أسعار واضحة في صفحات المنتجات. وشراء موجّه عندما تكون جاهزًا.',
    'about.contactTitle': 'تواصل',
    'about.inquiryTitle': 'استفسار عام',
    'about.messageLabel': 'الرسالة',
    'about.messageNoted': 'تم استلام الرسالة',
    'about.messageBody': 'تُعالَج الاستفسارات العامة عبر صناديق المبيعات/الدعم المهيأة في البيئة.',
    'about.placeholder': 'كيف يمكننا المساعدة؟',
    'about.send': 'إرسال الرسالة',

    'pricing.starter': 'الانطلاقة',
    'pricing.starterBlurb': 'منتج واحد. فريق مركّز.',
    'pricing.starter1': 'برنامج واحد',
    'pricing.starter2': 'حتى ٣ أجهزة',
    'pricing.starter3': 'دعم عبر البريد',
    'pricing.team': 'الفريق',
    'pricing.teamBlurb': 'عدة أشخاص وأجهزة على حساب واحد.',
    'pricing.team1': 'عدة منتجات',
    'pricing.team2': 'تحكم بمن يستخدم أي جهاز',
    'pricing.team3': 'مسار دعم بأولوية',
    'pricing.enterprise': 'المؤسسات',
    'pricing.enterpriseBlurb': 'اتفاقيات مخصصة. تحكم كامل.',
    'pricing.enterprise1': 'حدود مستخدمين وأجهزة مخصصة',
    'pricing.enterprise2': 'تهيئة مخصّصة',
    'pricing.enterprise3': 'فوترة يدوية',
    'pricing.featured': 'موصى به',
    'pricing.contact': 'تواصل للشراء',
    'pricing.noCheckoutNote': 'راجع أسعار المنتجات في الكتالوج، أو تواصل مع المبيعات لمسار موجّه.',

    'plan.name.starter': 'الانطلاقة',
    'plan.name.growth': 'النمو',
    'plan.name.enterprise': 'المؤسسات',
    'plan.period.month': 'شهر',
    'plan.period.year': 'سنة',
    'plan.feature.coreAccess': 'وصول أساسي إلى {name}',
    'plan.feature.coreAccessShort': 'وصول أساسي',
    'plan.feature.emailSupport': 'دعم عبر البريد',
    'plan.feature.oneOrg': 'مؤسسة واحدة',
    'plan.feature.everythingStarter': 'كل ما في الانطلاقة',
    'plan.feature.prioritySupport': 'دعم بأولوية',
    'plan.feature.moreSeats': 'مقاعد إضافية',
    'plan.feature.advancedReports': 'تقارير متقدمة',
    'plan.feature.customTerms': 'شروط مخصصة',
    'plan.feature.dedicatedSuccess': 'نجاح مخصص',
    'plan.feature.ssoReady': 'مسار جاهز لتسجيل الدخول الموحّد',
    'plan.feature.slaOptions': 'خيارات اتفاقية مستوى الخدمة',
    'plan.feature.fastCheckout': 'دفع سريع مع عمل دون اتصال',
    'plan.feature.inventorySync': 'مزامنة المخزون والسجلات المتعددة',
    'plan.feature.receiptsShifts': 'إيصالات ونوبات وأدوار الموظفين',
    'plan.feature.tabletDesktop': 'يعمل على الجهاز اللوحي وسطح المكتب',
    'plan.feature.booksParties': 'دفاتر وأطراف وإغلاق الفترة',
    'plan.feature.doubleEntry': 'مساحة عمل هادئة بالقيد المزدوج',
    'plan.feature.statements': 'كشوفات بلا ضوضاء',
    'plan.feature.buySell': 'شراء وبيع بانضباط الخزنة',
    'plan.feature.remittances': 'حوالات وإغلاق يومي',
    'plan.feature.auditExchange': 'عمليات صرافة جاهزة للتدقيق',
    'plan.feature.imageCatalog': 'كتالوج منتجات جاهز للصور',
    'plan.feature.ordersOps': 'طلبات وعمليات المتجر',
    'plan.feature.regionalRetail': 'مصمّم لتجارة التجزئة الإقليمية',
    'plan.feature.doubleEntryClarity': 'وضوح القيد المزدوج بلا فوضى',
    'plan.feature.exportStatements': 'كشوفات جاهزة للتصدير',
    'plan.feature.growingTeams': 'مصمّم للفرق النامية',
    'plan.feature.buySellLive': 'شراء/بيع بأسعار مباشرة',
    'plan.feature.vaultRemittance': 'سير عمل الخزنة والحوالات',
    'plan.feature.auditDailyClose': 'إغلاق يومي جاهز للتدقيق',
    'plan.feature.multiLangCounter': 'واجهة عدّاد متعددة اللغات',
    'plan.feature.catalogOrders': 'عمليات الكتالوج والطلبات',
    'plan.feature.imagePages': 'صفحات منتجات جاهزة للصور',
    'plan.feature.roleStore': 'إدارة متجر حسب الأدوار',
    'plan.feature.designedRetail': 'مصمّم لتجارة التجزئة الإقليمية',
    'plan.feature.liveScores': 'نتائج مباشرة ونبض المباريات',
    'plan.feature.fanNotifications': 'إشعارات تهم المشجعين',
    'plan.feature.sportsUi': 'واجهة رياضية هادئة',
    'plan.feature.mobileFirst': 'تجربة أولاً للجوال',
    'plan.feature.privateDist': 'توزيع آمن لبرامجك',
    'plan.feature.licensesDevices': 'تراخيص وأجهزة وإصدارات',
    'plan.feature.orgIdentity': 'هوية جاهزة للمؤسسات',
    'plan.feature.ecosystem': 'مصمّم لمنظومة BeCoder',
    'plan.category.application': 'تطبيق',
    'plan.category.pos': 'نقطة بيع',
    'plan.category.finance': 'مالية',
    'plan.category.exchange': 'صرافة',
    'plan.category.pharmacy': 'صيدلة',
    'plan.category.commerce': 'تجارة',
    'plan.category.sports': 'رياضة',
    'plan.category.platform': 'منصة',
    'status.active': 'نشط',
    'status.trialing': 'تجريبي',
    'status.trial': 'تجربة',
    'status.canceled': 'ملغى',
    'status.cancelled': 'ملغى',
    'status.past_due': 'متأخر',
    'status.incomplete': 'غير مكتمل',
    'status.expired': 'منتهٍ',
    'status.pending': 'قيد الانتظار',
    'status.paid': 'مدفوع',
    'status.open': 'مفتوح',
    'status.revoked': 'ملغى الصلاحية',
    'status.suspended': 'معلّق',
    'hub.orgNameDefault': 'مؤسسة {name}',
    'hub.launchFailedShort': 'فشل الإطلاق',
    'portal.provider.fastpay': 'FastPay',
    'portal.provider.manual': 'يدوي',
    'portal.provider.none': 'لا يوجد',
    'portal.provider.google': 'Google',
    'content.seed.glasspos.tagline': 'نظام نقاط بيع سريع وموثوق للأعمال الحديثة.',
    'content.seed.glasspos.description':
      'GlassPOS هو تطبيق نقطة البيع من BeCoder — دفع سريع ووضوح في المخزون وسجلات جاهزة للعمل دون اتصال.',
    'content.seed.glasspos.descriptionShort': 'GlassPOS هو تطبيق نقطة البيع من BeCoder.',
    'content.seed.ledger.tagline': 'دفاتر هادئة وصحيحة.',
    'content.seed.ledger.description':
      'يجلب Ledger المالية بالقيد المزدوج إلى مساحة عمل هادئة — أطراف وفترات وكشوفات بلا ضوضاء.',
    'content.seed.ledger.descriptionShort': 'يجلب Ledger المالية بالقيد المزدوج إلى مساحة عمل هادئة.',
    'content.seed.befx.tagline': 'عمليات صرافة بانضباط الخزنة.',
    'content.seed.befx.description':
      'يدير BeFX الشراء/البيع والخزائن والحوالات مع إغلاق يومي جاهز للتدقيق لمكاتب الصرافة.',
    'content.seed.befx.descriptionShort': 'يدير BeFX الشراء/البيع والخزائن والحوالات.',
    'content.seed.iqStore.tagline': 'كتالوج تجاري مصمّم لتجارة التجزئة الإقليمية.',
    'content.seed.iqStore.description':
      'يدير IQ Store المنتجات والطلبات وعمليات المتجر مع تجربة كتالوج جاهزة للصور.',
    'content.seed.iqStore.descriptionShort': 'يدير IQ Store المنتجات والطلبات وعمليات المتجر.',
    'content.seed.aura.tagline': 'نبض رياضي مباشر للمشجعين.',
    'content.seed.aura.description':
      'يقدّم Aura Sport طاقة المباريات بواجهة هادئة — نتائج وتنبيهات وتجربة رياضية أولاً للجوال.',

    'docs.quickstart': 'البدء السريع عبر الطرفية',
    'docs.auth': 'المصادقة',
    'docs.sdkC': 'تضمين C / C++',
    'docs.offline': 'التحقق دون اتصال',
    'docs.title': 'الوثائق',
    'docs.products': 'وثائق المنتجات',
    'docs.productsBody': 'أدلة وتفاصيل المنتجات من كتالوج برمجيات BeCoder الحالي.',
    'docs.openProductDocs': 'فتح الوثائق',
    'docs.viewProduct': 'عرض المنتج',
    'docs.developers': 'مساعدة المنتجات',
    'docs.subtitle':
      'مساعدة وتوثيق لمنتجات BeCoder — مرتبة حسب البرنامج، بلغة بسيطة للعملاء.',
    'docs.rust': 'حزمة Rust',
    'docs.rustTitle': 'ملاحظات حزمة Rust',
    'docs.cTitle': 'ملاحظات تضمين C / C++',
    'docs.offlineTitle': 'التفعيل دون اتصال',

    'status.operations': 'الحالة',
    'status.notReady': 'غير جاهز ({reason})',
    'status.databaseNamed': 'قاعدة البيانات ({name})',
    'status.sampleAt': 'تم الفحص · {time}',
    'portal.kbEmpty':
      'مقالات المساعدة غير منشورة بعد. استخدم وثائق المنتجات أو تواصل المبيعات للمساعدة.',
    'portal.kbEmptyDocs': 'تصفح وثائق المنتجات من كتالوج BeCoder.',
    'portal.kbEmptySales': 'تواصل مع BeCoder عبر تواصل المبيعات بينما تُجهَّز صناديق الدعم.',
    'docs.quickstartPending':
      'ستظهر أدلة التثبيت والتفعيل هنا عند نشر حزم إصدارات المنتجات.',
    'docs.sdkPending':
      'ستُنشر ملاحظات تكامل SDK مع وثائق الترخيص لكل منتج.',
    'docs.offlinePending':
      'ستُوثَّق خطوات التفعيل دون اتصال عند توفر حزم الترخيص دون اتصال.',
    'status.liveChecks':
      'نظرة سريعة على خدمات BeCoder التي يستخدمها العملاء — الموقع، كتالوج البرامج، الحساب، التنزيلات، والمدفوعات.',

    'soon.organizations': 'المؤسسات',
    'soon.organizationsBody': 'استخدم العملاء لإدارة الحسابات في هذه الدورة.',
    'soon.moderation': 'الإشراف',
    'soon.moderationBody': 'إشراف المتجر ليس ضمن هذه الدورة.',
    'soon.organizationBody': 'إدارة المؤسسة مؤجّلة.',
    'soon.checkout': 'الدفع',
    'soon.checkoutBody': 'مزودو الدفع غير مدمجين في هذه الدورة.',
    'soon.cartBody': 'الدفع معطّل حتى تفعيل الفوترة.',
    'soon.developerBody': 'النشر من طرف ثالث معطّل.',
    'soon.wishlist': 'قائمة الأمنيات',
    'account.title': 'حسابي',
    'account.subtitle': 'أدِر ملف المسؤول وكلمة المرور.',
    'account.profile': 'معلومات الملف',
    'account.details': 'تفاصيل الحساب',
    'account.displayName': 'الاسم الظاهر',
    'account.role': 'الدور',
    'account.save': 'حفظ التغييرات',
    'account.saved': 'تم حفظ الملف',
    'account.savedBody': 'تم تحديث الاسم الظاهر.',
    'account.nameTooShort': 'يجب أن يكون الاسم الظاهر حرفين على الأقل.',
    'account.passwordHelp':
      'أكّد كلمة المرور الحالية ثم عيّن كلمة جديدة. سيتم تسجيل الخروج من الجلسات الأخرى بعد تغيير كلمة المرور.',
    'account.passwordChangedBody': 'استخدم كلمة المرور الجديدة في المرة القادمة.',
    'theme.light': 'فاتح',
    'theme.dark': 'داكن',
    'theme.system': 'النظام',
    'nav.language': 'اللغة',
    'nav.theme': 'المظهر',
    'nav.menu': 'القائمة',

    'portal.details': 'التفاصيل',
    'portal.licensePrefix': 'الترخيص:',
    'portal.lastSeen': 'آخر ظهور {date}',
    'portal.register': 'تسجيل',
    'portal.flagsNote':
      'المتجر والدفع يبقيان مرفوضين بسياسة الواجهة في هذه الدورة حتى عند التبديل. بوابات البيئة تسري أيضًا.',
    'portal.noInAppPayments': 'لا مدفوعات داخل التطبيق',
    'portal.supportDeferred':
      'مسارات التذاكر مؤجّلة. استخدم وثائق المنتج وروابط الدعم أدناه.',
    'portal.invoicesDeferred': 'سيظهر سجل الفواتير هنا عند ربط مزود فوترة.',
    'portal.invoicesNoGateway':
      'لا بوابة دفع في هذا الإصدار. المشتريات عبر تواصل المبيعات.',
    'portal.auditPlatform': 'المنصة',
    'portal.seatsCount': 'مقاعد {count}',
    'portal.endsPrefix': 'ينتهي {date}',
    'portal.signedOut': 'تم تسجيل الخروج',
    'portal.sessionCleared': 'تم إنهاء الجلسة',
    'command.paletteAria': 'لوحة الأوامر',

    'license.modalTitle': 'صلاحية الترخيص',
    'license.modalBody':
      'تفعيل السوق الذاتي غير متاح. يصدر المسؤولون التراخيص وتظهر ضمن تراخيصي بعد تسجيل الدخول.',
    'license.placeholder': 'مفتاح الترخيص (للمرجع فقط)',
    'license.unavailableTitle': 'غير متاح',
    'license.unavailableBody': 'تواصل مع المسؤول لإصدار التراخيص أو إدارتها.',

    'ai.modalBody':
      'مزودو الذكاء الاصطناعي الخارجيون غير مدمجين في هذه الدورة. استخدم الوثائق وبوابة العميل للمساعدة.',

    'command.home': 'الرئيسية',
    'command.signIn': 'تسجيل الدخول',
    'command.adminPortal': 'بوابة الإدارة',
    'command.customerPortal': 'بوابة العميل',
    'command.marketplaceSoon': '{label} (قريبًا)',

    'concept.pos.category': 'نقطة بيع حديثة لقطاع التجزئة',
    'concept.pos.checkout': 'نقطة البيع',
    'concept.pos.order': 'الطلب الحالي',
    'concept.pos.total': 'الإجمالي',
    'concept.ledger.category': 'المراقبة المالية ومتابعة الأعمال',
    'concept.ledger.overview': 'نظرة مالية شاملة',
    'concept.ledger.balance': 'صافي الرصيد',
    'concept.ledger.income': 'الإيرادات',
    'concept.ledger.expenses': 'المصروفات',
    'concept.befx.category': 'إدارة عمليات الصرافة',
    'concept.befx.rates': 'مكتب الصرافة',
    'concept.befx.pair': 'زوج العملات',
    'concept.befx.buy': 'شراء',
    'concept.befx.sell': 'بيع',
    'concept.store.category': 'إدارة كتالوج المنتجات والمخزون',
    'concept.store.inventory': 'مخزون المنتجات',
    'concept.store.inStock': 'متوفر',
    'concept.store.lowStock': 'مخزون منخفض',

    'product.status.active': 'منشور',
    'product.status.hidden': 'تجريبي',
    'product.status.coming_soon': 'قريبًا',
    'product.status.archived': 'مؤرشف',
    'common.dayAgo': 'قبل يوم واحد',
    'release.policy.optional': 'اختياري',
    'release.policy.mandatory': 'إلزامي',
    'content.seed.studio.tagline': 'عميل خاص لتوزيع برمجيات BeCoder',
    'content.seed.studio.description':
      'BeCoder Studio هو تطبيقنا الرئيسي للوصول المرخّص عبر الويب/PWA وWindows وAndroid APK وiOS/PWA.',
    'content.seed.studio.changelog': 'الإصدار الخاص الأول من BeCoder Studio لنظام Windows x64.',
    'product.detail.platforms': 'المنصات',
    'product.detail.requirements': 'متطلبات النظام',
    'product.detail.releases': 'الإصدارات',
    'common.notFound': 'غير موجود',
    'portal.releaseFile': 'ملف الإصدار',
    'common.retry': 'إعادة المحاولة',

    'cc.controlCenter': 'مركز التحكم',
    'cc.dashboardTitle': 'نظرة تشغيلية',
    'cc.dashboardIntro': 'حالة المنصة من بيانات الاستحقاقات والكتالوج والتدقيق الفعلية.',
    'cc.rolePlatformAdmin': 'مسؤول المنصة',
    'cc.collapseSidebar': 'طي الشريط',
    'cc.expandSidebar': 'توسيع الشريط',
    'cc.apiOperational': 'واجهة البرمجة تعمل',
    'cc.apiUnavailable': 'واجهة البرمجة غير متاحة',
    'cc.loadFailed': 'تعذر تحميل نظرة مركز التحكم. حاول مجددًا.',
    'cc.kpis': 'مؤشرات تشغيلية',
    'cc.kpi.customers': 'العملاء',
    'cc.kpi.activeLicenses': 'تراخيص نشطة',
    'cc.kpi.activeTrials': 'تجارب نشطة',
    'cc.kpi.activeSubs': 'اشتراكات نشطة',
    'cc.kpi.activeDevices': 'أجهزة نشطة',
    'cc.kpi.publishedProducts': 'منتجات منشورة',
    'cc.attentionTitle': 'يتطلب انتباهًا',
    'cc.attentionNone': 'لا توجد عناصر حرجة.',
    'cc.attention.expiringTrials': '{count} تجارب تنتهي خلال 7 أيام',
    'cc.attention.suspendedLicenses': '{count} تراخيص معلّقة',
    'cc.attention.revokedLicenses': '{count} تراخيص ملغاة',
    'cc.attention.expiredSubs': '{count} اشتراكات منتهية',
    'cc.attention.missingPricing': '{name}: تسعير ناقص',
    'cc.attention.incompleteLocales': '{name}: محتوى لغة غير مكتمل',
    'cc.attention.noRelease': '{name}: لا يوجد إصدار منشور',
    'cc.attention.problemMissingPricing': 'تسعير ناقص',
    'cc.attention.problemIncompleteLocales': 'محتوى لغة غير مكتمل',
    'cc.attention.problemNoRelease': 'لا يوجد إصدار منشور',
    'cc.attention.openTrials': 'مراجعة التجارب',
    'cc.attention.openLicenses': 'إدارة التراخيص',
    'cc.attention.openSubs': 'مراجعة الاشتراكات',
    'cc.attention.openPricing': 'فتح التسعير',
    'cc.attention.openContent': 'فتح المحتوى',
    'cc.attention.openReleases': 'إدارة الإصدارات',
    'cc.kpi.hint.directory': 'دليل العملاء',
    'cc.kpi.hint.entitlements': 'تراخيص سارية',
    'cc.kpi.hint.trials': 'قيد التشغيل',
    'cc.kpi.hint.billing': 'فوترة نشطة',
    'cc.kpi.hint.devices': 'أجهزة مربوطة',
    'cc.kpi.hint.catalog': 'كتالوج منشور',
    'cc.quickActions': 'إجراءات سريعة',
    'cc.action.findCustomer': 'البحث عن عميل',
    'cc.action.issueLicense': 'إصدار ترخيص',
    'cc.action.createRelease': 'إنشاء إصدار',
    'cc.action.manageProducts': 'إدارة المنتجات',
    'cc.action.uploadMedia': 'رفع وسائط',
    'cc.action.downloadAudit': 'تدقيق التنزيلات',
    'cc.recentActivity': 'النشاط الأخير',
    'cc.viewAuditLog': 'عرض سجل التدقيق',
    'cc.noActivity': 'لا يوجد نشاط إداري حديث.',
    'cc.activity.system': 'النظام',
    'cc.activity.aProduct': 'منتج',
    'cc.activity.aLicense': 'ترخيص',
    'cc.activity.aCustomer': 'عميل',
    'cc.activity.byActor': 'بواسطة {actor}',
    'cc.activity.mediaUploaded': 'تم رفع وسائط إلى {name}',
    'cc.activity.mediaScreenshot': 'تم رفع لقطة شاشة إلى {name}',
    'cc.activity.mediaIcon': 'تم رفع أيقونة إلى {name}',
    'cc.activity.mediaHero': 'تم رفع صورة رئيسية إلى {name}',
    'cc.activity.mediaRemoved': 'تمت إزالة وسائط من {name}',
    'cc.activity.mediaScreenshotRemoved': 'تمت إزالة لقطة شاشة من {name}',
    'cc.activity.licenseIssued': 'تم إصدار ترخيص لـ {name}',
    'cc.activity.licenseIssuedTo': 'تم إصدار ترخيص إلى {name}',
    'cc.activity.licenseSuspended': 'تم تعليق ترخيص ({name})',
    'cc.activity.licenseRevoked': 'تم إلغاء ترخيص ({name})',
    'cc.activity.licenseActivated': 'تم تفعيل ترخيص ({name})',
    'cc.activity.trialExtended': 'تم تمديد تجربة {name}',
    'cc.activity.trialForceGrant': 'تم منح تجربة {name} إجباريًا',
    'cc.activity.trialCanceled': 'تم إلغاء تجربة {name}',
    'cc.activity.priceUpdated': 'تم تحديث سعر {code} لـ {name}',
    'cc.activity.planCreated': 'تم إنشاء خطة {code} لـ {name}',
    'cc.activity.planToggled': 'تم تحديث نشاط خطة {code} لـ {name}',
    'cc.activity.productCore': 'تم تحديث حالة {name}',
    'cc.activity.commercialFlags': 'تم تحديث الإعدادات التجارية لـ {name}',
    'cc.activity.productPublished': 'تم نشر المحتوى التجاري لـ {name}',
    'cc.activity.draftSaved': 'تم حفظ مسودة {name}',
    'cc.activity.customerSuspended': 'تم تعليق العميل: {name}',
    'cc.activity.customerActivated': 'تم تفعيل العميل: {name}',
    'cc.activity.releasePublished': 'تم نشر إصدار لـ {name}',
    'cc.activity.releaseUnpublished': 'تم إلغاء نشر إصدار لـ {name}',
    'cc.activity.settingsUpdated': 'تم تحديث إعدادات المنصة',
    'cc.time.justNow': 'الآن',
    'cc.time.minutesAgo': 'قبل {count} دقيقة',
    'cc.time.hoursAgo': 'قبل {count} ساعة',
    'cc.time.yesterday': 'أمس',
    'cc.time.daysAgo': 'قبل {count} يوم',
    'cc.ready.content': 'المحتوى',
    'cc.ready.pricing': 'التسعير',
    'cc.ready.media': 'الوسائط',
    'cc.ready.trial': 'التجربة',
    'cc.ready.distribution': 'التوزيع',
    'cc.ready.ok': 'جاهز',
    'cc.ready.warn': 'يحتاج انتباهًا',
    'cc.ready.na': 'غير منطبق',
    'cc.empty.devices': 'لا توجد أجهزة نشطة بعد.',
    'cc.empty.downloads': 'لا يوجد نشاط تنزيل بعد.',
    'cc.empty.activity': 'لا يوجد نشاط إداري حديث.',
    'cc.settings.savedState': 'محفوظ',
    'cc.settings.saving': 'جارٍ الحفظ…',
    'cc.settings.saveFailed': 'فشل الحفظ',
    'cc.nav.overview': 'نظرة عامة',
    'cc.nav.customers': 'العملاء',
    'cc.nav.catalog': 'الكتالوج',
    'cc.nav.entitlements': 'الاستحقاقات',
    'cc.nav.access': 'الوصول',
    'cc.nav.distribution': 'التوزيع',
    'cc.nav.trust': 'الثقة والعمليات',
    'cc.nav.settings': 'الإعدادات',
    'cc.products.intro': 'إدارة منتجات الكتالوج وجاهزية المحتوى التجاري.',
    'cc.products.create': 'إنشاء منتج',
    'cc.products.manage': 'إدارة',
    'cc.access.issueLicense': '+ إصدار ترخيص',
    'cc.settings.advanced': 'متقدم',
    'cc.settings.advancedIntro': 'وجهات تقنية والحساب.',
    'cc.settings.accountHint': 'حساب المسؤول',
    'cc.settings.flagsHint': 'أعلام الميزات',
    'cc.settings.auditHint': 'سجل تدقيق الأمان',
    'cms.hub.overview': 'نظرة عامة',
    'cms.hub.content': 'المحتوى',
    'cms.hub.plans': 'التسعير',
    'cms.hub.trial': 'التجربة',
    'cms.hub.media': 'الوسائط',
    'cms.hub.distribution': 'التوزيع',
    'cc.settings.identity': 'هوية المنصة',
    'cc.settings.contact': 'التواصل',
    'cc.settings.about': 'نبذة',
    'cc.settings.social': 'التواصل الاجتماعي',
    'cc.settings.footer': 'التذييل',
    'cc.customerSince': 'عميل منذ',
    'cc.showMetadata': 'عرض التفاصيل',
    'cc.hideMetadata': 'إخفاء التفاصيل',
    'cc.unsaved': 'تغييرات غير محفوظة',
  },

  ckb: {
    'nav.about': 'دەربارەمان',
    'nav.openPortal': 'کردنەوەی دەروازە',
    'nav.customerPortal': 'دەروازەی کڕیار',
    'footer.platform': 'پلاتفۆرم',
    'footer.contact': 'پەیوەندی',
    'footer.privateDistribution': 'دابەشکردنی پارێزراوی سۆفتوێر',
    'common.home': 'سەرەتا',
    'common.comingSoon': 'بەم زووانە',
    'common.backHome': 'گەڕانەوە بۆ سەرەتا',
    'a11y.skipToContent': 'بازدان بۆ ناوەڕۆک',
    'common.error': 'هەڵە',
    'common.failed': 'سەرکەوتوو نەبوو',
    'common.loading': 'بارکردن',
    'common.tryAgain': 'دووبارە هەوڵبدە',
    'common.today': 'ئەمڕۆ',
    'common.earlier': 'پێشتر',
    'common.yesterday': 'دوێنێ',
    'common.daysAgo': 'پێش {count} ڕۆژ',
    'common.dateUnavailable': 'بەروار بەردەست نییە',
    'common.version': 'وەشان',
    'common.status': 'دۆخ',
    'common.published': 'بڵاوکراوەتەوە',
    'common.draft': 'ڕەشنووس',
    'common.beta': 'تاقیکاری',
    'common.archived': 'ئەرشیڤکراو',
    'common.open': 'کردنەوە',
    'common.manage': 'بەڕێوەبردن',
    'common.viewAll': 'بینینی هەموو',
    'common.fullLog': 'تۆماری تەواو',
    'common.confirm': 'پشتڕاستکردنەوە',
    'status.checking': 'لە پشکنیندایە',
    'status.operational': 'کارا',
    'status.unavailable': 'بەردەست نییە',
    'status.down': 'وەستاوە',
    'status.disabled': 'ناکارا',
    'status.stable': 'جێگیر',
    'status.inProgress': 'لە کاردا',
    'status.awaitingRelease': 'چاوەڕوانی وەشان',

    'landing.softwareEyebrow': 'سۆفتوێر',
    'landing.softwareTitle': 'سۆفتوێر لە BeCoder',
    'landing.softwareLede':
      'ئەمانە ئەو بەرهەمانەن کە BeCoder پێشکەشیان دەکات. هەر کارتێک بکەرەوە بۆ تایبەتمەندی و وێنە و نرخ.',
    'landing.catalogEmpty': 'کەتەلۆگ بەردەست نییە — API دەستپێبکە یان بچۆ ژوورەوە بۆ بینینی سۆفتوێری مۆڵەتدار.',
    'landing.bannerLabel': 'پەیامەکانی BeCoder',
    'landing.banner.1.title': 'ئێمە سۆفتوێری تایبەت بۆ بازرگانیت دروست دەکەین.',
    'landing.banner.1.body': 'لە کارگێڕییەوە تا خاوەندارێتی — سۆفتوێر بەپێی شێوازی کارت.',
    'landing.banner.2.title': 'هەموو لقەکانت لە یەک سیستەمدا ببەستەوە.',
    'landing.banner.2.body': 'یەک ناسنامەی پلاتفۆرم لە شوێن و تیم و بەرهەمەکاندا.',
    'landing.banner.3.title': 'چارەسەری کڵاود بۆ کۆمپانیا گەشەسەندووەکان.',
    'landing.banner.3.body': 'ئەپلیکەیشنی مۆڵەتدارى BeCoder کە لەگەڵ ڕێکخراوەکەت گەشە دەکات.',
    'landing.banner.4.title': 'یەک پلاتفۆرم بۆ هەموو کارەکانی بازرگانیت.',
    'landing.banner.4.body': 'بەرهەمەکانی کەتەلۆگ بگەڕێ، تاقیکردنەوە دەستپێبکە، و لە دەرگاکەتەوە هەڵیدە.',
    'landing.banner.5.title': 'چارەسەری ئاستی دامەزراوە بۆ ڕێکخراوەکان.',
    'landing.banner.5.body': 'دابەشکردنی پارێزراوی سۆفتوێر، مۆڵەتدانێکی ورد، و بەڕێوەبردنێکی متمانەپێکراو.',
    'landing.banner.6.title': 'پێویستت بە سیستەمی تایبەتە؟ ئێمە دروستی دەکەین.',
    'landing.banner.6.body': 'پێی فرۆشتن بڵێ پێویستت بە چییە — BeCoder دیزاین و گەیاندنی سۆفتوێر دەکات.',
    'landing.banner.cta.sales': 'پەیوەندی فرۆشتن',
    'landing.banner.cta.custom': 'داوای سۆفتوێری تایبەت',
    'landing.banner.cta.explore': 'چارەسەرەکان بگەڕێ',
    'landing.banner.cta.trial': 'تاقیکردنەوەی خۆڕایی دەستپێبکە',
    'landing.manageEyebrow': 'چارەسەرەکان',
    'landing.manageTitle': 'دەتەوێت چی بەڕێوەببەیت؟',
    'landing.manageLede':
      'جۆری بازرگانیت هەڵبژێرە. ئەگەر بەرنامە ئامادە بێت پەڕەی بەرهەم دەکرێتەوە. ئەگەر نا، بەم زووانە دەبینیت.',
    'landing.need.talkSales': 'پەیوەندی فرۆشتن',
    'landing.need.customPath': 'ڕێڕەوی تایبەت',
    'landing.need.retail.title': 'مینی مارکێت / سوپەرمارکێت',
    'landing.need.retail.body':
      'خاڵی فرۆشتن بۆ مینی مارکێت و سوپەرمارکێت — فرۆشتن و بەرهەم و پارەدانی ڕۆژانە لەگەڵ GlassPOS.',
    'landing.need.restaurant.title': 'چێشتخانە',
    'landing.need.restaurant.body':
      'سۆفتوێری چێشتخانە لە BeCoder بەم زووانە.',
    'landing.need.pharmacy.title': 'دەرمانخانە',
    'landing.need.pharmacy.body':
      'سۆفتوێری دەرمانخانە لە BeCoder بەم زووانە.',
    'landing.need.exchange.title': 'ئۆفیسی ئاڵوگۆڕ',
    'landing.need.exchange.body':
      'کڕین/فرۆشتنی دراو، ڕێژە و جووڵەی پارەی قاسە بۆ کارەکانی ئۆفیسی ئاڵوگۆڕ.',
    'landing.need.company.title': 'کۆمپانیا',
    'landing.need.company.body':
      'سۆفتوێری کارپێکردنی کۆمپانیا لە BeCoder بەم زووانە.',
    'landing.need.warehouse.title': 'کۆگا',
    'landing.need.warehouse.body': 'سۆفتوێری کۆگا لە BeCoder بەم زووانە.',
    'landing.need.factory.title': 'کارگە',
    'landing.need.factory.body': 'سۆفتوێری کارگە لە BeCoder بەم زووانە.',
    'landing.need.clinic.title': 'کلینیک',
    'landing.need.clinic.body': 'سۆفتوێری کلینیک لە BeCoder بەم زووانە.',
    'landing.need.institute.title': 'پەیمانگا',
    'landing.need.institute.body': 'سۆفتوێری پەیمانگا لە BeCoder بەم زووانە.',
    'landing.need.custom.title': 'سۆفتوێری تایبەت',
    'landing.need.custom.body': 'پێمان بڵێ پێویستت بە چییە — سیستەمەکە دیزاین و دروست دەکەین.',
    'landing.journeyEyebrow': 'چۆن کاردەکات',
    'landing.journeyTitle': 'لە دۆزینەوەوە تا کارکردنی سۆفتوێر.',
    'landing.journeyLede':
      'BeCoder پلاتفۆرمی سەرەکیی کۆمپانیایە. بەرهەمەکان لە کەتەلۆگن؛ مۆڵەت، داگرتن و کردنەوە لە پۆرتاڵەکەت بەڕێوەدەبەیت.',
    'landing.journey1Title': 'بگەڕێ',
    'landing.journey1Body': 'کەتەلۆگی سۆفتوێر بگەڕێ. پێش بڕیار بزانە هەر بەرهەمێک بۆ چییە.',
    'landing.journey2Title': 'ڕێگاکەت هەڵبژێرە',
    'landing.journey2Body': 'نرخەکان لە پەڕەی بەرهەم ببینە؛ پاشان ئابوونە بکە، تاقیکردنەوە دەستپێبکە، یان پەیوەندی بە فرۆشتن بکە.',
    'landing.journey3Title': 'لە پۆرتاڵەکەت بەڕێوەی ببە',
    'landing.journey3Body': 'بچۆ ژوورەوە بۆ بەڕێوەبردنی بەرهەم، کلیل، ئامێر، پسوڵە و پشتگیری لە یەک شوێن.',
    'landing.journey4Title': 'بکەرەوە و نوێ بکەرەوە',
    'landing.journey4Body': 'بەرنامە مۆڵەتدارەکان بکەرەوە و کاتێک وەشانێکی نوێ بڵاوکرایەوە بە پارێزراوی دایبگرە.',
    'landing.trustEyebrow': 'بۆچی BeCoder',
    'landing.trustTitle': 'دابەشکردنی پارێزراوی سۆفتوێر کە دەتوانیت متمانەی پێ بکەیت.',
    'landing.showcaseTitle': 'هەموو بەرهەمێک لە یەک شوێن ببینە.',
    'landing.showcaseLede':
      'پێشانگای بەرنامەکان کەتەلۆگی BeCoder بە ڕوونی ئارام پیشان دەدات — سەرەتا بگەڕێ، کاتێک ئامادە بوویت بڕیار بدە.',
    'landing.exploreShowcase': 'بەرنامەکان ببینە',
    'landing.exploreSoftware': 'بەرنامەکان ببینە',
    'landing.heroCatalogCaption': 'سۆفتوێر لە کەتەلۆگی BeCoder',
    'landing.portalEyebrow': 'دەرگای کڕیار',
    'landing.portalTitle': 'هەموو شتێک دوای مۆڵەت.',
    'landing.portalLede':
      'دوای وەرگرتنی مۆڵەت، بەرهەمەکان، داگرتن و پشتگیری لە یەک پۆرتاڵدا بەردەست دەبن.',
    'landing.portalLicenses': 'مۆڵەت و ئامێر',
    'landing.portalLicensesBody': 'مۆڵەتە چالاکەکان، سنووری بەکارهێنەر و ئامێرە بەستراوەکان بە ئاسانی بەڕێوەببە.',
    'landing.portalDownloads': 'داگرتن و نوێکردنەوە',
    'landing.portalDownloadsBody': 'بەستەری داگرتنی واژۆکراو و کاتی کۆتایی بۆ وەشانە مۆڵەتدارەکان دەردەکەون.',
    'landing.portalSupport': 'پشتگیری و تۆمار',
    'landing.portalSupportBody': 'تیکەت، بەڵگەنامە و پسوڵە لە تەنیشت ئەو سۆفتوێریەی بەکاری دەهێنیت.',
    'landing.commercialEyebrow': 'ڕێڕەوی بازرگانی',
    'landing.commercialTitle': 'ئامادە کاتێک تۆ ئامادەیت.',
    'landing.commercialBody':
      'سەرەتا بەرهەم و نرخ بگەڕێ. کاتێک بڕیارت دا، لە ڕێگەی BeCoder بەردەوام بە — بەشداری، تاقیکردنەوە، یان پەیوەندی بە فرۆشتن.',
    'landing.resourcesEyebrow': 'سەرچاوەکان',
    'landing.resourcesTitle': 'ڕێنمایی و دۆخی پلاتفۆرم.',
    'landing.resourcesLede': 'بخوێنەوە بەرهەمەکان چۆن کاردەکەن، دۆخی سیستەم بپشکنە، یان بزانە BeCoder چۆن کاردەکات.',
    'landing.resourceDocs': 'ڕێنمایی بەرهەم بۆ ئەوانەی سۆفتوێر بەکاردەهێنن.',
    'landing.resourceKb': 'وەڵامی کرداری لە بنکەی زانیاری BeCoder.',
    'landing.resourceStatus': 'دۆخی پلاتفۆرم و خزمەتگوزارییەکان لە کاتی ڕاستەقینەدا.',
    'landing.resourceAbout': 'ئێمە کێین و دابەشکردنی پارێزراوی سۆفتوێر چۆن کاردەکات.',
    'landing.closeTitle': 'ئامادە بۆ هەنگاوی داهاتوو.',
    'landing.closeLede':
      'کەتەلۆگ بگەڕێ، پەیوەندی بە فرۆشتن بکە، یان بچۆ ژوورەوە بۆ پۆرتاڵەکەت — هەموو خزمەتگوزارییەکانت لە هەمان شوێندا بەردەست دەبن.',

    'catalog.eyebrow': 'سۆفتوێر',
    'catalog.title': 'کەتەلۆگی سۆفتوێر',
    'catalog.lede':
      'سۆفتوێری BeCoder ببینە — تایبەتمەندی و وێنە و نرخ پێش دروستکردنی هەژمار.',
    'catalog.viewDetails': 'وردەکاری ببینە',
    'catalog.viewAll': 'هەموو بەرنامەکان ببینە',
    'catalog.emptyTitle': 'کەتەلۆگی سۆفتوێر',
    'catalog.empty': 'سۆفتوێری بڵاوکراوەی BeCoder لێرە دەردەکەوێت کاتێک ئامادە بێت.',
    'catalog.error': 'کەتەلۆگی سۆفتوێر بارنەبوو. پەیوەندییەکەت بپشکنە و دووبارە هەوڵبدە.',
    'catalog.fromPrice': 'لە {price}',
    'catalog.fromMonth': 'لە {price}/مانگ',
    'catalog.fromYear': 'لە {price}/ساڵ',
    'catalog.fromLifetime': 'لە {price}',
    'catalog.lifetimeLabel': 'هەمیشەیی',
    'catalog.trialAvailable': 'تاقیکردنەوە بەردەستە',
    'catalog.statusAvailable': 'ئێستا بەردەستە',
    'guide.categories.title': 'چۆن دەست پێ بکەم؟',
    'guide.categories.body':
      'ئەو بەرنامەیەی گونجاوە بۆ کارەکەت هەڵبژێرە، وێنە و تایبەتمەندییەکان ببینە، تاقی بکەرەوە، ئەگەر بەدڵت بوو بەشداری بکە.',
    'guide.catalog.title': 'چۆن دەست پێ بکەم؟',
    'guide.catalog.body':
      'ئەو بەرنامەیەی گونجاوە بۆ تۆ هەڵبژێرە، وێنە و تایبەتمەندییەکان ببینە، تاقی بکەرەوە، ئەگەر بەدڵت بوو بەشداری بکە.',
    'guide.product.title': 'دڵنیا نیت گونجاوە؟',
    'guide.product.body':
      'سەرەتا وێنە و تایبەتمەندییەکان ببینە. دەتوانیت پێش بەشداریکردن تاقی بکەیتەوە.',
    'guide.trial.title': 'سەرەتا تاقی بکەرەوە',
    'guide.trial.body':
      'ئێستا پێویست ناکات پارە بدەیت. بچۆ ژوورەوە و دەست بە تاقیکردنەوە بکە.',
    'guide.docs.title': 'یارمەتیت پێویستە؟',
    'guide.docs.body':
      'بەرهەمێک هەڵبژێرە. ڕێنماییەکان لێرە دەردەکەون کاتێک بڵاودەکرێنەوە — پەڕەی بەرهەم وێنە و نرخ پیشان دەدات.',
    'docs.productHelp': 'یارمەتی بەرهەم',
    'docs.gettingStarted': 'دەستپێکردن',
    'docs.gettingStartedBody':
      'BeCoder شوێنێکە بۆ دۆزینەوە و خاوەندارێتی سۆفتوێری کارەکەت. ئەم هەنگاوە سادانە بگرە.',
    'docs.helpTopics': 'بابەتەکانی یارمەتی',
    'docs.helpComingSoon': 'وتارەکانی یارمەتی لێرە دەردەکەون کاتێک بۆ هەر بەرهەمێک بڵاودەکرێنەوە.',
    'docs.stepExplore': 'کەتەلۆگی بەرنامەکان بکەرەوە و بەرنامەیەک هەڵبژێرە کە لەگەڵ کارەکەت بگونجێت.',
    'docs.stepReview': 'بزانە چی دەکات، وێنە ڕاستەقینەکان ببینە، و نرخ بزانە.',
    'docs.stepTry': 'کاتێک بەردەست بوو تاقی بکەرەوە، پاشان بەشداری بکە یان بیکڕە ئەگەر گونجاو بوو.',
    'catalog.statusComingSoon': 'بەم زووانە',
    'catalog.noScreenshots': 'وێنەی شاشە لێرە دەردەکەوێت کاتێک وێنەی بەرهەم بڵاودەکرێتەوە.',

    'market.comingTitle': 'کەتەلۆگی سۆفتوێر',
    'market.comingBody':
      'ئەو سۆفتوێریانە بگەڕێ کە BeCoder دروستیان دەکات و پشتگیرییان دەکات. بەرهەم بکەرەوە بۆ تایبەتمەندی و وێنە و نرخ.',
    'market.catalogError': 'کتێبخانەی بەرهەم بارنەبوو. پەیوەندییەکەت بپشکنە و دووبارە هەوڵبدە.',
    'showcase.eyebrow': 'سۆفتوێر',
    'showcase.title': 'سۆفتوێری BeCoder',
    'showcase.lede': 'هەموو بەرهەمە بڵاوکراوەکانی BeCoder لە یەک کەتەلۆگی ڕوون و ئاساندا ببینە.',
    'showcase.carouselLabel': 'پیشاندانی بەرنامەکان',
    'showcase.appsRail': 'بەرنامەیەک هەڵبژێرە',
    'showcase.explore': 'وردەکاری ببینە',
    'showcase.subscribe': 'بەشداری بکە',
    'showcase.subscribeSoon': 'هەڵبژاردەکانی ئابوونە کاتێک بۆ ئەم بەرهەمە ئامادە بکرێن لێرە دەردەکەون',
    'showcase.startTrial': 'تاقیکردنەوەی {days} ڕۆژ دەستپێبکە',
    'showcase.trialSoon': 'تاقیکردنەوە کاتێک بۆ ئەم بەرهەمە چالاک بکرێت لێرە دەردەکەوێت',
    'showcase.launch': 'کردنەوە',
    'showcase.openApp': 'بکەرەوە',
    'showcase.launchPlaceholder': 'کردنەوەی بەرنامە لە پۆرتاڵی بەرهەم بەردەست دەبێت',
    'showcase.comingSoon': 'بەم زووانە',
    'showcase.prev': 'بەرنامەی پێشوو',
    'showcase.next': 'بەرنامەی داهاتوو',
    'showcase.navHint': 'تیرەکان، سوایپ، یان کۆنترۆڵەر بەکاربهێنە.',
    'showcase.highlights': 'تایبەتمەندییە سەرەکییەکان',
    'showcase.plans': 'پلانەکان',
    'showcase.plansPlaceholder': 'نرخی گشتی دەردەکەوێت کاتێک پلانەکانی ئەم بەرهەمە بڵاودەکرێنەوە.',
    'showcase.screenshots': 'وێنەی شاشە',
    'showcase.openScreenshot': 'وێنە بکەرەوە',
    'lightbox.title': 'پێشبینینی وێنە',
    'lightbox.prev': 'وێنەی پێشوو',
    'lightbox.next': 'وێنەی داهاتوو',
    'lightbox.position': '{current} / {total}',
    'showcase.systemRequirements': 'پێداویستییەکانی سیستەم',
    'showcase.systemRequirementsSoon': 'پێداویستی ورد لەگەڵ هەر وەشانێک بڵاودەکرێتەوە.',
    'showcase.releaseNotes': 'تێبینییەکانی وەشان',
    'showcase.releaseNotesSoon': 'تێبینییەکانی وەشان لێرە دەردەکەون کاتێک بڵاودەکرێنەوە.',
    'showcase.backToShowcase': 'گەڕانەوە بۆ سۆفتوێر',
    'showcase.catalogError': 'بەرنامەکان بارنەبوون. پەیوەندییەکەت بپشکنە و دووبارە هەوڵبدە.',
    'showcase.empty': 'هیچ بەرنامەیەک لە کەتەلۆگ بڵاونەکراوەتەوە.',
    'showcase.notFound': 'ئەم بەرنامەیە بەردەست نییە.',
    'showcase.about': 'دەربارە',
    'hub.startTrial': 'تاقیکردنەوەی خۆڕایی دەستپێبکە',
    'hub.startTrialLede':
      'تاقیکردنەوەی خۆڕایی ٣ ڕۆژە بۆ {slug} دەستپێبکە. پێویستت بە شێوازی پارەدان نییە و ماوەکە تەنها لە یەکەم چالاککردندا دەستپێدەکات.',
    'hub.confirmTrial': 'تاقیکردنەوەی خۆڕایی دەستپێبکە',
    'hub.noPaymentRequired': 'پێویستت بە هیچ شێوازێکی پارەدان نییە',
    'hub.trialDaysBadge': 'تاقیکردنەوەی خۆڕایی ٣ ڕۆژە',
    'hub.trialReady': 'تاقیکردنەوەی {name} ئامادەیە',
    'hub.daysRemaining': '{count} ڕۆژ ماوە',
    'hub.dayRemaining': '١ ڕۆژ ماوە',
    'hub.trialEndsToday': 'تاقیکردنەوە ئەمڕۆ کۆتایی دێت',
    'hub.trialExpiredTitle': 'تاقیکردنەوەی خۆڕایی کۆتایی هات',
    'hub.trialExpiredBody':
      'تاقیکردنەوەی خۆڕایی کۆتایی هات. داتاکانت پارێزراون. بەشداری بکە بۆ بەردەوامبوون لەو شوێنەی وەستابوویت.',
    'hub.dataChoiceTitle': 'ئابوونەکەت چالاکە. چۆن دەتەوێت بەردەوام بیت؟',
    'hub.dataChoiceKeep': 'داتای تاقیکردنەوە بمێنێتەوە',
    'hub.dataChoiceFresh': 'لە سەرەتاوە دەستپێبکە',
    'hub.dataChoiceFreshConfirmTitle': 'لە سەرەتاوە دەستپێبکەیت؟',
    'hub.dataChoiceFreshConfirmBody':
      'ئەمە داتای کارکردنی GlassPOS کە لە ماوەی تاقیکردنەوە دروستکراوە بۆ هەمیشە دەسڕێتەوە. هەژماری BeCoder و ئابوونە و مۆڵەت چالاک دەمێننەوە. ئەم کردارە ناگەڕێتەوە.',
    'hub.dataChoiceKeepDone': 'داتای تاقیکردنەوە دەمێنێتەوە. لەو شوێنەی وەستابوویت بەردەوامبە.',
    'hub.dataChoiceFreshDone': 'شوێنی کاری نوێت ئامادەیە.',
    'hub.dataChoiceDeferred':
      'هێشتا بە شێوەیەکی پارێزراو ناتوانرێت لە سەرەتاوە دەستپێبکرێت بۆ ئەم بەرهەمە. داتاکانت نەسڕدرانەوە.',
    'hub.dataChoiceFreshUnavailable':
      'لە سەرەتاوە دەستپێکردن پێشکەش دەکرێت کاتێک ئەم بەرهەمە ڕێستێکی کارکردنی پارێزراو پشتگیری دەکات. تا ئەو کاتە داتای تاقیکردنەوە بمێنێتەوە.',
    'hub.dataChoicePending': 'چۆن دەتەوێت بە داتای تاقیکردنەوە بەردەوام بیت هەڵبژێرە',
    'hub.trialBadge': 'تاقیکردنەوە',
    'hub.trialExpiredBadge': 'تاقیکردنەوە کۆتایی هات',
    'hub.paidBadge': 'پارەدراو',
    'hub.signInRequired': 'بچۆ ژوورەوە بۆ دەستپێکردنی تاقیکردنەوە.',
    'hub.orgName': 'ناوی ڕێکخراو',
    'hub.trialEnds': 'تاقیکردنەوە لە {date} کۆتایی دێت.',
    'hub.trialFailed': 'تاقیکردنەوە دەستی پێ نەکرا.',
    'hub.launchFailed': 'بەرنامە نەکرایەوە.',
    'hub.upgrade': 'پلانی نوێکردنەوە',
    'hub.upgradeLede': 'پلانێک بۆ ئەم بەرنامەیە هەڵبژێرە. پارەدانی ئۆنلاین لێرە دەردەکەوێت کاتێک چالاک بکرێت.',
    'hub.upgradeNeedProduct': 'سەرەتا بەرهەمێک لە بەرنامەکان یان بەرهەمەکانم هەڵبژێرە.',
    'hub.plansLoadFailed': 'پلانەکان بارنەبوون.',
    'hub.checkoutFailed': 'پارەدان دەستی پێ نەکرا.',
    'pay.title': 'پارەدان',
    'pay.redirecting': 'بە شێوەیەکی پارێزراو دەگوازرێیتەوە بۆ FastPay بۆ تەواوکردنی پارەدان.',
    'pay.checking': 'پارەدان پشتڕاست دەکرێتەوە…',
    'pay.successTitle': 'پارەدان سەرکەوتوو بوو',
    'pay.successBody': 'پارەدان بە سەرکەوتوویی تەواو بوو. سۆفتوێرکەت ئێستا لە هەژمارەکەتدا بەردەستە.',
    'pay.pendingTitle': 'پارەدان چاوەڕوانە',
    'pay.pendingBody':
      'هێشتا پارەدان پشتڕاست دەکەینەوە. سۆفتوێرکەت دوای پشتڕاستکردنەوە دەردەکەوێت — پێویست ناکات دووبارە پارە بدەیت.',
    'pay.failedTitle': 'پارەدان سەرکەوتوو نەبوو',
    'pay.failedBody': 'پارەدان تەواو نەبوو. دەتوانیت دووبارە هەوڵ بدەیت یان پەیوەندی بە فرۆشتن بکەیت.',
    'pay.cancelledTitle': 'پارەدان هەڵوەشێنرایەوە',
    'pay.cancelledBody': 'پارەدان هەڵوەشێنرایەوە و هیچ پارەیەک وەرنەگیرا. کاتێک ئامادە بوویت دووبارە هەوڵ بدە.',
    'pay.openSoftware': 'سۆفتوێرەکانم بکەرەوە',
    'pay.goWorkspace': 'بڕۆ بۆ شوێنی کارکردنی کڕیار',
    'pay.refresh': 'دووبارە بپشکنە',
    'pay.tryAgain': 'دووبارە هەوڵبدە',
    'pay.missingPayment': 'سەرچاوەی پارەدان نەدۆزرایەوە.',
    'pay.confirmFailed': 'دۆخی پارەدان پشتڕاست نەکرایەوە.',
    'admin.reconcile': 'هاوتاکردن',
    'admin.paymentsIntro': 'پشکنینی پارەدانەکانی FastPay و هاوتاکردنی چالاککردن کاتێک پچڕا.',
    'hub.billingOff': 'پارەدانی ئۆنلاین هێشتا چالاک نییە. پەیوەندی بە فرۆشتن بکە، یان تاقیکردنەوە دەستپێبکە کاتێک بەردەست بێت.',
    'hub.subscriptionsIntro': 'تاقیکردنەوە و بەشداری بۆ ڕێکخراوە چالاکەکەت.',
    'hub.subscriptionsLoadFailed': 'بەشدارییەکان بارنەبوون.',
    'hub.trials': 'تاقیکردنەوەکان',
    'hub.plans': 'بەشدارییەکان',
    'hub.noTrials': 'هیچ تاقیکردنەوەیەک نییە.',
    'hub.noSubscriptions': 'هیچ بەشدارییەک نییە.',
    'hub.invoicesIntro': 'پسوڵە و پارەدان بۆ ڕێکخراوەکەت.',
    'hub.invoicesLoadFailed': 'پسوڵەکان بارنەبوون.',
    'admin.trialsIntro': 'تاقیکردنەوەی بەرهەم بۆ ڕێکخراوەکان.',
    'admin.subscriptionsIntro': 'بەشدارییەکانی ڕێکخراو. دەستی چالاک بکە کاتێک پارەدان ناکارایە.',
    'admin.activateManual': 'چالاککردنی دەستی',
    'admin.activateSubscriptionConfirm': 'ئەم بەشدارییە بۆ ٣٠ ڕۆژ بەبێ پارەدان چالاک بکەیت؟',
    'admin.confirmSuspendCustomer.title': 'کڕیار ڕابگریت؟',
    'admin.confirmSuspendCustomer.message':
      '{name} ({email}) ڕابگریت؟ تا چالاککردنەوە دەستڕاگەیشتنیان نامێنێت.',
    'admin.confirmActivateCustomer.title': 'کڕیار چالاک بکەیت؟',
    'admin.confirmActivateCustomer.message': '{name} ({email}) چالاک بکەیت و دەستڕاگەیشتن بگەڕێنیتەوە؟',
    'admin.confirmSuspendLicense.title': 'مۆڵەت ڕابگریت؟',
    'admin.confirmSuspendLicense.message':
      'مۆڵەتی {name} ({email}) ڕابگریت؟ ئامێرە چالاکەکان پشتڕاستکردنەوە دەوەستن.',
    'admin.confirmRevokeLicense.title': 'مۆڵەت هەڵبوەشێنیتەوە؟',
    'admin.confirmRevokeLicense.message':
      'مۆڵەتی {name} ({email}) بۆ هەمیشە هەڵبوەشێنیتەوە؟ ناتوانرێت پاشگەز ببیتەوە.',
    'admin.confirmActivateLicense.title': 'مۆڵەت چالاک بکەیت؟',
    'admin.confirmActivateLicense.message': 'مۆڵەتی {name} ({email}) چالاک بکەیت؟',
    'admin.confirmDeactivateDevice.title': 'ئامێر بەزۆر ناکارا بکەیت؟',
    'admin.confirmDeactivateDevice.message':
      '{name} بۆ {email} بەزۆر ناکارا بکەیت؟ کورسییەکە دەستبەجێ ئازاد دەبێت.',
    'admin.confirmPublishRelease.title': 'وەشان بڵاوبکەیتەوە؟',
    'admin.confirmPublishRelease.message':
      'وەشانی {name} بڵاوبکەیتەوە؟ کڕیاران دەتوانن دایبگرن.',
    'admin.confirmDeleteMedia.title': 'میدیا بسڕیتەوە؟',
    'admin.confirmDeleteMedia.message': 'ئەم میدیایە لاببەیت؟ ناتوانرێت پاشگەز ببیتەوە.',
    'admin.confirmActivateSubscription.title': 'بەشداری چالاک بکەیت؟',
    'admin.confirmActivateSubscription.message':
      'ئەم بەشدارییە بۆ ٣٠ ڕۆژ بەبێ پارەدان چالاک بکەیت؟',
    'admin.customersIntro': 'کڕیاران بگەڕێ و وردەکاری بکەرەوە بۆ کارگێڕی.',
    'admin.searchCustomers': 'ناو، ئیمەیڵ، یان ناسنامەی بەکارهێنەر بگەڕێ',
    'admin.filterAnyStatus': 'هەر دۆخێک',
    'admin.filterActive': 'چالاک',
    'admin.filterSuspended': 'ڕاگیراو',
    'admin.filterAnyProvider': 'هەر دابینکەرێک',
    'admin.open360': 'وردەکاری',
    'admin.customer360': 'وردەکاری کڕیار',
    'admin.customerDetailsSub': 'وردەکاری و بەڕێوەبردنی کڕیار',
    'desk.metric.activeSubscriptions': 'بەشداری چالاک',
    'cc.access.showSecondary': 'پێوانەی زیاتر',
    'cc.access.hideSecondary': 'کەمتر',
    'cc.settings.tab.identity': 'ناسنامە',
    'cc.settings.tab.contact': 'پەیوەندی',
    'cc.settings.tab.about': 'دەربارە',
    'cc.settings.tab.social': 'کۆمەڵایەتی',
    'cc.settings.tab.footer': 'پێپەڕە',
    'cc.settings.tab.advanced': 'پێشکەوتوو',
    'admin.results': 'ئەنجام',
    'admin.noCustomers': 'هیچ کڕیارێک لەگەڵ ئەم فلتەرانە ناگونجێت.',
    'admin.backToCustomers': 'گەڕانەوە بۆ کڕیاران',
    'admin.customerNotFound': 'کڕیار نەدۆزرایەوە.',
    'admin.provider': 'دابینکەر',
    'admin.status': 'دۆخ',
    'admin.verified': 'پشتڕاستکراو',
    'admin.lastSignIn': 'دوایین چوونەژوورەوە',
    'admin.created': 'دروستکراو',
    'admin.role': 'ڕۆڵ',
    'admin.orgMemberships': 'ئەندامێتی ڕێکخراو',
    'admin.tabOverview': 'گشتی',
    'admin.tabNotes': 'تێبینییەکان',
    'admin.tabActivity': 'چالاکی',
    'admin.emptyProducts': 'هیچ بەرهەمێکی پەیوەندیدار نییە.',
    'admin.emptyLicenses': 'هیچ مۆڵەتێک نییە.',
    'admin.emptyTrials': 'هیچ تاقیکردنەوەیەک نییە.',
    'admin.emptySubscriptions': 'هیچ بەشدارییەک نییە.',
    'admin.emptyInvoices': 'هیچ پسوڵەیەک نییە.',
    'admin.emptyPayments': 'هیچ پارەدانێک نییە.',
    'admin.emptyDevices': 'هێشتا ئامێری چالاک نییە.',
    'admin.emptyDownloads': 'هێشتا چالاکی داگرتن نییە.',
    'admin.emptyNotes': 'هێشتا تێبینیی ناوخۆیی نییە.',
    'admin.emptyActivity': 'هێشتا چالاکی پەیوەندیدار نییە.',
    'admin.licenseType': 'جۆر',
    'admin.starts': 'دەستپێک',
    'admin.ends': 'کۆتایی',
    'admin.openLicenses': 'مۆڵەتەکان',
    'admin.organization': 'ڕێکخراو',
    'admin.plan': 'پلان',
    'admin.periodStart': 'دەستپێکی ماوە',
    'admin.periodEnd': 'کۆتایی ماوە',
    'admin.amount': 'بڕ',
    'admin.payments': 'پارەدانەکان',
    'admin.version': 'وەشان',
    'admin.platform': 'پلاتفۆرم',
    'admin.downloadedAt': 'کاتی داگرتن',
    'admin.deviceName': 'ئامێر',
    'admin.activated': 'چالاککراو',
    'admin.lastSeen': 'دوایین بینین',
    'admin.action': 'کردار',
    'admin.resource': 'سەرچاوە',
    'admin.actor': 'ئەکتەر',
    'admin.notePlaceholder': 'تێبینیی ناوخۆیی (بۆ کڕیار دیار نییە)',
    'admin.addNote': 'تێبینی زیاد بکە',
    'admin.noteSaved': 'تێبینی پاشەکەوت کرا',
    'admin.settings.nav': 'ناسنامەی پلاتفۆرم',
    'admin.settings.title': 'ناسنامە و پەیوەندی پلاتفۆرم',
    'admin.settings.intro':
      'پەڕەکانی دەربارە و پەیوەندی گشتی ئەم بەهاکانە دەخوێننەوە. خانە هەڵبژێردراوەکان بەتاڵ بهێڵەرەوە بۆ شاردنەوەیان.',
    'admin.settings.saved': 'ڕێکخستنەکانی پلاتفۆرم پاشەکەوت کران',
    'admin.settings.platformName': 'ناوی پیشاندانی پلاتفۆرم',
    'admin.settings.contactPerson': 'کەسی سەرەکی پەیوەندی',
    'admin.settings.mobilePhone': 'مۆبایل / تەلەفۆن',
    'admin.settings.whatsappNumber': 'ژمارەی واتسئاپ',
    'admin.settings.supportEmail': 'ئیمەیڵی پشتگیری گشتی',
    'admin.settings.salesEmail': 'ئیمەیڵی فرۆشتن',
    'admin.settings.address': 'دەقی ناونیشان / کۆمپانیا',
    'admin.settings.hours': 'دەقی کاتژمێرەکانی کار',
    'admin.settings.aboutShortEn': 'دەربارەمان کورت (EN)',
    'admin.settings.aboutShortAr': 'دەربارەمان کورت (AR)',
    'admin.settings.aboutShortCkb': 'دەربارەمان کورت (CKB)',
    'admin.settings.aboutFullEn': 'دەربارەمان تەواو (EN)',
    'admin.settings.aboutFullAr': 'دەربارەمان تەواو (AR)',
    'admin.settings.aboutFullCkb': 'دەربارەمان تەواو (CKB)',
    'admin.settings.facebook': 'بەستەری فەیسبووک',
    'admin.settings.instagram': 'بەستەری ئینستاگرام',
    'admin.settings.linkedin': 'بەستەری لینکدئین',
    'admin.settings.youtube': 'بەستەری یوتیوب',
    'admin.settings.footerContact': 'دەقی پەیوەندی پێپەڕە',
    'admin.settings.footerCopyright': 'دەقی مافی لەبەرگرتنەوە / کۆمپانیا',
    'contact.title': 'پەیوەندی',
    'contact.lede': 'پەیوەندی بە BeCoder بکە بۆ پرسیاری بەرهەم و فرۆشتن.',
    'contact.person': 'کەسی پەیوەندی',
    'contact.phone': 'تەلەفۆن',
    'contact.hours': 'کاتژمێرەکانی کار',
    'contact.address': 'ناونیشان',
    'contact.call': 'پەیوەندی',
    'contact.whatsapp': 'واتسئاپ',
    'contact.email': 'ئیمەیڵ',
    'contact.contactSales': 'پەیوەندی بۆ فرۆشتن',
    'contact.openPage': 'پەڕەی تەواوی پەیوەندی',
    'contact.social': 'تۆڕە کۆمەڵایەتییەکان',
    'about.defaultBody':
      'BeCoder پلاتفۆرمێکی سۆفتوێریە کە ئەپڵیکەیشنی ئامادەی کاروبار دابین دەکات و سۆفتوێری تایبەت بۆ کۆمپانیا و کارەکانی فرەلقە دروست دەکات — سەرنج لەسەر ئامێری کرداریی کاروبار.',
    'about.pillarApps': 'ئەپڵیکەیشنی ئامادە',
    'about.pillarAppsDesc': 'بەرهەمی کەتەلۆگ بۆ ڕێڕەوی راستەقینەی کاروبار.',
    'about.pillarCustom': 'سۆفتوێری تایبەت',
    'about.pillarCustomDesc': 'سیستەمی مەبەستدار کاتێک بەرهەمی کەتەلۆگ بەس نییە.',
    'about.pillarPractical': 'سەرنجی کرداری',
    'about.pillarPracticalDesc': 'سۆفتوێر کە خزمەت بە کارگێڕی دەکات — نە نمایشی بازاڕگەری.',
    'common.search': 'گەڕان',
    'common.prev': 'پێشوو',
    'common.next': 'دواتر',
    'common.yes': 'بەڵێ',
    'common.no': 'نەخێر',
    'common.actions': 'کردارەکان',
    'desk.licensesTitle': 'مۆڵەتەکان',
    'desk.licensesIntro': 'مۆڵەتی کڕیاران بگەڕێ، فلتەر بکە و بەڕێوەببە.',
    'desk.trialsTitle': 'تاقیکردنەوەکان',
    'desk.subscriptionsTitle': 'بەشدارییەکان',
    'desk.devicesTitle': 'ئامێرەکان',
    'desk.devicesIntro': 'بەستەری ئامێری چالاک و ناکارا.',
    'desk.searchLicenses': 'کڕیار، ئیمەیڵ، ناسنامەی مۆڵەت، بەرهەم بگەڕێ',
    'desk.searchTrials': 'کڕیار، ئیمەیڵ، بەرهەم، تاقیکردنەوە یان ڕێکخراو بگەڕێ',
    'desk.searchSubscriptions': 'کڕیار، ئیمەیڵ، بەرهەم، بەشداری بگەڕێ',
    'desk.searchDevices': 'کڕیار، ئیمەیڵ، ئامێر، بەرهەم، مۆڵەت بگەڕێ',
    'desk.filterAnyProduct': 'هەر بەرهەمێک',
    'desk.filterAnyType': 'هەر جۆرێک',
    'desk.filterAnyValidity': 'هەر دۆخێکی بەسەرداچوون',
    'desk.filterAnyPeriod': 'هەر ماوەیەک',
    'desk.type.trial': 'تاقیکردنەوە',
    'desk.type.monthly': 'مانگانە',
    'desk.type.yearly': 'ساڵانە',
    'desk.type.lifetime': 'هەمیشەیی',
    'desk.status.expired': 'بەسەرچوو',
    'desk.status.revoked': 'هەڵوەشاوە',
    'desk.status.canceled': 'هەڵوەشێنراو',
    'desk.status.converted': 'گۆڕدراو',
    'desk.status.deactivated': 'ناکارا',
    'desk.validity.active': 'ئێستا دروستە',
    'desk.validity.expired': 'دوای کۆتایی بەروار',
    'desk.licenseId': 'مۆڵەت',
    'desk.issuedByAdmin': 'لەلایەن بەڕێوەبەرەوە',
    'desk.permanent': 'هەمیشەیی',
    'desk.lifetimeNoEnd': 'مۆڵەتی هەمیشەیی کۆتایی نییە',
    'desk.endsOptional': 'بەرواری کۆتایی ئارەزوومەندانە',
    'desk.lifetimePolicy':
      'هەمیشەیی = یەکجار پارە بدە / هەمیشە بەکاریبهێنە. پشتگیری Premium (٣ مانگ) جیاوازە و وەک کۆتایی مۆڵەت بەکارناهێنرێت.',
    'desk.openDetail': 'وردەکاری',
    'desk.openInDesk': 'لە دێسک بکەرەوە',
    'desk.backLicenses': 'گەڕانەوە بۆ مۆڵەتەکان',
    'desk.licenseDetail': 'وردەکاری مۆڵەت',
    'desk.licenseNotFound': 'مۆڵەت نەدۆزرایەوە.',
    'desk.history': 'مێژوو',
    'desk.newEndDate': 'بەرواری کۆتایی نوێ',
    'desk.currentEnd': 'کۆتایی ئێستا',
    'desk.saveExpiration': 'کۆتایی نوێ بکە',
    'desk.expirationSaved': 'کۆتایی نوێکرایەوە',
    'desk.confirmExpiration.title': 'کۆتایی مۆڵەت نوێ بکەیتەوە؟',
    'desk.confirmExpiration.message':
      'بەرواری کۆتایی {product} ({email}) لە {old} بۆ {next} بگۆڕیت؟',
    'desk.confirmActivateLicense.message':
      'مۆڵەتی {id} بۆ {name} ({email}) لەسەر {product} چالاک بکەیت؟',
    'desk.confirmSuspendLicense.message':
      'مۆڵەتی {id} بۆ {name} ({email}) لەسەر {product} ڕابگریت؟ ئامێرەکان دەوەستن.',
    'desk.confirmRevokeLicense.message':
      'مۆڵەتی {id} بۆ {name} ({email}) لەسەر {product} هەڵبوەشێنیتەوە؟ تۆمار دەمێنێت.',
    'desk.confirmExtendTrial.title': 'تاقیکردنەوە درێژ بکەیتەوە؟',
    'desk.confirmExtendTrial.message':
      '{product} بۆ {email} بە {days} ڕۆژ درێژ بکەیتەوە؟ ئێستا {old} → نوێ {next}.',
    'desk.confirmForceGrantTrial.title': 'بەزۆر کاتی تاقیکردنەوە ببەخشیت؟',
    'desk.confirmForceGrantTrial.message':
      'تاقیکردنەوەی {product} بۆ {email} پێشتر {status}ە. پشتڕاستکردنەوەی بەخشینی {days} ڕۆژ ({old} → {next}). ئەمە تێپەڕاندنی بەڕێوەبەرایەتییە نەک درێژکردنەوەی ئاسایی.',
    'desk.confirmCancelTrial.title': 'تاقیکردنەوە هەڵبوەشێنیتەوە؟',
    'desk.confirmCancelTrial.message':
      'تاقیکردنەوەی {product} ({email}) زوو کۆتایی پێبهێنیت؟ تۆماری مێژوویی دەمێنێت.',
    'desk.confirmActivateSubscription.message':
      'بەشداری {product} ({email}) بە دەستی بۆ ٣٠ ڕۆژ بەبێ پارەدان چالاک بکەیت؟',
    'desk.confirmDeactivateDevice.message':
      '{name} بۆ {email} لەسەر {product} بەزۆر ناکارا بکەیت؟ تۆماری ئامێر دەمێنێت؛ مۆڵەت ناسڕدرێتەوە.',
    'desk.extend': 'درێژکردنەوە',
    'desk.forceGrant': 'بەخشینی بەزۆر',
    'desk.extendDays': 'ڕۆژەکانی درێژکردنەوە',
    'desk.cancelTrial': 'هەڵوەشاندنەوەی تاقیکردنەوە',
    'desk.daysLeft': 'ڕۆژی ماوە',
    'desk.noFilterResults': 'هیچ تۆمارێک لەگەڵ ئەم فلتەرانە ناگونجێت.',
    'desk.loadError': 'نەتوانرا داتا باربکرێت',
    'desk.metric.activeLicenses': 'مۆڵەتی چالاک',
    'desk.metric.suspendedLicenses': 'ڕاگیراو',
    'desk.metric.revokedLicenses': 'هەڵوەشاوە',
    'desk.metric.lifetime': 'هەمیشەیی',
    'desk.metric.activeTrials': 'تاقیکردنەوەی چالاک',
    'desk.metric.expiringTrials': 'تاقیکردنەوەی نزیک (٧ ڕۆژ)',
    'desk.metric.activeMonthly': 'مانگانەی چالاک',
    'desk.metric.activeAnnual': 'ساڵانەی چالاک',
    'desk.metric.activeDevices': 'ئامێری چالاک',
    'dist.releasesTitle': 'دابەشکردن — وەشانەکان',
    'dist.releasesIntro': 'وەشانی بەرهەم بگەڕێ، ڕەشنووس دروست بکە، بار بکە، بڵاوبکەرەوە و چاودێری بکە.',
    'dist.downloadAuditNav': 'چاودێری داگرتن',
    'dist.downloadAuditTitle': 'چاودێری داگرتن',
    'dist.downloadAuditIntro': 'ڕووداوی داگرتنی کڕیار لەسەر بەرهەم و وەشانەکان.',
    'dist.searchReleases': 'وەشان، فایل، checksum بگەڕێ',
    'dist.searchDownloads': 'کڕیار، ئیمەیڵ، وەشان بگەڕێ',
    'dist.filterAnyPlatform': 'هەر پلاتفۆرمێک',
    'dist.createDraft': 'ڕەشنووس دروست بکە',
    'dist.draftCreated': 'ڕەشنووس دروستکرا',
    'dist.uploadFile': 'فایل بار بکە',
    'dist.replaceFile': 'فایل بگۆڕە',
    'dist.uploadDone': 'بارکردن تەواو بوو',
    'dist.unpublish': 'بڵاوکردنەوە بکەرەوە',
    'dist.unpublished': 'بڵاوکردنەوە هەڵوەشێنرایەوە',
    'dist.deleted': 'ڕەشنووس سڕایەوە',
    'dist.verifyIntegrity': 'پشتڕاستکردنەوەی یەکپارچەیی',
    'dist.integrityOk': 'یەکپارچەیی پشتڕاستکرایەوە',
    'dist.integrityFail': 'پشتڕاستکردنەوەی یەکپارچەیی سەرکەوتوو نەبوو',
    'dist.viewDownloads': 'داگرتنەکان ببینە',
    'dist.openDownloadAudit': 'چاودێری داگرتن بکەرەوە',
    'dist.downloadAuditHint': '{count} ڕووداوی داگرتن — بۆ گەڕان و فلتەر چاودێری داگرتن بەکاربهێنە.',
    'dist.file': 'فایل',
    'dist.size': 'قەبارە',
    'dist.checksum': 'پشکنینی فایل',
    'dist.flags': 'نیشانەکان',
    'dist.current': 'ئێستا',
    'dist.superseded': 'جێگۆڕکراو',
    'dist.notesYes': 'تێبینی ✓',
    'dist.notesNo': 'بێ تێبینی',
    'dist.minSupported': 'کەمترین وەشانی پشتگیریکراو',
    'dist.notes.en': 'تێبینی وەشان (ئینگلیزی)',
    'dist.notes.ar': 'تێبینی وەشان (عەرەبی)',
    'dist.notes.ckb': 'تێبینی وەشان (کوردی)',
    'dist.confirmPublishDetail':
      'بڵاوکردنەوەی {product} {version} ({platform})؟ فایل: {file}. کڕیاران دەتوانن دایبگرن.',
    'dist.confirmUnpublish.title': 'بڵاوکردنەوە هەڵبوەشێنیتەوە؟',
    'dist.confirmUnpublish.message': 'بڵاوکردنەوەی {product} {version} هەڵبوەشێنیتەوە؟ کڕیاران دەستگەیشتنیان لە دەست دەدەن.',
    'dist.confirmDelete.title': 'ڕەشنووس بسڕیتەوە؟',
    'dist.confirmDelete.message': 'ڕەشنووسی {version} بسڕیتەوە؟ ناگەڕێتەوە.',
    'dist.distributionStatus': 'دابەشکردن',
    'dist.manageReleases': 'وەشانەکان بەڕێوەببە',
    'dist.publishedAvailable': 'وەشانی بڵاوکراوە بەردەستە',
    'dist.latestVersion': 'دوایین وەشان',
    'dist.platforms': 'پلاتفۆرمەکان',
    'dist.packageReady': 'پاکێج ئامادەیە',
    'dist.fromDate': 'لە بەروار',
    'dist.toDate': 'بۆ بەروار',
    'dist.empty.no_entitlement': 'هێشتا مۆڵەتی داگرتنت نییە بۆ ئەم بەرهەمە.',
    'dist.empty.coming_soon': 'داگرتن هێشتا بەردەست نییە — بەرهەمەکە بەزوویی دێت.',
    'dist.empty.release_unpublished': 'هێشتا وەشانی بڵاوکراوە بۆ داگرتن نییە.',
    'dist.empty.package_missing': 'وەشان هەیە بەڵام پاکێجی دامەزراندن هێشتا ئامادە نییە.',
    'dist.empty.product_not_distributable': 'ئەم بەرهەمە لەڕێگەی داگرتنی BeCoder دابەش ناکرێت.',
    'dist.empty.expired': 'دەستگەیشتنت بۆ داگرتنی ئەم بەرهەمە بەسەرچووە.',
    'dist.empty.suspended_or_revoked': 'داگرتن بەردەست نییە چونکە دەستگەیشتن ڕاگیراوە یان هەڵوەشێنراوەتەوە.',
    'dist.empty.unknown': 'داگرتن ئێستا بەردەست نییە.',
    'admin.launchBaseUrl': 'بەستەری سەرەکی کردنەوە',
    'admin.saveLaunchUrl': 'بەستەری کردنەوە پاشەکەوت بکە',
    'admin.launchUrlSaved': 'بەستەری کردنەوە پاشەکەوت کرا',
    'admin.mediaTitle': 'بەڕێوەبەری میدیا',
    'admin.mediaLede': 'میدیای پیشاندانی بەرهەمەکانی کەتەلۆگ باربکە و بەڕێوەببە.',
    'admin.mediaIcon': 'ئایکۆنی ئەپ',
    'admin.mediaHero': 'وێنەی سەرەکی',
    'admin.mediaPoster': 'پۆستەری ڤیدیۆ',
    'admin.mediaVideo': 'ڤیدیۆی بانگەشە',
    'admin.mediaScreenshots': 'وێنەی شاشە',
    'admin.mediaUploadReplace': 'بارکردن / گۆڕین',
    'admin.mediaAddScreenshot': 'زیادکردنی وێنەی شاشە',
    'admin.mediaAddScreenshots': 'زیادکردنی وێنەکان',
    'admin.mediaSelectScreenshots': 'هەڵبژاردنی وێنەی شاشە',
    'admin.mediaScreenshotsHint':
      'چەند وێنەیەک هەڵبژێرە، پێشبینین بکە، پاشان هەموویان باربکە. وێنەکانی ئێستا دەمێننەوە. بۆ ڕیزکردنەوە ڕاکێشە.',
    'admin.mediaDragReorder': 'بۆ ڕیزکردنەوە ڕاکێشە',
    'admin.mediaPreview': 'پێشبینینی وێنە',
    'admin.mediaUploadDone': 'تەواو',
    'admin.mediaQueued': 'لە چاوەڕوانیدایە',
    'admin.mediaSkipped': 'پەڕێندرا',
    'admin.mediaPartialUpload': 'هەندێک بارکردن سەرکەوتوو نەبوو',
    'admin.mediaUploadFailed': 'بارکردنی وێنەی شاشە سەرکەوتوو نەبوو',
    'admin.mediaPendingTitle': '{count} هەڵبژێردرا ({valid} ئامادەیە بۆ بارکردن)',
    'admin.mediaPendingInvalid': '{count} فایل بەهۆی هەڵەی پشتڕاستکردنەوە پەڕێندرێت.',
    'admin.mediaClearSelection': 'پاککردنەوەی هەڵبژاردن',
    'admin.mediaUploadAll': 'هەمووی باربکە ({count})',
    'admin.mediaRemoveFromSelection': 'لە هەڵبژاردن لاببە',
    'admin.mediaBatchComplete': 'بارکردنی کۆمەڵە تەواو بوو',
    'admin.mediaBatchResult': 'بارکرا {uploaded} · سەرکەوتوو نەبوو {failed} · پەڕێندرا {skipped} · کۆی گشتی {total}',
    'admin.mediaReplaceScreenshot': 'گۆڕینی وێنەی شاشە',
    'admin.mediaReplaced': 'وێنەی شاشە گۆڕدرا',
    'admin.mediaInvalidType': '{name}: جۆری پشتگیرینەکراو ({type}). PNG، JPG، WEBP یان GIF بەکاربهێنە.',
    'admin.mediaInvalidSize': '{name}: لە سنووری قەبارەی {max} گەورەترە.',
    'admin.mediaInvalidEmpty': '{name}: فایل بەتاڵە.',
    'admin.mediaEmpty': 'هێشتا میدیا نییە.',
    'admin.mediaNoProducts': 'سەرەتا بەرهەمێک دروست بکە.',
    'admin.mediaUploaded': 'میدیا بارکرا',
    'admin.mediaDeleted': 'میدیا لابرا',

    'cms.saveDraft': 'پاشەکەوتکردنی ڕەشنووس',
    'cms.publish': 'بڵاوکردنەوە',
    'cms.preview': 'پێشبینینی لاپەڕەی بەرهەم',
    'cms.saved': 'پاشەکەوت کرا',
    'cms.published': 'بڵاوکرایەوە',
    'cms.notFound': 'بەرهەم نەدۆزرایەوە',
    'cms.editCommercial': 'دەستکاری بازرگانی',
    'cms.searchProducts': 'گەڕان بە ناو یان ناسنامە',
    'cms.filterAnyStatus': 'هەر دۆخێک',
    'cms.filterAnyPurchasable': 'هەر کڕینێک',
    'cms.filterAnyTrial': 'هەر تاقیکردنەوە',
    'cms.purchasable': 'بۆ کڕین',
    'cms.notPurchasable': 'نەکڕدراو',
    'cms.trialEnabled': 'تاقیکردنەوە چالاکە',
    'cms.trialDisabled': 'تاقیکردنەوە ناچالاکە',
    'cms.operationalTrial': 'تاقیکردنەوەی کارپێکردن',
    'cms.trialDays': 'ڕۆژانی تاقیکردنەوە',
    'cms.trialSaved': 'ڕێکخستنی تاقیکردنەوە پاشەکەوت کرا',
    'cms.statusPublished': 'بڵاوکراوە',
    'cms.statusDraft': 'ڕەشنووس',
    'cms.pricingOk': 'نرخدان باشە',
    'cms.pricingMissing': 'نرخدان ناتەواوە',
    'cms.plansPricing': 'پلان و نرخ',
    'cms.lifetimeLicense': 'مۆڵەتی هەمیشەیی',
    'cms.priceUsd': 'نرخ (USD)',
    'cms.priceMajor': 'بڕ',
    'cms.priceHintIqd': 'IQD وەک بڕی تەواو بنووسە (نموونە 600000 واتە 600,000 دینار).',
    'cms.priceHintUsd': 'USD وەک بڕی تەواو بنووسە (نموونە 550 واتە 550$).',
    'cms.pricePreview': 'وەک {minor} amountMinor پاشەکەوت دەکرێت',
    'cms.marketIraq': 'عێراق (IQD)',
    'cms.marketInternational': 'نێودەوڵەتی (USD)',
    'cms.planType': 'جۆری پلان',
    'cms.planMarket': 'بازار',
    'cms.planPeriod': 'ماوەی پسوڵە',
    'cms.planActive': 'چالاک',
    'cms.planInactive': 'ناچالاک',
    'cms.cloudServices': 'خزمەتگوزاری کڵاودی ساڵانە',
    'cms.planMonthly': 'مانگانە',
    'cms.planAnnual': 'ساڵانە',
    'cms.createPlan': 'زیادکردنی پلان',
    'cms.planCreated': 'پلان دروستکرا',
    'cms.planUpdated': 'پلان نوێکرایەوە',
    'cms.noPlansMarket': 'هێشتا پلان لەم بازارەدا نییە.',
    'cms.raqbSubscriptionOnly': 'Raqb تەنها بەشدارییە. پلانی هەمیشەیی نییە.',
    'cms.raqbTrialNote': 'تاقیکردنەوە: {days} ڕۆژ. تەنها دوای دەستپێکردنی تاقیکردنەوە دەستپێدەکات.',
    'cms.lifetimeVsCloud': 'مۆڵەتی هەمیشەیی ڕێگەت پێدەدات سۆفتوێرەکە بێ سنووری کات بەکاربهێنیت. خزمەتگوزاریی کڵاود ئابوونەیەکی ساڵانەی جیاوازە؛ کۆتاییهاتنی ئابوونەی کڵاود، مۆڵەتی هەمیشەیی هەڵناوەشێنێتەوە.',
    'cms.trialPlatformOff': 'دەستپێکردنی تاقیکردنەوە لە پلاتفۆرم ئێستا کوژاوەتەوە (TRIALS_ENABLED). ڕێکخستنەکانی خوارەوە تاقیکردنەوەی گشتی ناخەنە کار.',
    'cms.trialPlatformOn': 'دەستپێکردنی تاقیکردنەوە لە پلاتفۆرم کارایە. بەرهەم دەبێت تاقیکردنەوە پشتگیری بکات و کارپێکراو بێت.',
    'cms.trialProductSupport': 'ئەم بەرهەمە تاقیکردنەوە پشتگیری دەکات',
    'cms.trialPlatformGate': 'پلاتفۆرم ئێستا ڕێگە بە دەستپێکردنی تاقیکردنەوە دەدات',
    'cms.statusHelp': 'دەرکەوتنی کاتالۆگ: چالاک پیشان دەدرێت، شاردراو/ئەرشیف نایەت، بەم زووانە دێت بەبێ فرۆشتن وەک بەردەست.',
    'cms.purchasableHelp': 'ئاڵای کڕین. سەربەخۆیە لە دۆخی کاتالۆگ و بڵاوکردنەوەی ناوەڕۆک.',
    'cms.publishedHelp': 'دەقی فرۆشگا بڵاوکراوەتەوە. ڕەشنووس لە ئەدمین دەمێنێتەوە تا بڵاوکردنەوە.',
    'cms.heroAuthoritative': 'وێنەی سەرەکی، وێنە گەورەکەی پەڕەی بەرهەمە. گۆڕینی ئەم وێنەیە، وێنەکانی شاشە یان ئایکۆن ناسڕێتەوە.',
    'cms.savePrice': 'پاشەکەوتکردنی نرخ',
    'cms.priceSaved': 'نرخ پاشەکەوت کرا',
    'cms.annualSavings': 'پاشەکەوتکردنی ساڵانە بەرامبەر مانگانە',
    'cms.invalidPrice': 'نرخێکی دروست بنووسە',
    'cms.displayName': 'ناوی پیشاندان',
    'cms.heroIntro': 'پێشەکی hero',
    'cms.whoLede': 'پێشەکی کێ',
    'cms.highlightsTitle': 'سەردێڕی تایبەتمەندییەکان',
    'cms.explainHeading': 'سەردێڕی ڕوونکردنەوە',
    'cms.fitGuidance': 'ڕێنمایی گونجاندن',
    'cms.audiences': 'ئامانجەکان',
    'cms.features': 'تایبەتمەندییەکان',
    'cms.problems': 'کێشەکان',
    'cms.explainSections': 'بەشەکانی ڕوونکردنەوە',
    'cms.fitRecommendations': 'پێشنیارەکانی گونجاندن',
    'cms.addItem': 'زیادکردنی دانە',
    'cms.featureTitle': 'سەردێڕ',
    'cms.featureDesc': 'وەسف',
    'cms.faqNote': 'پرسیارە هاوبەشەکان بۆ هەموو بەرهەمەکان؛ پرسیارەکانی بەرهەم لێرەن.',
    'cms.addFaq': 'زیادکردنی پرسیار',
    'cms.faqShared': 'پرسیاری هاوبەش',
    'cms.faqProduct': 'پرسیاری بەرهەم',
    'cms.faqSaved': 'پرسیار پاشەکەوت کرا',
    'cms.faqCreated': 'پرسیار دروست کرا',
    'cms.faqDeleted': 'پرسیار سڕایەوە',
    'cms.newFaqQuestion': 'پرسیاری نوێ',
    'cms.savePolicy': 'پاشەکەوتکردنی سیاسەت',
    'cms.policySaved': 'سیاسەت پاشەکەوت کرا',
    'cms.mediaCounts': 'میدیا',
    'cms.openMediaManager': 'کردنەوەی بەڕێوەبەری میدیا',
    'cms.warnMissingAr': 'ناوەڕۆکی عەرەبی کەمە',
    'cms.warnMissingCkb': 'ناوەڕۆکی کوردی کەمە',
    'cms.warnMissingPricing': 'نرخدان ناتەواوە',
    'cms.warnMissingHero': 'وێنەی سەرەکی نییە',
    'cms.warnMissingScreenshots': 'هیچ وێنەی شاشەیەک نییە',
    'cms.warnMissingLaunch': 'بەستەری کردنەوە کەمە بۆ بەرهەمی چالاک',
    'cms.warnTrialMismatch': 'تاقیکردنەوە چالاکە بەڵام کارپێ نەکراوە',
    'cms.section.overview': 'پوختە',
    'cms.section.content': 'ناوەڕۆک',
    'cms.section.plans': 'پلان و نرخ',
    'cms.section.trial': 'تاقیکردنەوە',
    'cms.section.structure': 'ئامادەبووان و تایبەتمەندییەکان',
    'cms.section.faq': 'پرسیار',
    'cms.section.policy': 'سیاسەتی پشتگیری',
    'cms.section.media': 'میدیا',
    'cms.section.distribution': 'دابەشکردن',
    'cms.confirmLaunchUrl.title': 'گۆڕینی بەستەری کردنەوە؟',
    'cms.confirmLaunchUrl.message': 'کۆن: {old}\nنوێ: {next}',
    'cms.confirmPrice.title': 'گۆڕینی نرخی پلان؟',
    'cms.confirmPrice.message': '{plan}\nکۆن: {old}\nنوێ: {next}',
    'cms.confirmPublish.title': 'بڵاوکردنەوەی ناوەڕۆکی بازرگانی؟',
    'cms.confirmPublish.message': 'بڵاوکردنەوەی ناوەڕۆکی فرۆشگا بۆ {name}؟',
    'cms.confirmPolicy.title': 'پاشەکەوتکردنی سیاسەتی پشتگیری؟',
    'cms.confirmPolicy.message': 'نوێکردنەوەی سیاسەت بۆ {locale}؟',
    'cms.confirmFaqDelete.title': 'سڕینەوەی پرسیار؟',
    'cms.policy.lifetimeTitle': 'سەردێڕی هەمیشەیی',
    'cms.policy.lifetimeLede': 'پێشەکی هەمیشەیی',
    'cms.policy.lifetimeBody': 'ناوەڕۆکی هەمیشەیی',
    'cms.policy.supportTitle': 'سەردێڕی پشتگیری',
    'cms.policy.supportBugsTitle': 'سەردێڕی هەڵەکان',
    'cms.policy.supportBugsBody': 'ناوەڕۆکی هەڵەکان',
    'cms.policy.supportSecurityTitle': 'سەردێڕی ئاسایش',
    'cms.policy.supportSecurityBody': 'ناوەڕۆکی ئاسایش',
    'cms.policy.supportTechTitle': 'سەردێڕی تەکنیکی',
    'cms.policy.supportTechBody': 'ناوەڕۆکی تەکنیکی',
    'cms.policy.supportCustomTitle': 'سەردێڕی تایبەت',
    'cms.policy.supportCustomBody': 'ناوەڕۆکی تایبەت',
    'cms.policy.thirdPartyTitle': 'سەردێڕی لایەنی سێیەم',
    'cms.policy.thirdPartyBody': 'ناوەڕۆکی لایەنی سێیەم',
    'cms.policy.updatesTitle': 'سەردێڕی نوێکردنەوەکان',
    'cms.policy.updatesBody': 'ناوەڕۆکی نوێکردنەوەکان',
    'admin.mediaDeleteConfirm': 'ئەم میدیایە لاببرێت؟',
    'admin.mediaMoveUp': 'بۆ سەرەوە',
    'admin.mediaMoveDown': 'بۆ خوارەوە',
    'nav.media': 'میدیا',
    'portal.launch': 'بکەرەوە',
    'nav.trials': 'تاقیکردنەوەکان',
    'showcase.getStarted': 'تاقیکردنەوە، بەشداری، کردنەوە',
    'showcase.getStartedLede':
      'دوگمەکان لە کەتەلۆگ ئامادەن. پارەدان و Product Hub دواتر دێن.',
    'showcase.videoControls': 'کۆنترۆڵی ڤیدیۆ',
    'showcase.play': 'ڤیدیۆ لێبدە',
    'showcase.pause': 'ڤیدیۆ بوەستێنە',
    'showcase.mute': 'بێدەنگ',
    'showcase.unmute': 'دەنگ بکەرەوە',
    'showcase.fullscreen': 'شاشەی تەواو',
    'showcase.exitFullscreen': 'دەرچوون لە شاشەی تەواو',
    'showcase.videoReducedMotion': 'جوڵە کەمکراوەتەوە — وێنەیەکی جێگیر پیشان دەدرێت.',

    'soon.defaultSubtitle': 'بۆ وەشانێکی داهاتوو دانراوە و لە وەشانی ئێستای BeCoderدا بەردەست نییە.',

    'company.eyebrow': 'سۆفتوێری BeCoder',
    'company.heroTitle': 'سۆفتوێر، بۆ مانەوە دروستکراو.',
    'company.heroSubtitle': 'دروستکردن، بڵاوکردنەوە و پەرەپێدانی کۆمەڵێک سۆفتوێری جددی لە یەک پلاتفۆرمی متمانەپێکراو.',
    'company.exploreSoftware': 'گەڕان لە سۆفتوێر',
    'company.apiStatus': 'API {status}',
    'company.softwareCount': '١ ناونیشانی سۆفتوێر',
    'company.softwareCountPlural': '{count} ناونیشانی سۆفتوێر',
    'company.releaseCount': '١ وەشانی بڵاوکراوە',
    'company.releaseCountPlural': '{count} وەشانی بڵاوکراوە',

    'pulse.aria': 'لێدانی کۆمپانیا',
    'pulse.software': 'سۆفتوێر',
    'pulse.softwareDetail': 'دروستکراوی BeCoder',
    'pulse.titles': '{count} ناونیشان',
    'pulse.latestRelease': 'نوێترین وەشان',
    'pulse.noneYet': 'هێشتا نییە',
    'pulse.awaitingPublication': 'چاوەڕوانی بڵاوکردنەوە',
    'pulse.customerUse': 'بەکارهێنانی کڕیار',
    'pulse.activeCount': '{count} چالاک',
    'pulse.deployments': '{count} دامەزراندن',
    'pulse.needsAttention': 'پێویستی بە سەرنجدان هەیە',
    'pulse.trackedExceptions': 'کێشەی تۆمارکراو',
    'pulse.noTrackedExceptions': 'هیچ کێشەیەکی تۆمارکراو نییە',
    'pulse.platformHealth': 'تەندروستی پلاتفۆرم',
    'pulse.liveApiSignal': 'نیشانەی زیندووی API',

    'featured.sectionEyebrow': 'ئەوەی BeCoder دروستی دەکات',
    'featured.sectionTitle': 'سۆفتوێری دیار',
    'featured.sectionDesc': 'ئەو بەرهەمەی ئێستا سەرکردایەتی پۆرتفۆلیۆی BeCoder دەکات',
    'featured.managePortfolio': 'بەڕێوەبردنی پۆرتفۆلیۆ',
    'featured.empty': 'یەکەم بەرهەمت بڵاوبکەرەوە بۆ دامەزراندنی پۆرتفۆلیۆی سۆفتوێری BeCoder.',
    'featured.portfolioLabel': 'پۆرتفۆلیۆی BeCoder',
    'featured.fallbackTagline': 'سۆفتوێری مەبەستدار، ئەندازیاری و دابەشکراو لەلایەن BeCoder.',
    'featured.currentVersion': 'وەشانی ئێستا',
    'featured.released': 'بەرواری بڵاوکردنەوە',
    'featured.customers': 'کڕیارەکان',
    'featured.deployments': 'دامەزراندنەکان',
    'featured.stability': 'جێگیری',
    'featured.updatePolicy': 'سیاسەتی نوێکردنەوە',
    'featured.recentImprovements': 'باشترکردنە نوێیەکان',
    'featured.openProduct': 'کردنەوەی بەرهەم',
    'featured.coverLabel': 'سۆفتوێری BeCoder',

    'portfolio.sectionEyebrow': 'دروستکراوی BeCoder',
    'portfolio.sectionTitle': 'پۆرتفۆلیۆی سۆفتوێر',
    'portfolio.sectionDesc': 'هەموو بەرهەمێک یەک ستانداردی ئەندازیاری و وردکاری پەیڕەو دەکات',
    'portfolio.fallbackTagline': 'سۆفتوێر ئەندازیاری و دابەشکراو لەلایەن BeCoder.',
    'portfolio.customers': 'کڕیارەکان',
    'portfolio.deployments': 'دامەزراندن',
    'portfolio.policy': 'سیاسەت',
    'portfolio.updated': 'نوێکرایەوە لە {date}',
    'portfolio.updateUnavailable': 'مێژووی نوێکردنەوە بەردەست نییە',
    'portfolio.open': 'کردنەوە',

    'releases.sectionEyebrow': 'پێشکەوتنی نێردراو',
    'releases.sectionTitle': 'وەشانە نوێیەکان',
    'releases.sectionDesc': 'هەنگاوە پشتڕاستکراوەکانی سۆفتوێری بڵاوکراوەی BeCoder',
    'releases.pipeline': 'هێڵی وەشان',
    'releases.empty': 'هێشتا هیچ وەشانێکی بڵاوکراوە نییە.',
    'releases.fallbackProduct': 'بەرهەمی سۆفتوێر',
    'releases.noNotes': 'هیچ تێبینی وەشانێک نییە.',

    'activity.momentumEyebrow': 'جوڵەی کڕیار',
    'activity.momentumTitle': 'سۆفتوێر لە بەکارهێناندایە',
    'activity.momentumDesc': 'نوێترین دامەزراندن و چالاککردن لە چالاکیی ڕاستەقینەی کڕیار',
    'activity.newestDeployments': 'نوێترین دامەزراندنەکان',
    'activity.noDeployments': 'هیچ دامەزراندنێک تۆمار نەکراوە.',
    'activity.latestActivation': 'دوایین چالاککردن',
    'activity.customer': 'کڕیار',
    'activity.software': 'سۆفتوێر',
    'activity.newsEyebrow': 'ژووری هەواڵی سۆفتوێر',
    'activity.newsTitle': 'چی نوێیە',
    'activity.newsDesc': 'نیشانە ڕاستەقینەکان لە وەشان، نەخشەی ڕێگا و چالاکیی ئاسایش',
    'activity.latestRelease': 'نوێترین وەشان',
    'activity.noPublishedRelease': 'هیچ وەشانێکی بڵاوکراوە نییە',
    'activity.noDate': 'بەروار بەردەست نییە',
    'activity.releaseWork': 'کاری وەشان',
    'activity.releasesInProgress': '{count} وەشان لە کاردا',
    'activity.releaseInProgress': '١ وەشان لە کاردا',
    'activity.draftPipeline': 'هێڵی ڕەشنووس',
    'activity.upcomingSoftware': 'سۆفتوێری داهاتوو',
    'activity.noAnnouncedTitles': 'هیچ ناونیشانێکی ڕاگەیەندراو نییە',
    'activity.onRoadmap': '{count} لە نەخشەی ڕێگای گشتی',
    'activity.latestSecurity': 'دوایین چالاکیی ئاسایش',
    'activity.noSecurityEvent': 'هیچ ڕووداوی ئاسایشی نوێ نییە',
    'activity.auditChecked': 'تۆماری چاودێری پشکنرا',

    'kpi.sectionEyebrow': 'تەندروستی بازرگانی',
    'kpi.sectionTitle': 'پێوەرەکانی پلاتفۆرم',
    'kpi.sectionDesc': 'نیشانە کارگێڕییەکان کە پۆرتفۆلیۆی سۆفتوێر پشتگیری دەکەن',
    'kpi.customers': 'کڕیارەکان',
    'kpi.customersSub': 'هەژماری چالاک لەسەر پلاتفۆرم',
    'kpi.products': 'بەرهەمەکان',
    'kpi.productsSub': 'ناونیشانەکانی ناو دابەشکردن',
    'kpi.licenses': 'مۆڵەتەکان',
    'kpi.licensesSub': 'مۆڵەتی دەرچوو',
    'kpi.suspended': 'هەڵپەسێردراو',
    'kpi.suspendedSub': 'کورسییە هەڵپەسێردراو یان هەڵوەشاوەکان',

    'eng.sectionEyebrow': 'شەفافیەتی ئەندازیاری',
    'eng.sectionTitle': 'ئەندازیاری پلاتفۆرم',
    'eng.sectionDesc': 'نیشانە ڕاستەقینە و زیندووەکان لە سیستەمەکانی پشت سۆفتوێری BeCoder',
    'eng.apiStatus': 'دۆخی API',
    'eng.publishedArtifacts': 'فایلە بڵاوکراوەکان',
    'eng.checksummed': 'فایلە پشتڕاستکراوەکان',
    'eng.docsCoverage': 'داپۆشینی بەڵگەنامە',
    'eng.activeDeployments': 'دامەزراندنی چالاک',
    'eng.recentAudit': 'ڕووداوەکانی چاودێریی نوێ',

    'attention.sectionEyebrow': 'سەرنجدان',
    'attention.titleNone': 'هیچ کێشەیەکی تۆمارکراو نییە',
    'attention.titleSome': '{count} کێشەی تۆمارکراو',
    'attention.titleOne': '١ کێشەی تۆمارکراو',
    'attention.desc': 'تەنها ئەو دۆخانە پیشان دەدرێن کە لە داتای ئێستای پلاتفۆرم هەن',
    'attention.suspendedLicenses': 'مۆڵەتی هەڵپەسێردراو یان هەڵوەشاوە',
    'attention.draftReleases': 'ڕەشنووسی وەشان',
    'attention.inactiveDeployments': 'دامەزراندنی ناچالاک',
    'attention.hiddenProducts': 'بەرهەمی شاراوە یان ئەرشیڤکراو',

    'pulse.sectionEyebrow': 'چالاکیی پلاتفۆرم',
    'pulse.sectionTitle': 'لێدانی سیستەم',
    'pulse.sectionDesc': 'نوێترین کارە تەواوکراوەکان لەسەر پلاتفۆرمەکەت',
    'pulse.empty': 'هێشتا هیچ تۆمارێکی چاودێری نییە.',
    'pulse.authLogin': 'چوونەژوورەوەی پارێزراو تەواو بوو',

    'ops.sectionEyebrow': 'وردەکاری کارگێڕی',
    'ops.sectionTitle': 'بەڕێوەبردنی پلاتفۆرم',
    'ops.sectionDesc': 'کۆنترۆڵەکانی پشت دابەشکردن، دەسەڵات و تەندروستی وەشان',
    'ops.customerAccess': 'دەسەڵاتی کڕیار',
    'ops.customerAccessDetail': '{count} هەژمار',
    'ops.licenseOps': 'کارەکانی مۆڵەت',
    'ops.licenseOpsDetail': '{count} مۆڵەت',
    'ops.releasePipeline': 'هێڵی وەشان',
    'ops.releasePipelineDetail': '{count} ناونیشانی سۆفتوێر',

    'customer.heroEyebrow': 'کڕیاری BeCoder',
    'customer.heroTitle': 'سۆفتوێرکەت ئامادەیە.',
    'customer.heroSubtitle': 'بەرهەمی مۆڵەتدار، داگرتنی پارێزراو و ئامێری متمانەپێکراو — لە یەک شوێن.',
    'customer.openDownloads': 'کردنەوەی داگرتنەکان',
    'customer.trustStanding': 'هەژمار لە دۆخی باشدایە',
    'customer.trustLicensed': 'دەسەڵاتی مۆڵەتدار',
    'customer.trustPrivate': 'دابەشکردنی پارێزراوی سۆفتوێر',
    'customer.kpiProducts': 'بەرهەمەکان',
    'customer.kpiProductsSub': 'ناونیشانە مۆڵەتدارەکان بۆ تۆ',
    'customer.kpiLicenses': 'مۆڵەتەکان',
    'customer.kpiLicensesSub': 'مۆڵەتی چالاک و پێشوو',
    'customer.kpiDevices': 'ئامێرە چالاکەکان',
    'customer.kpiDevicesSub': 'کورسییە بەستراوەکانی لە بەکارهێنان',
    'customer.readyTitle': 'ئامادەی بەکارهێنان',
    'customer.readyDesc': 'سۆفتوێری ئێستا مۆڵەتدراو بۆ هەژمارەکەت',
    'customer.readyEmptyTitle': 'گەشتی سۆفتوێرکەت لێرە دەست پێدەکات.',
    'customer.readyEmptyBody': 'کاتێک BeCoder سۆفتوێر بۆ هەژمارەکەت مۆڵەت بدات، لێرە دەردەکەوێت.',
    'customer.downloadsLink': 'داگرتنەکان',
    'customer.loadFailed': 'بارکردن سەرکەوتوو نەبوو',

    'home.eyebrow': 'ماڵەوەی کڕیار',
    'home.title': 'بەخێربێیتەوە، {name}.',
    'home.subtitle': 'سۆفتوێر، مۆڵەت، نوێکردنەوە و پشتگیری — لە یەک شوێنی ئارام.',
    'home.exploreProducts': 'بەرهەمەکان بگەڕێ',
    'home.announceTitle': 'تۆ لەناو BeCoder دایت',
    'home.announceBody': 'ئەمە ماڵەوەی کڕیارەکەتە. سۆفتوێری خاوەندار، داگرتن، مۆڵەت و یارمەتی لێرەن — نە بڵاو لە نێوان ئامرازەکان.',
    'home.dismiss': 'لابردن',
    'home.kpi.products': 'سۆفتوێری مۆڵەتدار بۆ تۆ',
    'home.kpi.licenses': 'مافە چالاکەکان',
    'home.kpi.updatesLabel': 'نوێکردنەوە',
    'home.kpi.updates': 'ئامادە کاتێک پێویستت بێت',
    'home.ownedTitle': 'سۆفتوێری خاوەندار',
    'home.ownedDesc': 'ناونیشانە مۆڵەتدراوەکانی ئەم هەژمارە',
    'home.empty.productsTitle': 'گەشتی سۆفتوێرکەت لێرە دەست پێدەکات.',
    'home.empty.productsBody': 'ژینگەی BeCoder بگەڕێ، پاشان داوای مۆڵەت بکە لە فرۆشتن یان بەڕێوەبەر.',
    'home.updatesTitle': 'نوێکردنەوەی بەردەست',
    'home.updatesDesc': 'تێبینی وەشان بۆ سۆفتوێری خاوەندار',
    'home.empty.updatesTitle': 'نوێکردنەوە دەردەکەوێت کاتێک وەشان بڵاودەکرێتەوە.',
    'home.empty.updatesBody': 'بەرهەمە مۆڵەتدارەکان یەکەمجار لێرە وەشانی واژۆکراو بڵاودەکەنەوە.',
    'home.releaseNotesFallback': 'تێبینی وەشان لە کاتی داگرتندا بەردەستە.',
    'home.activityTitle': 'چالاکی دوایی',
    'home.activityDesc': 'مۆڵەت و ئامێرەکانی پەیوەست بە هەژمارەکەت',
    'home.activity.licenses': '{count} مۆڵەت لەسەر ئەم هەژمارە',
    'home.activity.devices': '{count} ئامێری چالاک',
    'home.empty.licensesTitle': 'کاتێک سۆفتوێر دەکڕیت، مۆڵەتەکانت لێرە دەردەکەون.',
    'home.empty.devicesTitle': 'ئامێرە بەستراوەکان دوای یەکەم دامەزراندن دەردەکەون.',
    'home.resourcesTitle': 'پشتگیری و سەرچاوە',
    'home.resourcesDesc': 'هەموو ئەوەی کڕیار چاوەڕوانی دەکات لە نزیک',
    'home.guide.docs': 'بەڵگەنامە',
    'home.guide.docsBody': 'ڕێنمایی بەرهەم بۆ ئەوانەی سۆفتوێر بەڕێوەدەبەن.',
    'home.guide.support': 'پشتگیری',
    'home.guide.supportBody': 'پەیوەندی بە BeCoder بکە کاتێک پێویستت بە مرۆڤ هەیە.',
    'home.guide.community': 'بنکەی زانیاری',
    'home.guide.communityBody': 'وەڵامی کرداری لە بنکەی زانیاری BeCoder.',
    'home.guide.invoices': 'فاکتۆرەکان',
    'home.guide.invoicesBody': 'تۆمارە بازرگانییەکان لێرە دەردەکەون دوای پشتڕاستکردنەوەی ڕێککەوتن.',

    'welcome.skip': 'تێپەڕاندن',
    'welcome.skipStep': 'ئێستا تێپەڕێنە',
    'welcome.back': 'گەڕانەوە',
    'welcome.continue': 'بەردەوامبوون',
    'welcome.enter': 'بچۆ ناو BeCoder',
    'welcome.learnMore': 'زیاتر بزانە',
    'welcome.selectProduct': '{name} وەک بەرهەمی دڵخواز هەڵبژێرە',
    'welcome.catalogError': 'کەتەلۆگی بەرهەم بارنەبوو. پێشکەوتنی بەخێرهاتنەکەت پارێزراوە.',
    'welcome.catalogEmpty': 'بەرهەمە نوێکانی BeCoder هەرکات بڵاوبکرێنەوە لێرە دەردەکەون.',
    'welcome.saveError': 'ئەم هەنگاوە پاشەکەوت نەکرا. پێش تەواوکردن پەیوەندییەکەت بپشکنە؛ هەژمارەکەت نەگۆڕاوە.',
    'welcome.progress': 'هەنگاوی {current} لە {total}',
    'welcome.step1.eyebrow': 'بەخێربێیت',
    'welcome.step1.title': 'بەخێربێیت بۆ BeCoder.',
    'welcome.step1.body': 'یەک شوێن بۆ دۆزینەوە، بەڕێوەبردن و گەشەکردن لەگەڵ سۆفتوێری پیشەیی بازرگانی.',
    'welcome.step2.title': 'ژینگەکە بناسە.',
    'welcome.step2.body': 'هەر بەرهەمێک بۆ کارێکی ڕوون دروستکراوە. لە هەر شوێنێک دەستپێبکە — باقی نزیک دەمێنێتەوە.',
    'welcome.product.glasspos': 'خاڵی فرۆشتنی خێرای ڕۆژانە.',
    'welcome.product.ledger': 'چاودێری دارایی کاروبار.',
    'welcome.product.befx': 'سۆفتوێری تایبەت بۆ ئۆفیسی ئاڵوگۆڕ.',
    'welcome.product.iqstore': 'بەرهەم، کەتەلۆگ و کۆگا.',
    'welcome.status.available': 'بەردەست',
    'welcome.step3.title': 'دەتەوێت سەرەتا چی بگەڕێیت؟',
    'welcome.step3.body': 'خاڵی دەستپێک هەڵبژێرە. هەمیشە دەتوانیت بگەڕێیتەوە بۆ ئەم ماڵەوە.',
    'welcome.start.products': 'بەرهەمەکان بگەڕێ',
    'welcome.start.portal': 'دەروازەی کڕیار بکەرەوە',
    'welcome.start.licenses': 'مۆڵەتەکان بەڕێوەببە',
    'welcome.start.docs': 'سەردانی بەڵگەنامە',
    'welcome.start.dashboard': 'بەردەوامبوون بۆ داشبۆرد',
    'welcome.step4.title': 'بیکە بە هی خۆت.',
    'welcome.step4.body': 'هەڵبژاردەی ئارەزوومەندانە. هەر شتێک تێپەڕێنە — BeCoder هێشتا وەک ماڵ دەمێنێتەوە.',
    'welcome.field.name': 'ناوی پیشاندان',
    'welcome.field.language': 'زمان',
    'welcome.field.theme': 'ڕووکار',
    'welcome.field.timezone': 'ناوچەی کات',
    'welcome.field.notifications': 'ئاگادارییەکان',
    'welcome.field.photoHint': 'وێنەی پڕۆفایل لە Google دێت کاتێک بەستراوە. دواتر لە پڕۆفایل دەتوانیت بیگۆڕیت.',
    'welcome.theme.dark': 'تاریک',
    'welcome.theme.light': 'ڕووناک',
    'welcome.theme.system': 'سیستەم',
    'welcome.notify.products': 'نوێکردنەوەی بەرهەم',
    'welcome.notify.security': 'ئاگاداری ئاسایش',
    'welcome.notify.announcements': 'ڕاگەیاندنی کۆمپانیا',
    'welcome.step5.title': 'ئامادەیت.',
    'welcome.step5.body': 'BeCoderت تێگەیشت. دەزانیت چی دروست دەکەین. ئێستا بچۆ ناو پلاتفۆرمەکە.',
    'welcome.ready.title': 'تۆ لەناویت.',
    'welcome.ready.body': 'دەتبەین بۆ خاڵی دەستپێک…',
    'welcome.reset': 'دووبارەکردنی ئەزموونی بەخێرهاتن',
    'welcome.resetDone': 'ئەزموونی بەخێرهاتن ڕیسێت کرا. دووبارە بچۆ ژوورەوە بۆ بینینی.',

    'portal.customers': 'کڕیارەکان',
    'portal.products': 'بەرهەمەکان',
    'portal.releases': 'وەشانەکان',
    'portal.licenses': 'مۆڵەتەکان',
    'portal.devices': 'ئامێرەکان',
    'portal.auditLogs': 'تۆماری چاودێری',
    'portal.featureFlags': 'ئاڵاکانی تایبەتمەندی',
    'portal.myProducts': 'بەرهەمەکانم',
    'portal.downloadsTitle': 'داگرتن و نوێکردنەوە',
    'portal.downloadsDesc': 'بەستەری واژۆکراو و کاتی دیاریکراو بۆ وەشانە مۆڵەتدارەکان',
    'portal.profile': 'پرۆفایل',
    'portal.supportTitle': 'پشتیوانی و بەڵگەنامە',
    'portal.helpTitle': 'یارمەتی',
    'portal.helpLede': 'بەڵگەنامەی بەرهەم ببینە یان پەیوەندی بە فرۆشتنی BeCoder بکە بۆ پشتگیری مرۆیی.',
    'portal.helpSalesBody': 'پرسیارەکانی مۆڵەت، تاقیکردنەوە و بازرگانی لەڕێگەی پەیوەندی فرۆشتن.',
    'portal.downloadsEmpty': 'هێشتا بەرهەمی خاوەندار نییە — کەتەلۆگ بگەڕێ یان پەیوەندی بە فرۆشتن بکە.',
    'portal.softLaunchTitle': 'گەیاندنی ئەپ هێشتا ئۆنلاین نییە',
    'portal.softLaunchTitleNamed': '{name} هێشتا بۆ هەڵدانی ڕاستەوخۆ ئامادە نییە',
    'portal.softLaunchBody':
      'مافەکەت چالاکە. داگرتن بەکاربهێنە کاتێک وەشان بڵاو دەبێتەوە، یان پەیوەندی بە فرۆشتن بکە.',
    'portal.softLaunchNoReleases':
      'هێشتا پاکێجی دامەزراندن بۆ ئەم بەرهەمە بڵاونەکراوەتەوە. پەیوەندی بە فرۆشتن بکە ئەگەر پێویستت بە بینامەیە.',
    'portal.noLicenses': 'کاتێک سۆفتوێر دەکڕیت، مۆڵەتەکانت لێرە دەردەکەون.',
    'portal.noPublishedReleases': 'وەشانی بڵاوکراوە لێرە دەردەکەوێت کاتێک بینامەکان ئامادە بن.',
    'portal.noOwnedProducts': 'گەشتی سۆفتوێرکەت لێرە دەست پێدەکات. کەتەلۆگ بگەڕێ، پاشان ئەوەی گونجاوە مۆڵەت بکە.',
    'portal.createCustomer': 'دروستکردنی کڕیار',
    'portal.createProduct': 'دروستکردنی بەرهەم',
    'portal.createUpload': 'دروستکردن و بارکردن',
    'portal.issueLicense': 'دەرکردنی مۆڵەت',
    'portal.registerDevice': 'تۆمارکردنی ئامێر',
    'portal.suspend': 'هەڵپەساردن',
    'portal.activate': 'چالاککردن',
    'portal.publish': 'بڵاوکردنەوە',
    'portal.revoke': 'هەڵوەشاندنەوە',
    'portal.forceDeactivate': 'ناچالاککردنی خێرا',
    'portal.remove': 'لابردن',
    'portal.signOut': 'چوونەدەرەوە',
    'portal.download': 'داگرتن',
    'portal.name': 'ناو',
    'portal.email': 'ئیمەیڵ',
    'portal.password': 'وشەی نهێنی (١٠+)',
    'portal.slug': 'ناسنامە',
    'portal.productName': 'ناو',
    'portal.tagline': 'کورتە وەسف',
    'portal.description': 'وەسف (Markdown)',
    'portal.version': 'وەشان',
    'portal.changelog': 'تۆماری گۆڕانکاری',
    'portal.architecture': 'ئەرشیتهکتور',
    'portal.deviceName': 'ناوی ئامێر',
    'portal.appVersion': 'وەشانی بەرنامە',
    'portal.product': 'بەرهەم',
    'portal.key': 'کلیل',
    'portal.type': 'جۆر',
    'portal.seats': 'کورسی',
    'portal.ends': 'کۆتایی',
    'portal.lifetime': 'هەمیشەیی',
    'portal.noFile': 'فایل نییە',
    'portal.on': 'کارا',
    'portal.off': 'ناکارا',
    'portal.actor': 'کردارەر',
    'portal.action': 'کردار',
    'portal.resource': 'سەرچاوە',
    'portal.time': 'کات',
    'portal.allEvents': 'هەموو',
    'portal.authEvents': 'چوونەژوورەوە',
    'portal.licenseEvents': 'مۆڵەتەکان',
    'portal.downloadEvents': 'داگرتنەکان',
    'portal.subscriptionsDesc': 'مۆڵەتەکان لەلایەن بەڕێوەبەرانی BeCoder دوای پشتڕاستکردنەوەی کڕین لە دەرەوە دەردەچن.',
    'portal.subscriptionsEmpty': 'هێشتا ئابوونە نییە. پەیوەندی بە فرۆشتن بکە بۆ مۆڵەت.',
    'portal.invoicesEmpty': 'فاکتۆرەکان لێرە دەردەکەون دوای تۆمارکردنی ڕێککەوتنی بازرگانی. پەیوەندی بە فرۆشتن بکە.',
    'portal.supportEmpty': 'بەڵگەنامە بکەرەوە بۆ ڕێنمایی بەرهەم، یان پەیوەندی بە فرۆشتن بکە بۆ یارمەتی بازرگانی.',
    'portal.profileSignedIn': 'چووتە ژوورەوە وەک',
    'portal.profileRole': 'ڕۆڵ',
    'security.title': 'ناسنامە و ئاسایش',
    'security.subtitle': 'ڕێگاکانی چوونەژوورەوە بۆ BeCoder بەڕێوەببە.',
    'security.pageDesc': 'وردەکاری هەژمار و ئاسایشی ناسنامە.',
    'security.methods': 'ڕێگاکانی چوونەژوورەوە',
    'security.status': 'دۆخی هەژمار',
    'security.connected': 'بەستراوە',
    'security.notConnected': 'نەبەستراوە',
    'security.connectGoogle': 'بەستنی Google',
    'security.disconnectGoogle': 'لابردنی Google',
    'security.replaceGoogle': 'گۆڕینی هەژماری Google',
    'security.emailPassword': 'ئیمەیڵ و وشەی نهێنی',
    'security.createdVia': 'هەژمار دروستکراوە لە ڕێگەی',
    'security.emailVerified': 'ئیمەیڵ پشتڕاستکراوەتەوە',
    'security.lastSignIn': 'دوایین چوونەژوورەوەی سەرکەوتوو',
    'security.lastProvider': 'دوایین دابینکەری ناسنامە',
    'security.yes': 'بەڵێ',
    'security.no': 'نەخێر',
    'security.unlockTitle': 'دڵنیابە کە تۆیت',
    'security.unlockBody': 'پێش گۆڕینی ڕێکخستنی ناسنامە دووبارە پشتڕاستی بکەوە.',
    'security.confirmPassword': 'پشتڕاستکردنەوە بە وشەی نهێنی',
    'security.confirmGoogle': 'پشتڕاستکردنەوە بە Google',
    'security.unlocked': 'ڕێکخستنی ئاسایش بۆ ماوەیەکی کورت کراوەتەوە.',
    'security.reauthRequired': 'پێش بەردەوامبوون دووبارە پشتڕاستی بکەوە.',
    'security.reauthOk': 'ناسنامە پشتڕاستکرایەوە',
    'security.reauthOkBody': 'دەتوانیت بۆ چەند خولەکێک ڕێکخستنی ئاسایش نوێ بکەیتەوە.',
    'security.reauthFailed': 'دووبارە پشتڕاستکردنەوە سەرکەوتوو نەبوو',
    'security.addPassword': 'زیادکردنی وشەی نهێنی',
    'security.changePassword': 'گۆڕینی وشەی نهێنی',
    'security.newPassword': 'وشەی نهێنی نوێ',
    'security.confirmNewPassword': 'پشتڕاستکردنەوەی وشەی نهێنی نوێ',
    'security.passwordMismatch': 'وشە نهێنییەکان دەبێت یەکسان بن و لانیکەم ١٠ پیت بن.',
    'security.passwordAdded': 'وشەی نهێنی زیادکرا',
    'security.passwordChanged': 'وشەی نهێنی نوێکرایەوە',
    'security.googleLinked': 'Google بەسترا',
    'security.googleReplaced': 'هەژماری Google گۆڕدرا',
    'security.googleDisconnected': 'Google لابردرا',
    'security.needOtherMethod': 'پێش لابردنی Google وشەی نهێنی زیاد بکە.',
    'security.sessions': 'دانیشتنە چالاکەکان',
    'security.signOutAll': 'چوونەدەرەوە لە هەموو ئامێرەکانی تر',
    'security.thisDevice': 'ئەم ئامێرە',
    'security.unknownDevice': 'ئامێری نەناسراو',
    'security.lastSeen': 'دوایین بینین',
    'security.revokeSession': 'هەڵوەشاندنەوە',
    'security.sessionRevoked': 'دانیشتن هەڵوەشایەوە',
    'security.sessionsRevoked': 'دانیشتنەکانی تر هەڵوەشاندنەوە',
    'security.noSessions': 'هیچ دانیشتنێکی چالاک نییە.',
    'portal.kbSearch': 'گەڕان لە وتارەکانی یارمەتی...',
    'portal.kbViews': '{count} بینین',
    'portal.blogDesc': 'نوێکاری ئەندازیاری، قووڵبوونەوەی تەکنیکی و تۆماری بەرهەم.',
    'portal.kbDesc': 'گەڕان لە ڕێنمایی پشتیوانی، چارەسەری مۆڵەت و ڕێکخستنی دامەزراوە.',

    'status.pageTitle': 'دۆخی خزمەتگوزاری',
    'status.pageSubtitle': 'نیشانە زیندووەکانی خزمەتگوزارییەکانی پلاتفۆرمی BeCoder.',
    'status.apiLiveness': 'زیندوویی API',
    'status.apiReadiness': 'ئامادەیی API',
    'status.database': 'بنکەدراوە',
    'status.marketplace': 'بازاڕ',
    'status.billing': 'دابینکەرانی پارەدان',
    'status.website': 'ماڵپەڕی BeCoder',
    'status.catalogService': 'کەتەلۆگی بەرنامەکان',
    'status.accounts': 'هەژماری کڕیار',
    'status.downloadsLicensing': 'داگرتن و مۆڵەت',
    'status.payments': 'پارەدان',
    'status.notAvailableYet': 'هێشتا بەردەست نییە',
    'status.comingSoonNeutral': 'بەم زووانە',

    'about.title': 'دەربارەی BeCoder',
    'about.heroTitle': 'سۆفتوێری کرداریی کاروبار.',
    'about.private': 'تایبەت',
    'about.privateDesc': 'بازاڕی گشتی نییە. تەنها بەرهەمەکانی خۆمان.',
    'about.licensed': 'مۆڵەتدار',
    'about.licensedDesc': 'مۆڵەت، ئامێر و وەشان — لە ژێر کۆنترۆڵدا.',
    'about.deliberate': 'وریایانە',
    'about.deliberateDesc': 'نرخی ڕوون لە پەڕەکانی بەرهەم. کڕینی ڕێنماییکراو کاتێک ئامادە بیت.',
    'about.contactTitle': 'پەیوەندی',
    'about.inquiryTitle': 'پرسیاری گشتی',
    'about.messageLabel': 'پەیام',
    'about.messageNoted': 'پەیامەکە وەرگیرا',
    'about.messageBody': 'پرسیارە گشتییەکان لە ڕێگەی سندوقی فرۆشتن/پشتیوانی ڕێکخراو لە ژینگەوە وەڵام دەدرێنەوە.',
    'about.placeholder': 'چۆن یارمەتیت بدەین؟',
    'about.send': 'ناردنی پەیام',

    'pricing.starter': 'دەستپێک',
    'pricing.starterBlurb': 'یەک بەرهەم. تیمێکی سەرنجتەرکیز.',
    'pricing.starter1': 'یەک بەرنامە',
    'pricing.starter2': 'تا ٣ ئامێر',
    'pricing.starter3': 'پشتیوانی ئیمەیڵ',
    'pricing.team': 'تیم',
    'pricing.teamBlurb': 'چەند کەس و ئامێر لەسەر یەک هەژمار.',
    'pricing.team1': 'چەندین بەرهەم',
    'pricing.team2': 'کۆنترۆڵی ئەوەی کێ کام ئامێر بەکاردەهێنێت',
    'pricing.team3': 'ڕێڕەوی پشتیوانی پێشینەدار',
    'pricing.enterprise': 'دامەزراوە',
    'pricing.enterpriseBlurb': 'ڕێککەوتنی تایبەت. کۆنترۆڵی تەواو.',
    'pricing.enterprise1': 'سنووری بەکارهێنەر و ئامێری تایبەت',
    'pricing.enterprise2': 'دامەزراندنی تایبەت',
    'pricing.enterprise3': 'فاکتۆری دەستی',
    'pricing.featured': 'پێشنیارکراو',
    'pricing.contact': 'پەیوەندی بۆ کڕین',
    'pricing.noCheckoutNote':
      'نرخی بەرهەمەکان لە کەتەلۆگ ببینە، یان پەیوەندی بە فرۆشتن بکە بۆ ڕێگایەکی ڕێنماییکراو.',

    'plan.name.starter': 'دەستپێک',
    'plan.name.growth': 'گەشە',
    'plan.name.enterprise': 'دامەزراوە',
    'plan.period.month': 'مانگ',
    'plan.period.year': 'ساڵ',
    'plan.feature.coreAccess': 'دەستگەیشتنی سەرەکی بۆ {name}',
    'plan.feature.coreAccessShort': 'دەستگەیشتنی سەرەکی',
    'plan.feature.emailSupport': 'پشتیوانی ئیمەیڵ',
    'plan.feature.oneOrg': '١ ڕێکخراو',
    'plan.feature.everythingStarter': 'هەموو شت لە دەستپێک',
    'plan.feature.prioritySupport': 'پشتیوانی پێشینەدار',
    'plan.feature.moreSeats': 'کورسیی زیاتر',
    'plan.feature.advancedReports': 'ڕاپۆرتی پێشکەوتوو',
    'plan.feature.customTerms': 'مەرجی تایبەت',
    'plan.feature.dedicatedSuccess': 'سەرکەوتنی تایبەت',
    'plan.feature.ssoReady': 'ڕێڕەوی ئامادەی SSO',
    'plan.feature.slaOptions': 'بژاردەکانی SLA',
    'plan.feature.fastCheckout': 'پارەدانی خێرا لەگەڵ کارکردنی ئۆفلاین',
    'plan.feature.inventorySync': 'هاوکاتکردنی کۆگا و چەند تۆمار',
    'plan.feature.receiptsShifts': 'وەسڵ و شفت و ڕۆڵی کارمەند',
    'plan.feature.tabletDesktop': 'کار دەکات لە تابلێت و دێسکتۆپ',
    'plan.feature.booksParties': 'کتێب و لایەن و داخستنی ماوە',
    'plan.feature.doubleEntry': 'شوێنی کاری هێمن بە دوو تۆمار',
    'plan.feature.statements': 'کەشفی بێ ژاوەژاو',
    'plan.feature.buySell': 'کڕین/فرۆشتن بە دیسیپلینی خەزنە',
    'plan.feature.remittances': 'ناردە و داخستنی ڕۆژانە',
    'plan.feature.auditExchange': 'کارەکانی ئاڵوگۆڕی گونجاو بۆ وردبینی',
    'plan.feature.imageCatalog': 'کەتەلۆگی بەرهەم ئامادەی وێنە',
    'plan.feature.ordersOps': 'داواکاری و کارەکانی فرۆشگا',
    'plan.feature.regionalRetail': 'دروستکراو بۆ فرۆشی ناوچەیی',
    'plan.feature.doubleEntryClarity': 'ڕوونی دوو تۆمار بەبێ شێواوی',
    'plan.feature.exportStatements': 'کەشفی ئامادە بۆ هەناردە',
    'plan.feature.growingTeams': 'دروستکراو بۆ تیمە گەشەسەندووەکان',
    'plan.feature.buySellLive': 'کڕین/فرۆشتن بە نرخى زیندوو',
    'plan.feature.vaultRemittance': 'کاری خەزنە و ناردە',
    'plan.feature.auditDailyClose': 'داخستنی ڕۆژانەی گونجاو بۆ وردبینی',
    'plan.feature.multiLangCounter': 'ڕووکاری ژمێریاری فرەزمان',
    'plan.feature.catalogOrders': 'کارەکانی کەتەلۆگ و داواکاری',
    'plan.feature.imagePages': 'پەڕەی بەرهەم ئامادەی وێنە',
    'plan.feature.roleStore': 'بەڕێوەبردنی فرۆشگا بەپێی ڕۆڵ',
    'plan.feature.designedRetail': 'دیزاینکراو بۆ فرۆشی ناوچەیی',
    'plan.feature.liveScores': 'ئەنجامی زیندوو و تەوژمی یاری',
    'plan.feature.fanNotifications': 'ئاگادارییەکانی گرنگ بۆ هاندانەران',
    'plan.feature.sportsUi': 'ڕووکاری وەرزشی هێمن',
    'plan.feature.mobileFirst': 'ئەزموونی یەکەم بۆ مۆبایل',
    'plan.feature.privateDist': 'دابەشکردنی پارێزراو بۆ سۆفتوێرەکەت',
    'plan.feature.licensesDevices': 'مۆڵەت و ئامێر و وەشان',
    'plan.feature.orgIdentity': 'ناسنامەی ئامادە بۆ ڕێکخراو',
    'plan.feature.ecosystem': 'دروستکراو بۆ سیستەمی BeCoder',
    'plan.category.application': 'ئەپلیکەیشن',
    'plan.category.pos': 'خاڵی فرۆشتن',
    'plan.category.finance': 'دارایی',
    'plan.category.exchange': 'ئاڵوگۆڕ',
    'plan.category.pharmacy': 'دەرمانخانە',
    'plan.category.commerce': 'بازرگانی',
    'plan.category.sports': 'وەرزش',
    'plan.category.platform': 'پلاتفۆرم',
    'status.active': 'چالاک',
    'status.trialing': 'تاقیکردنەوە',
    'status.trial': 'تاقیکردنەوە',
    'status.canceled': 'هەڵوەشاوە',
    'status.cancelled': 'هەڵوەشاوە',
    'status.past_due': 'دواکەوتوو',
    'status.incomplete': 'ناتەواو',
    'status.expired': 'بەسەرچوو',
    'status.pending': 'چاوەڕوان',
    'status.paid': 'پارەدراو',
    'status.open': 'کراوە',
    'status.revoked': 'هەڵوەشێنراو',
    'status.suspended': 'ڕاگیراو',
    'hub.orgNameDefault': 'ڕێکخراوی {name}',
    'hub.launchFailedShort': 'هەڵدانی سەرکەوتوو نەبوو',
    'portal.provider.fastpay': 'FastPay',
    'portal.provider.manual': 'دەستی',
    'portal.provider.none': 'نییە',
    'portal.provider.google': 'Google',
    'content.seed.glasspos.tagline': 'سیستەمی خاڵی فرۆشتنی خێرا و متمانەپێکراو بۆ کاروباری مۆدێرن.',
    'content.seed.glasspos.description':
      'GlassPOS ئەپلیکەیشنی خاڵی فرۆشتنی BeCoderـە — پارەدانی خێرا، ڕوونی کۆگا، و تۆماری ئامادەی ئۆفلاین.',
    'content.seed.glasspos.descriptionShort': 'GlassPOS ئەپلیکەیشنی خاڵی فرۆشتنی BeCoderـە.',
    'content.seed.ledger.tagline': 'کتێبەکان هێمن و دروست دەمێننەوە.',
    'content.seed.ledger.description':
      'Ledger دارایی دوو تۆمار دەهێنێتە شوێنی کاری هێمن — لایەن و ماوە و کەشفی بێ ژاوەژاو.',
    'content.seed.ledger.descriptionShort': 'Ledger دارایی دوو تۆمار دەهێنێتە شوێنی کاری هێمن.',
    'content.seed.befx.tagline': 'کارەکانی ئاڵوگۆڕ بە دیسیپلینی خەزنە.',
    'content.seed.befx.description':
      'BeFX کڕین/فرۆشتن و خەزنە و ناردە بەڕێوەدەبات لەگەڵ داخستنی ڕۆژانەی گونجاو بۆ وردبینی.',
    'content.seed.befx.descriptionShort': 'BeFX کڕین/فرۆشتن و خەزنە و ناردە بەڕێوەدەبات.',
    'content.seed.iqStore.tagline': 'کەتەلۆگی بازرگانی دروستکراو بۆ فرۆشی ناوچەیی.',
    'content.seed.iqStore.description':
      'IQ Store بەرهەم و داواکاری و کارەکانی فرۆشگا بەڕێوەدەبات لەگەڵ ئەزموونی کەتەلۆگی ئامادەی وێنە.',
    'content.seed.iqStore.descriptionShort': 'IQ Store بەرهەم و داواکاری و کارەکانی فرۆشگا بەڕێوەدەبات.',
    'content.seed.aura.tagline': 'تەوژمی وەرزشی زیندوو بۆ هاندانەران.',
    'content.seed.aura.description':
      'Aura Sport وزەی یاری لەگەڵ ڕووکاری هێمن دەدات — ئەنجام و ئاگاداری و ئەزموونی وەرزشی یەکەم بۆ مۆبایل.',

    'docs.quickstart': 'دەستپێکی خێرا CLI',
    'docs.auth': 'ڕەسەنایەتی',
    'docs.sdkC': 'جێگیرکردنی C / C++',
    'docs.offline': 'پشتڕاستکردنەوەی ئۆفلاین',
    'docs.title': 'بەڵگەنامە',
    'docs.products': 'بەڵگەنامەی بەرهەم',
    'docs.productsBody': 'ڕێنمایی و وردەکاری بەرهەمەکانی کەتەلۆگی ئێستای سۆفتوێری BeCoder.',
    'docs.openProductDocs': 'بەڵگەنامە بکەرەوە',
    'docs.viewProduct': 'بەرهەم ببینە',
    'docs.developers': 'یارمەتی بەرهەم',
    'docs.subtitle':
      'یارمەتی و بەڵگەنامە بۆ بەرهەمەکانی BeCoder — بەپێی بەرنامە، بە زمانی سادەی کڕیار.',
    'docs.rust': 'SDKـی Rust',
    'docs.rustTitle': 'تێبینییەکانی SDKـی Rust',
    'docs.cTitle': 'تێبینییەکانی جێگیرکردنی C / C++',
    'docs.offlineTitle': 'چالاککردنی ئۆفلاین',

    'status.operations': 'دۆخ',
    'status.notReady': 'ئامادە نییە ({reason})',
    'status.databaseNamed': 'بنکەدراوە ({name})',
    'status.sampleAt': 'پشکنین · {time}',
    'portal.kbEmpty':
      'وتارەکانی یارمەتی هێشتا بڵاو نەکراونەتەوە. بەڵگەنامەی بەرهەم یان پەیوەندی فرۆشتن بەکاربهێنە.',
    'portal.kbEmptyDocs': 'بەڵگەنامەی بەرهەمەکان لە کەتەلۆگی BeCoder ببینە.',
    'portal.kbEmptySales': 'لە ڕێگەی پەیوەندی فرۆشتن پەیوەندی بە BeCoder بکە تا سندوقی پشتگیری ئامادە دەبێت.',
    'docs.quickstartPending':
      'ڕێنمایی دامەزراندن و چالاککردن لێرە دەردەکەوێت کاتێک پاکێجی وەشانی بەرهەم بڵاو دەکرێتەوە.',
    'docs.sdkPending':
      'تێبینییەکانی یەکخستنی SDK لەگەڵ بەڵگەنامەی مۆڵەتی هەر بەرهەمێک بڵاو دەکرێتەوە.',
    'docs.offlinePending':
      'هەنگاوەکانی چالاککردنی ئۆفلاین دەنووسرێت کاتێک پاکێجی مۆڵەتی ئۆفلاین بەردەست بێت.',
    'status.liveChecks':
      'سەیرێکی خێرا لە خزمەتگوزارییەکانی BeCoder کە کڕیاران بەکاریان دەهێنن — ماڵپەڕ، کەتەلۆگی بەرنامە، هەژمار، داگرتن، و پارەدان.',

    'soon.organizations': 'ڕێکخراوەکان',
    'soon.organizationsBody': 'لەم خولەدا کڕیارەکان بۆ بەڕێوەبردنی هەژمار بەکاربهێنە.',
    'soon.moderation': 'چاودێری',
    'soon.moderationBody': 'چاودێری بازاڕ بەشی ئەم خولە نییە.',
    'soon.organizationBody': 'بەڕێوەبردنی ڕێکخراو دواخراوە.',
    'soon.checkout': 'پارەدان',
    'soon.checkoutBody': 'دابینکەرانی پارەدان لەم خولەدا جێگیر نەکراون.',
    'soon.cartBody': 'پارەدان ناکارا کراوە تا فاکتۆر چالاک بکرێت.',
    'soon.developerBody': 'بڵاوکردنەوەی لایەنی سێیەم ناکارا کراوە.',
    'soon.wishlist': 'لیستی ئارەزوو',
    'account.title': 'هەژمارەکەم',
    'account.subtitle': 'پرۆفایلی بەڕێوەبەر و وشەی نهێنی بەڕێوەببە.',
    'account.profile': 'زانیاری پرۆفایل',
    'account.details': 'وردەکاری هەژمار',
    'account.displayName': 'ناوی پیشاندان',
    'account.role': 'ڕۆڵ',
    'account.save': 'پاشەکەوتکردنی گۆڕانکارییەکان',
    'account.saved': 'پرۆفایل پاشەکەوت کرا',
    'account.savedBody': 'ناوی پیشاندان نوێکرایەوە.',
    'account.nameTooShort': 'ناوی پیشاندان دەبێت لانیکەم ٢ پیت بێت.',
    'account.passwordHelp':
      'وشەی نهێنی ئێستا پشتڕاست بکەوە، پاشان وشەیەکی نوێ دابنێ. دوای گۆڕینی وشەی نهێنی دانیشتنەکانی تر دەچنە دەرەوە.',
    'account.passwordChangedBody': 'جارێکی تر وشەی نهێنی نوێ بەکاربهێنە.',
    'theme.light': 'ڕووناک',
    'theme.dark': 'تاریک',
    'theme.system': 'سیستەم',
    'nav.language': 'زمان',
    'nav.theme': 'ڕووکار',
    'nav.menu': 'مێنۆ',

    'portal.details': 'وردەکاری',
    'portal.licensePrefix': 'مۆڵەت:',
    'portal.lastSeen': 'دوایین بینین {date}',
    'portal.register': 'تۆمارکردن',
    'portal.flagsNote':
      'بازاڕ و پارەدان لەم خولەدا بە سیاسەتی API ڕەت دەکرێنەوە تەنانەت ئەگەر بگۆڕدرێن. دەروازەکانی ژینگەش جێبەجێ دەبن.',
    'portal.noInAppPayments': 'پارەدانی ناو بەرنامە نییە',
    'portal.supportDeferred':
      'ڕێڕەوی تیکێت دواخراوە. بەڵگەنامەی بەرهەم و بەستەری پشتیوانی لە خوارەوە بەکاربهێنە.',
    'portal.invoicesDeferred': 'مێژووی فاکتۆر لێرە دەردەکەوێت کاتێک دابینکەری فاکتۆر ببەسترێت.',
    'portal.invoicesNoGateway':
      'لەم وەشانەدا دەروازەی پارەدان نییە. کڕینەکان لە ڕێگەی پەیوەندی فرۆشتن.',
    'portal.auditPlatform': 'پلاتفۆرم',
    'portal.seatsCount': 'کورسی {count}',
    'portal.endsPrefix': 'کۆتایی {date}',
    'portal.signedOut': 'چوویتە دەرەوە',
    'portal.sessionCleared': 'دانیشتن پاککرایەوە',
    'command.paletteAria': 'تابلۆی فەرمان',

    'license.modalTitle': 'مۆڵەتی دەسەڵات',
    'license.modalBody':
      'چالاککردنی خۆکار لە بازاڕ ناکارا کراوە. بەڕێوەبەران مۆڵەت دەردەکەن و دوای چوونەژوورەوە لە مۆڵەتەکانمدا دەردەکەون.',
    'license.placeholder': 'کلیلی مۆڵەت (تەنها بۆ سەرچاوە)',
    'license.unavailableTitle': 'بەردەست نییە',
    'license.unavailableBody': 'پەیوەندی بە بەڕێوەبەر بکە بۆ دەرکردن یان بەڕێوەبردنی مۆڵەت.',

    'ai.modalBody':
      'دابینکەرانی دەرەوەی AI لەم خولەدا جێگیر نەکراون. بەڵگەنامە و دەروازەی کڕیار بەکاربهێنە.',

    'command.home': 'سەرەتا',
    'command.signIn': 'چوونەژوورەوە',
    'command.adminPortal': 'دەروازەی بەڕێوەبەر',
    'command.customerPortal': 'دەروازەی کڕیار',
    'command.marketplaceSoon': '{label} (بەم زووانە)',

    'concept.pos.category': 'خاڵی فرۆشتنی مۆدێرن بۆ تاکەفرۆشی',
    'concept.pos.checkout': 'خاڵی فرۆشتن',
    'concept.pos.order': 'داواکاریی ئێستا',
    'concept.pos.total': 'کۆی گشتی',
    'concept.ledger.category': 'چاودێری دارایی و سەرپەرشتی کاروبار',
    'concept.ledger.overview': 'پوختەی دارایی',
    'concept.ledger.balance': 'باڵانسی پاک',
    'concept.ledger.income': 'داهات',
    'concept.ledger.expenses': 'خەرجییەکان',
    'concept.befx.category': 'بەڕێوەبردنی کاری ئاڵوگۆڕی دراو',
    'concept.befx.rates': 'مێزی ئاڵوگۆڕ',
    'concept.befx.pair': 'جووتی دراو',
    'concept.befx.buy': 'کڕین',
    'concept.befx.sell': 'فرۆشتن',
    'concept.store.category': 'بەڕێوەبردنی کەتەلۆگی بەرهەم و کۆگا',
    'concept.store.inventory': 'کۆگای بەرهەم',
    'concept.store.inStock': 'لە کۆگادایە',
    'concept.store.lowStock': 'کۆگای کەم',

    'product.status.active': 'بڵاوکراوەتەوە',
    'product.status.hidden': 'تاقیکاری',
    'product.status.coming_soon': 'بەم زووانە',
    'product.status.archived': 'ئەرشیڤکراو',
    'common.dayAgo': 'پێش یەک ڕۆژ',
    'release.policy.optional': 'ئارەزوومەندانە',
    'release.policy.mandatory': 'ناچاری',
    'content.seed.studio.tagline': 'ئەپێکی پارێزراو بۆ وەرگرتن و بەڕێوەبردنی سۆفتوێری BeCoder',
    'content.seed.studio.description':
      'BeCoder Studio بەرنامەی سەرەکیمانە بۆ دەستگەیشتنی مۆڵەتدار لە ڕێگەی وێب/PWA و Windows و Android APK و iOS/PWA.',
    'content.seed.studio.changelog': 'یەکەم وەشانی تایبەتی BeCoder Studio بۆ Windows x64.',
    'product.detail.platforms': 'سەکۆکان',
    'product.detail.requirements': 'پێداویستییەکانی سیستەم',
    'product.detail.releases': 'وەشانەکان',
    'common.notFound': 'نەدۆزرایەوە',
    'portal.releaseFile': 'فایلی وەشان',
    'common.retry': 'دووبارە هەوڵبدە',

    'cc.controlCenter': 'ناوەندی کۆنترۆڵ',
    'cc.dashboardTitle': 'پوختەی کارگێڕی',
    'cc.dashboardIntro': 'دۆخی پلاتفۆرم لە داتای ڕاستەقینەی مافەکان و کەتەلۆگ و وردبینی.',
    'cc.rolePlatformAdmin': 'بەڕێوەبەری پلاتفۆرم',
    'cc.collapseSidebar': 'کۆکردنەوە',
    'cc.expandSidebar': 'فراوانکردنی لایەنە',
    'cc.apiOperational': 'API کارایە',
    'cc.apiUnavailable': 'API بەردەست نییە',
    'cc.loadFailed': 'نەتوانرا پوختەی ناوەندی کۆنترۆڵ بار بکرێت. دووبارە هەوڵبدە.',
    'cc.kpis': 'نیشاندەرە کارگێڕییەکان',
    'cc.kpi.customers': 'کڕیارەکان',
    'cc.kpi.activeLicenses': 'مۆڵەتی چالاک',
    'cc.kpi.activeTrials': 'تاقیکردنەوەی چالاک',
    'cc.kpi.activeSubs': 'بەشداری چالاک',
    'cc.kpi.activeDevices': 'ئامێری چالاک',
    'cc.kpi.publishedProducts': 'بەرهەمی بڵاوکراو',
    'cc.attentionTitle': 'پێویستی بە سەرنجدان',
    'cc.attentionNone': 'هیچ بابەتی گرنگ نییە.',
    'cc.attention.expiringTrials': '{count} تاقیکردنەوە لە ماوەی ٧ ڕۆژدا کۆتایی دێن',
    'cc.attention.suspendedLicenses': '{count} مۆڵەتی ڕاگیراو',
    'cc.attention.revokedLicenses': '{count} مۆڵەتی هەڵوەشاوە',
    'cc.attention.expiredSubs': '{count} بەشداری بەسەرچوو',
    'cc.attention.missingPricing': '{name}: نرخدانان نییە',
    'cc.attention.incompleteLocales': '{name}: ناوەڕۆکی زمان ناتەواو',
    'cc.attention.noRelease': '{name}: وەشانی بڵاوکراو نییە',
    'cc.attention.problemMissingPricing': 'نرخدانان نییە',
    'cc.attention.problemIncompleteLocales': 'ناوەڕۆکی زمان ناتەواو',
    'cc.attention.problemNoRelease': 'وەشانی بڵاوکراو نییە',
    'cc.attention.openTrials': 'پێداچوونەوەی تاقیکردنەوەکان',
    'cc.attention.openLicenses': 'بەڕێوەبردنی مۆڵەتەکان',
    'cc.attention.openSubs': 'پێداچوونەوەی بەشدارییەکان',
    'cc.attention.openPricing': 'کردنەوەی نرخ',
    'cc.attention.openContent': 'کردنەوەی ناوەڕۆک',
    'cc.attention.openReleases': 'بەڕێوەبردنی وەشانەکان',
    'cc.kpi.hint.directory': 'لیستی کڕیار',
    'cc.kpi.hint.entitlements': 'مۆڵەتی کارا',
    'cc.kpi.hint.trials': 'ئێستا چالاک',
    'cc.kpi.hint.billing': 'بیلکردنی چالاک',
    'cc.kpi.hint.devices': 'ئامێری بەستراو',
    'cc.kpi.hint.catalog': 'کەتەلۆگی بڵاوکراو',
    'cc.quickActions': 'کردارە خێراکان',
    'cc.action.findCustomer': 'دۆزینەوەی کڕیار',
    'cc.action.issueLicense': 'دەرکردنی مۆڵەت',
    'cc.action.createRelease': 'دروستکردنی وەشان',
    'cc.action.manageProducts': 'بەڕێوەبردنی بەرهەمەکان',
    'cc.action.uploadMedia': 'بارکردنی میدیا',
    'cc.action.downloadAudit': 'وردبینی داگرتن',
    'cc.recentActivity': 'چالاکی دوایی',
    'cc.viewAuditLog': 'بینینی تۆماری وردبینی',
    'cc.noActivity': 'هیچ چالاکییەکی بەڕێوەبەری نوێ نییە.',
    'cc.activity.system': 'سیستەم',
    'cc.activity.aProduct': 'بەرهەمێک',
    'cc.activity.aLicense': 'مۆڵەتێک',
    'cc.activity.aCustomer': 'کڕیارێک',
    'cc.activity.byActor': 'لەلایەن {actor}',
    'cc.activity.mediaUploaded': 'میدیا بۆ {name} بارکرا',
    'cc.activity.mediaScreenshot': 'وێنەی شاشە بۆ {name} بارکرا',
    'cc.activity.mediaIcon': 'ئایکۆن بۆ {name} بارکرا',
    'cc.activity.mediaHero': 'وێنەی سەرەکی بۆ {name} بارکرا',
    'cc.activity.mediaRemoved': 'میدیا لە {name} سڕایەوە',
    'cc.activity.mediaScreenshotRemoved': 'وێنەی شاشە لە {name} سڕایەوە',
    'cc.activity.licenseIssued': 'مۆڵەت بۆ {name} دەرکرا',
    'cc.activity.licenseIssuedTo': 'مۆڵەت بۆ {name} دەرکرا',
    'cc.activity.licenseSuspended': 'مۆڵەت ڕاگیرا ({name})',
    'cc.activity.licenseRevoked': 'مۆڵەت هەڵوەشایەوە ({name})',
    'cc.activity.licenseActivated': 'مۆڵەت چالاککرا ({name})',
    'cc.activity.trialExtended': 'تاقیکردنەوەی {name} درێژکرایەوە',
    'cc.activity.trialForceGrant': 'تاقیکردنەوەی {name} بەزۆر بەخشرا',
    'cc.activity.trialCanceled': 'تاقیکردنەوەی {name} هەڵوەشایەوە',
    'cc.activity.priceUpdated': 'نرخی {code} بۆ {name} نوێکرایەوە',
    'cc.activity.planCreated': 'پلانی {code} بۆ {name} دروستکرا',
    'cc.activity.planToggled': 'چالاکی پلانی {code} بۆ {name} نوێکرایەوە',
    'cc.activity.productCore': 'دۆخی {name} نوێکرایەوە',
    'cc.activity.commercialFlags': 'ئاڵاکانی بازرگانی {name} نوێکرانەوە',
    'cc.activity.productPublished': 'ناوەڕۆکی بازرگانی {name} بڵاوکرایەوە',
    'cc.activity.draftSaved': 'ڕەشنووسی {name} پاشەکەوت کرا',
    'cc.activity.customerSuspended': 'کڕیار ڕاگیرا: {name}',
    'cc.activity.customerActivated': 'کڕیار چالاککرا: {name}',
    'cc.activity.releasePublished': 'وەشان بۆ {name} بڵاوکرایەوە',
    'cc.activity.releaseUnpublished': 'وەشان بۆ {name} لە بڵاوکردنەوە لابرا',
    'cc.activity.settingsUpdated': 'ڕێکخستنەکانی پلاتفۆرم نوێکرانەوە',
    'cc.time.justNow': 'ئێستا',
    'cc.time.minutesAgo': 'پێش {count} خولەک',
    'cc.time.hoursAgo': 'پێش {count} کاتژمێر',
    'cc.time.yesterday': 'دوێنێ',
    'cc.time.daysAgo': 'پێش {count} ڕۆژ',
    'cc.ready.content': 'ناوەڕۆک',
    'cc.ready.pricing': 'نرخ',
    'cc.ready.media': 'میدیا',
    'cc.ready.trial': 'تاقیکردنەوە',
    'cc.ready.distribution': 'دابەشکردن',
    'cc.ready.ok': 'ئامادەیە',
    'cc.ready.warn': 'پێویستی بە سەرنجدان',
    'cc.ready.na': 'بەکارناهێت',
    'cc.empty.devices': 'هێشتا ئامێری چالاک نییە.',
    'cc.empty.downloads': 'هێشتا چالاکی داگرتن نییە.',
    'cc.empty.activity': 'هیچ چالاکی کارگێڕی نوێ نییە.',
    'cc.settings.savedState': 'پاشەکەوتکراو',
    'cc.settings.saving': 'پاشەکەوتکردن…',
    'cc.settings.saveFailed': 'پاشەکەوتکردن سەرکەوتوو نەبوو',
    'cc.nav.overview': 'پوختە',
    'cc.nav.customers': 'کڕیارەکان',
    'cc.nav.catalog': 'کەتەلۆگ',
    'cc.nav.entitlements': 'مافەکان',
    'cc.nav.access': 'دەستگەیشتن',
    'cc.nav.distribution': 'دابەشکردن',
    'cc.nav.trust': 'متمانە و کارگێڕی',
    'cc.nav.settings': 'ڕێکخستنەکان',
    'cc.products.intro': 'بەڕێوەبردنی بەرهەمەکانی کەتەلۆگ و ئامادەیی بازرگانی.',
    'cc.products.create': 'دروستکردنی بەرهەم',
    'cc.products.manage': 'بەڕێوەبردن',
    'cc.access.issueLicense': '+ دەرکردنی مۆڵەت',
    'cc.settings.advanced': 'پێشکەوتوو',
    'cc.settings.advancedIntro': 'شوێنی تەکنیکی و هەژمار.',
    'cc.settings.accountHint': 'هەژماری کارگێڕ',
    'cc.settings.flagsHint': 'ئاڵای تایبەتمەندی',
    'cc.settings.auditHint': 'تۆماری وردبینی ئاسایش',
    'cms.hub.overview': 'پوختە',
    'cms.hub.content': 'ناوەڕۆک',
    'cms.hub.plans': 'نرخ',
    'cms.hub.trial': 'تاقیکردنەوە',
    'cms.hub.media': 'میدیا',
    'cms.hub.distribution': 'دابەشکردن',
    'cc.settings.identity': 'ناسنامەی پلاتفۆرم',
    'cc.settings.contact': 'پەیوەندی',
    'cc.settings.about': 'دەربارە',
    'cc.settings.social': 'کۆمەڵایەتی',
    'cc.settings.footer': 'فۆتەر',
    'cc.customerSince': 'کڕیار لە',
    'cc.showMetadata': 'پیشاندانی وردەکاری',
    'cc.hideMetadata': 'شاردنەوەی وردەکاری',
    'cc.unsaved': 'گۆڕانکاری پاشەکەوتنەکراو',
  },
};
